import { CompleteKundali, Language, PlanetName, PlanetPosition, ZodiacSign, DashaPeriod } from '../types/kundali';
import {
  VartamanaHorizon,
  ActivationLifecycleState,
  TransitTriggerPhase,
  EvidenceStrength,
  LifeAreaThemeId,
  AnubhavaCategory,
  AnubhavaTemporalTier,
  AnubhavaPriority,
  VartamanaAnubhavaSignature,
  DashaLevelInfo,
  Dasha5LevelResolution,
  GocharTriggerInfo,
  EvidenceChainStep,
  AcharyaThemeDeepDive,
  ExpressionPolarity,
  ManifestationSpecificity,
  VartamanaActiveTheme,
  DynamicTimelineMarker,
  HorizonTransitionEvent,
  HorizonAnalysisResult,
  TodayComparisonDiff,
  CurrentStateValidityWindow,
  VartamanaCurrentFocusState,
  VartamanaSthitiSnapshot,
  VartamanaSthitiResult,
  TemporalStatus,
  VartamanaPanchangaSummary
} from '../types/vartamanaSthiti';
import {
  ConcernDomainId,
  ConcernDomainMeta,
  CANONICAL_CONCERN_DOMAINS,
  FreeTextEvaluationResult,
  ConcernTimingWindow,
  EpistemicInsightStatement,
  PsychologicalSafetyFraming,
  ConcernUpayaPractice,
  AcharyaConcernDeepDive,
  VartmanConcernIntelligenceResult,
  EpistemicCategory,
  EPISTEMIC_CLASSIFICATION_REGISTRY
} from '../types/vartmanConcernIntelligence';
import {
  VIMSHOTTARI_ORDER,
  VIMSHOTTARI_YEARS,
  calculatePratyantardashas,
  calculateSookshmaDashas,
  calculatePranaDashas
} from './dashaCalculator';
import {
  calculateLivePlanetaryEphemeris,
  ZODIAC_SIGNS,
  ZODIAC_LORDS,
  calculateShadbala,
  getBirthUtcDate,
  getSavBySign,
  getSavByBhava,
  getSpecialDrishtiHouses,
  calculateDignity
} from './vedicEngine';
import { formatDate } from './dateUtils';
import { calculatePrastaraTables } from './ashtakvargaAdvancedEngine';
import { calculateD9Navamsha } from './planetaryMatrixEngine';
import { calculateAuthoritativePanchanga, PanchangaCoordinates } from './panchangaEngine';

// ============================================================================
// CANONICAL LIFE THEME CATALOG DEFINITIONS (VARTAMĀNA JEEVAN THEMES)
// ============================================================================
interface ThemeMeta {
  themeId: LifeAreaThemeId;
  nameEn: string;
  nameHi: string;
  sanskritName: string;
  primaryHouses: number[];
  supportingHouses: number[];
  primarySignificator: PlanetName;
  jaiminiSignificator: string; // e.g. 'AmK (Amatyakaraka)', 'DK (Darakaraka)'
  meaningEn: string;
  meaningHi: string;
  karmicDomainNoteEn: string;
  karmicDomainNoteHi: string;
  activePrinciplesEn: string[];
  activePrinciplesHi: string[];
  growthKeywordsEn: string[];
  growthKeywordsHi: string[];
  cautionKeywordsEn: string[];
  cautionKeywordsHi: string[];
}

const LIFE_THEME_CATALOG: Record<LifeAreaThemeId, ThemeMeta> = {
  karma_career: {
    themeId: 'karma_career',
    nameEn: 'Karma / Career & Vocation',
    nameHi: 'कर्म, आजीविका व पद-प्रतिष्ठा',
    sanskritName: 'Karma Sthana (10th Bhava)',
    primaryHouses: [10, 6],
    supportingHouses: [1, 2, 11],
    primarySignificator: 'Saturn',
    jaiminiSignificator: 'Amatyakaraka (AmK)',
    meaningEn: 'The 10th Bhava governs external duty (Karma), executive responsibility, social impact, and vocational reputation. In Vedic philosophy, professional life is the primary theater where individual destiny interacts with collective society.',
    meaningHi: 'दशम भाव कर्म, आजीविका, सामाजिक उत्तरदायित्व एवं यश-प्रतिष्ठा का प्रतीक है। वैदिक दर्शन में कर्म क्षेत्र व्यक्ति के प्रारब्ध का प्रमुख अभिव्यक्ति स्थल है।',
    karmicDomainNoteEn: 'Governs vocational elevation, institutional authority, and sustained diligence in public endeavors.',
    karmicDomainNoteHi: 'यह कर्मक्षेत्र में प्रगति, प्रशासनिक अधिकार एवं कर्तव्यनिष्ठा से संबंधित है।',
    activePrinciplesEn: ['Sustained professional diligence', 'Ethical leadership alignment', 'Strategic career pacing'],
    activePrinciplesHi: ['निरंतर कर्मठता', 'नैतिक नेतृत्व', 'योजनाबद्ध प्रगति'],
    growthKeywordsEn: ['Executive authority', 'Professional stabilization', 'Leadership recognition', 'Skill mastery'],
    growthKeywordsHi: ['प्रशासनिक दायित्व', 'आजीविका स्थायित्व', 'नेतृत्व सम्मान', 'कार्यकुशलता'],
    cautionKeywordsEn: ['Avoid hasty career disruption', 'Guard against friction with seniors', 'Maintain rigorous consistency'],
    cautionKeywordsHi: ['शीघ्रता में पद-त्याग से बचें', 'वरिष्ठों से मतभेद न करें', 'अनुशासन बनाए रखें']
  },
  artha_finance: {
    themeId: 'artha_finance',
    nameEn: 'Artha / Wealth, Assets & Inflow',
    nameHi: 'अर्थ, संचित कोष व लाभ संवृद्धि',
    sanskritName: 'Dhana & Labha Sthana (2nd & 11th Bhavas)',
    primaryHouses: [2, 11],
    supportingHouses: [5, 9],
    primarySignificator: 'Jupiter',
    jaiminiSignificator: 'Bhratrikaraka / Putrakaraka',
    meaningEn: 'The 2nd Bhava (accumulated liquid wealth and family assets) and 11th Bhava (gains, income streams, and fulfilled aspirations) constitute the core Artha axis. Wealth in Jyotish is viewed as an enabling resource for fulfilling Dharma.',
    meaningHi: 'द्वितीय भाव (संचित धन व कोष) तथा एकादश भाव (आय के स्रोत व लाभ) मिलकर अर्थ त्रिकोण की धुरी बनाते हैं। ज्योतिष में धन को धर्म-साधना का संबल माना गया है।',
    karmicDomainNoteEn: 'Governs financial consolidation, monetary liquidity, asset growth, and fulfillment of material aspirations.',
    karmicDomainNoteHi: 'यह पूंजी संचय, वित्तीय स्थिरता एवं आय के साधनों की वृद्धि से जुड़ा है।',
    activePrinciplesEn: ['Measured resource allocation', 'Prudent long-term wealth compounding', 'Asset preservation'],
    activePrinciplesHi: ['संतुलित व्यय प्रबंधन', 'दीर्घकालिक पूंजी संचय', 'संपत्ति सुरक्षा'],
    growthKeywordsEn: ['Capital accumulation', 'Diversified revenue streams', 'Resource consolidation', 'Favorable returns'],
    growthKeywordsHi: ['पूंजी संचय', 'आय के नए स्रोत', 'संसाधन सुदृढ़ीकरण', 'उचित प्रतिफल'],
    cautionKeywordsEn: ['Avoid uncalculated speculation', 'Review contractual commitments', 'Control sudden outflow'],
    cautionKeywordsHi: ['जोखिमपूर्ण सट्टेबाजी से बचें', 'दस्तावेज़ों की सावधानीपूर्वक जांच', 'अनावश्यक व्यय नियंत्रण']
  },
  sambandha_relationships: {
    themeId: 'sambandha_relationships',
    nameEn: 'Sambandha / Relationships & Union',
    nameHi: 'संबंध, दांपत्य व साझेदारी',
    sanskritName: 'Kalatra & Jaya Sthana (7th Bhava)',
    primaryHouses: [7],
    supportingHouses: [2, 4, 11],
    primarySignificator: 'Venus',
    jaiminiSignificator: 'Darakaraka (DK)',
    meaningEn: 'The 7th Bhava represents the mirror of the self: marital union, life partnerships, public contracts, and significant one-on-one alliances. Astrologically, it tests mutual compromise, emotional attunement, and contractual integrity.',
    meaningHi: 'सप्तम भाव दांपत्य, जीवनसाथी, साझेदारी एवं सार्वजनिक संबंधों का केंद्र है। यह परस्पर सामंजस्य, विश्वास एवं समर्पण की परीक्षा करता है।',
    karmicDomainNoteEn: 'Governs interpersonal harmony, alliance formations, marital accord, and collective collaboration.',
    karmicDomainNoteHi: 'यह वैवाहिक सुख, साझेदारी में विश्वास एवं सामाजिक समझौतों से संबंधित है।',
    activePrinciplesEn: ['Empathetic listening and reciprocity', 'Clear mutual expectations', 'Patience during friction'],
    activePrinciplesHi: ['संवेदनशील संवाद', 'पारस्परिक स्पष्टता', 'विवाद में धैर्य'],
    growthKeywordsEn: ['Deepened mutual accord', 'Public alliances', 'Constructive partnership agreements', 'Relational warmth'],
    growthKeywordsHi: ['पारस्परिक सामंजस्य', 'सामाजिक सहयोग', 'साझेदारी में स्पष्टता', 'भावनात्मक निकटता'],
    cautionKeywordsEn: ['Cultivate clear communication', 'Avoid egoic confrontations', 'Practice patience during friction'],
    cautionKeywordsHi: ['स्पष्ट संवाद रखें', 'अहं टकराव से बचें', 'विवाद में धैर्य रखें']
  },
  kutumba_family: {
    themeId: 'kutumba_family',
    nameEn: 'Kutumba / Lineage, Clan & Kinship',
    nameHi: 'कुटुंब, कुल व स्वजन कल्याण',
    sanskritName: 'Kutumba Bhava (2nd & 4th Bhavas)',
    primaryHouses: [2, 4],
    supportingHouses: [3, 9],
    primarySignificator: 'Jupiter',
    jaiminiSignificator: 'Matrikaraka / Pitrikaraka',
    meaningEn: 'Kutumba encompasses ancestral heritage, extended clan stability, parental welfare, and domestic values. It represents the familial bedrock that shapes an individual’s early impressions and ongoing emotional anchor.',
    meaningHi: 'कुटुंब भाव कुल-परंपरा, पारिवारिक सौहार्द एवं माता-पिता के कल्याण का प्रतिनिधित्व करता है। यह व्यक्ति की प्रारंभिक नींव एवं संस्कार का आधार है।',
    karmicDomainNoteEn: 'Governs family unity, parental well-being, resolution of ancestral matters, and domestic peace.',
    karmicDomainNoteHi: 'यह परिवार के कल्याण, माता-पिता की सेवा एवं कुल-सौहार्द से संबंधित है।',
    activePrinciplesEn: ['Generational respect', 'Harmonious domestic speech', 'Clan solidarity'],
    activePrinciplesHi: ['कुल-मर्यादा का पालन', 'मधुर पारिवारिक वाणी', 'पारस्परिक सहयोग'],
    growthKeywordsEn: ['Harmonious domestic gatherings', 'Shared family progress', 'Honor of lineage commitments'],
    growthKeywordsHi: ['पारिवारिक सौहार्द', 'सामूहिक उन्नति', 'कुल परंपरा का सम्मान'],
    cautionKeywordsEn: ['Exercise measured speech in family discussions', 'Avoid reviving past grievances'],
    cautionKeywordsHi: ['पारिवारिक संवाद में मधुर वाणी', 'पुरानी बातों पर विवाद से बचें']
  },
  griha_residence: {
    themeId: 'griha_residence',
    nameEn: 'Gṛha / Home, Property & Fixed Assets',
    nameHi: 'गृह, भूमि व अचल संपत्ति',
    sanskritName: 'Sukha & Bandhu Sthana (4th Bhava)',
    primaryHouses: [4],
    supportingHouses: [1, 2, 12],
    primarySignificator: 'Mars',
    jaiminiSignificator: 'Matrikaraka (MK)',
    meaningEn: 'The 4th Bhava represents inner emotional contentment (Sukha), immovable physical property (Bhumi), residential stability (Griha), and conveyances (Vahana). It is the sacred sanctuary of daily peace.',
    meaningHi: 'चतुर्थ भाव आंतरिक सुख, भूमि, भवन, वाहन एवं अचल संपत्तियों का स्थान है। यह व्यक्ति के मानसिक विश्राम एवं गृह-शांति का केंद्र है।',
    karmicDomainNoteEn: 'Governs real estate transactions, residential enhancements, home renovation, and domestic serenity.',
    karmicDomainNoteHi: 'यह भूमि-भवन क्रय, गृह नवीनीकरण एवं पारिवारिक सुख-सुविधाओं से संबंधित है।',
    activePrinciplesEn: ['Physical environment harmony', 'Due diligence on legal titles', 'Creating peaceful domestic sanctuary'],
    activePrinciplesHi: ['वास्तु एवं वातावरण संतुलन', 'विलेखों की प्रामाणिकता', 'गृह-शांति संवर्धन'],
    growthKeywordsEn: ['Property acquisition or refinement', 'Domestic tranquility', 'Vehicular additions', 'Nurturing sanctuary'],
    growthKeywordsHi: ['भूमि/गृह सुधार या क्रय', 'घरेलू शांति', 'वाहन सुख', 'सुरक्षित गृह वातावरण'],
    cautionKeywordsEn: ['Verify legal titles thoroughly', 'Avoid boundary disagreements', 'Budget adequately for maintenance'],
    cautionKeywordsHi: ['भूमि विलेखों की पूर्ण जांच', 'सीमा विवादों से दूर रहें', 'रखरखाव का समुचित प्रबंध']
  },
  relocation: {
    themeId: 'relocation',
    nameEn: 'Deshantara / Travel, Relocation & Horizons',
    nameHi: 'देशांतर, स्थान परिवर्तन व प्रवास',
    sanskritName: 'Pravasa & Deshantara (3rd, 9th & 12th Bhavas)',
    primaryHouses: [3, 9, 12],
    supportingHouses: [4, 7],
    primarySignificator: 'Moon',
    jaiminiSignificator: 'Atmakaraka / Rahu',
    meaningEn: 'The 3rd (short journeys), 9th (long-distance pilgrimages and higher horizon travel), and 12th (foreign residence and overseas journeys) Bhavas activate movement across physical and cultural boundaries.',
    meaningHi: 'तृतीय (अल्प यात्राएं), नवम (दीर्घ तीर्थाटन व विदेश गमन) तथा द्वादश (सुदूर प्रवास) भाव भौगोलिक एवं सांस्कृतिक विस्तार के परिचायक हैं।',
    karmicDomainNoteEn: 'Governs geographical relocation, international connections, cross-cultural initiatives, and travel logistics.',
    karmicDomainNoteHi: 'यह स्थान परिवर्तन, विदेश गमन एवं नए भौगोलिक अवसरों से संबंधित है।',
    activePrinciplesEn: ['Adaptability to new environments', 'Meticulous logistical preparation', 'Cultural receptivity'],
    activePrinciplesHi: ['नए परिवेश में अनुकूलन', 'दस्तावेज़ीकरण में सतर्कता', 'सांस्कृतिक समन्वय'],
    growthKeywordsEn: ['Expansion of horizons', 'New geographical or cultural connections', 'Productive transition of base'],
    growthKeywordsHi: ['नए अवसरों का विस्तार', 'भौगोलिक/सांस्कृतिक जुड़ाव', 'सफल स्थान परिवर्तन'],
    cautionKeywordsEn: ['Organize documentation meticulously', 'Allow buffer time during transitions'],
    cautionKeywordsHi: ['दस्तावेज़ पूर्ण व्यवस्थित रखें', 'प्रवास में समय का संतुलन रखें']
  },
  health_vitality: {
    themeId: 'health_vitality',
    nameEn: 'Sharira Bala / Vitality, Routine & Balance',
    nameHi: 'शरीर बल, दैनिक दिनचर्या व ऊर्जा संतुलन',
    sanskritName: 'Tanu & Roga Shamana (1st & 6th Bhavas)',
    primaryHouses: [1, 6],
    supportingHouses: [8, 12],
    primarySignificator: 'Sun',
    jaiminiSignificator: 'Atmakaraka (AK)',
    meaningEn: 'The 1st Bhava (physical constitution and vitality) and 6th Bhava (daily regimen, immune response, and therapeutic habits) determine physical resilience. Vedic astrology views health as dynamic harmony between Doshas and daily Ritucharya.',
    meaningHi: 'प्रथम भाव (शारीरिक काया व जीवन शक्ति) तथा षष्ठ भाव (रोग प्रतिरोधक क्षमता व दिनचर्या) स्वास्थ्य संतुलन निर्धारित करते हैं।',
    karmicDomainNoteEn: 'Governs daily physical discipline, regenerative recovery, immune resilience, and stress moderation.',
    karmicDomainNoteHi: 'यह शारीरिक स्फूर्ति, ऋतुचर्या पालन, रोगोपचार एवं जीवन ऊर्जा संरक्षण से संबंधित है।',
    activePrinciplesEn: ['Consistent restorative sleep and nutrition', 'Mindful physical activity', 'Preventive health vigilance'],
    activePrinciplesHi: ['संतुलित आहार व विश्राम', 'नियमित योग-व्यायाम', 'स्वास्थ्य के प्रति सजगता'],
    growthKeywordsEn: ['Regimen consistency', 'Enhanced resilience through restorative habits', 'Physical vitality renewal'],
    growthKeywordsHi: ['संतुलित दिनचर्या', 'प्राकृतिक ऊर्जा संवर्धन', 'शारीरिक स्फूर्ति'],
    cautionKeywordsEn: ['Respect seasonal biorhythms', 'Avoid physical exhaustion', 'Prioritize restorative rest and hydration'],
    cautionKeywordsHi: ['ऋतुचर्या का पालन करें', 'अत्यधिक श्रम से बचें', 'पर्याप्त विश्राम व जलपान']
  },
  antarjivana_inner: {
    themeId: 'antarjivana_inner',
    nameEn: 'Antarjīvana / Mind, Equanimity & Purpose',
    nameHi: 'अंतर्जीवन, मानसिक शांति व संकल्प',
    sanskritName: 'Chitta Shanti & Manas (4th & 5th Bhavas)',
    primaryHouses: [4, 5, 9],
    supportingHouses: [1, 12],
    primarySignificator: 'Moon',
    jaiminiSignificator: 'Atmakaraka (AK)',
    meaningEn: 'The Manas (emotional mind) and Buddhi (intellect and discernment) govern subjective peace, meditative depth, and philosophical resilience. This axis determines internal joy irrespective of outer circumstances.',
    meaningHi: 'मन एवं बुद्धि मिलकर आंतरिक शांति, विवेक और दार्शनिक परिपक्वता का निर्माण करते हैं। यह मानसिक संतुलन व संकल्प शक्ति का आधार है।',
    karmicDomainNoteEn: 'Governs mental clarity, emotional equilibrium, philosophical orientation, and reduction of internal friction.',
    karmicDomainNoteHi: 'यह चित्त शुद्धि, भावनात्मक स्थिरता एवं मानसिक शांति संवर्धन से संबंधित है।',
    activePrinciplesEn: ['Contemplative stillness', 'Emotional detachment from temporary outcomes', 'Cultivation of discernment (Viveka)'],
    activePrinciplesHi: ['ध्यान व आत्म-चिंतन', 'अनावश्यक तनाव से मुक्ति', 'सकारात्मक विचार'],
    growthKeywordsEn: ['Clarity of intent', 'Philosophical serenity', 'Strengthened contemplative equilibrium'],
    growthKeywordsHi: ['विचारों में स्पष्टता', 'दार्शनिक शांति', 'मानसिक स्थिरता'],
    cautionKeywordsEn: ['Minimize mental over-stimulation', 'Practice grounding breathwork'],
    cautionKeywordsHi: ['अति-विचार से बचें', 'प्राणायाम व ध्यान का अभ्यास']
  },
  education: {
    themeId: 'education',
    nameEn: 'Vidya / Knowledge, Study & Skill Acquisition',
    nameHi: 'विद्या, ज्ञानार्जन व बौद्धिक दक्षता',
    sanskritName: 'Vidya & Buddhi Sthana (4th & 5th Bhavas)',
    primaryHouses: [4, 5],
    supportingHouses: [9, 10],
    primarySignificator: 'Mercury',
    jaiminiSignificator: 'Putrakaraka (PK)',
    meaningEn: 'Vidya encompasses formal academic learning (4th Bhava), applied intellect and creative intelligence (5th Bhava), and higher wisdom (9th Bhava). It represents the acquisition of tools for intellectual sovereignty.',
    meaningHi: 'विद्या एवं बुद्धि भाव ज्ञानार्जन, बौद्धिक दक्षता, अनुसंधान एवं उच्च शिक्षा का संकेत देते हैं।',
    karmicDomainNoteEn: 'Governs academic progression, examination preparation, technical certifications, and intellectual mastery.',
    karmicDomainNoteHi: 'यह विद्या प्राप्ति, परीक्षा सफलता एवं बौद्धिक क्षमताओं के विकास से जुड़ा है।',
    activePrinciplesEn: ['Structured study habits', 'Depth of conceptual understanding', 'Application of learning'],
    activePrinciplesHi: ['नियमित अध्ययन', 'गहन ज्ञानार्जन', 'व्यावहारिक अनुप्रयोग'],
    growthKeywordsEn: ['Intellectual absorption', 'Scholarly achievement', 'Specialized credential acquisition'],
    growthKeywordsHi: ['बौद्धिक एकाग्रता', 'शैक्षणिक सफलता', 'विशिष्ट ज्ञानार्जन'],
    cautionKeywordsEn: ['Maintain disciplined study hours', 'Avoid dispersion of academic focus'],
    cautionKeywordsHi: ['अध्ययन में नियमितता', 'ध्यान के भटकाव से बचें']
  },
  children: {
    themeId: 'children',
    nameEn: 'Santana / Progeny, Creativity & Mantras',
    nameHi: 'संतान, सृजनात्मकता व मंत्र साधना',
    sanskritName: 'Putra & Purva Punya Sthana (5th Bhava)',
    primaryHouses: [5],
    supportingHouses: [2, 9, 11],
    primarySignificator: 'Jupiter',
    jaiminiSignificator: 'Putrakaraka (PK)',
    meaningEn: 'The 5th Bhava represents progeny (Santana), creative inspiration, speculative intellect, and past-life merit (Purva Punya). It is the sacred house of intuitive foresight and progeny welfare.',
    meaningHi: 'पंचम भाव संतान सुख, पूर्व पुण्य, सृजनात्मक प्रतिभा एवं मंत्र साधना का स्थान है।',
    karmicDomainNoteEn: 'Governs progeny development, creative initiatives, intellectual creation, and spiritual merit activation.',
    karmicDomainNoteHi: 'यह संतान की उन्नति, नवीन सृजन एवं पूर्व संचित शुभ कर्मों के फल से संबंधित है।',
    activePrinciplesEn: ['Nurturing support for children', 'Channeling creative inspiration', 'Gratitude for past merits'],
    activePrinciplesHi: ['संतान का मार्गदर्शन', 'सृजनात्मक अभिव्यक्ति', 'सद्कर्मों का संचय'],
    growthKeywordsEn: ['Creative fruitfulness', 'Children’s progress', 'Intuitive inspiration', 'Spiritual merit activation'],
    growthKeywordsHi: ['सृजनशीलता का विकास', 'संतान की उन्नति', 'अंतःप्रेरणा', 'पूर्व पुण्य का शुभ प्रभाव'],
    cautionKeywordsEn: ['Support progeny with gentle patience', 'Channel creative surges into structured projects'],
    cautionKeywordsHi: ['संतान के प्रति धैर्यपूर्ण व्यवहार', 'सृजनात्मक ऊर्जा का सदुपयोग']
  },
  spirituality: {
    themeId: 'spirituality',
    nameEn: 'Dharma & Moksha / Spiritual Ascent & Seva',
    nameHi: 'धर्म व मोक्ष, साधना एवं निस्वार्थ सेवा',
    sanskritName: 'Dharma & Moksha Trikona (9th & 12th Bhavas)',
    primaryHouses: [9, 12],
    supportingHouses: [4, 5, 8],
    primarySignificator: 'Ketu',
    jaiminiSignificator: 'Atmakaraka (AK)',
    meaningEn: 'The 9th Bhava (Dharma, Guru grace, and higher philosophy) and 12th Bhava (Moksha, meditation, and detachment) constitute the spiritual summit of the birth chart.',
    meaningHi: 'नवम भाव (धर्म, गुरु कृपा व उच्च ज्ञान) तथा द्वादश भाव (मोक्ष, ध्यान व वैराग्य) मिलकर आध्यात्मिक साधना का आधार बनते हैं।',
    karmicDomainNoteEn: 'Governs meditative discipline, pilgrimage, spiritual initiation, charitable works, and inner detachment.',
    karmicDomainNoteHi: 'यह ध्यान साधना, तीर्थ यात्रा, गुरु सेवा एवं परोपकार से संबंधित है।',
    activePrinciplesEn: ['Devotional surrender', 'Selfless service (Seva)', 'Adherence to righteous conduct (Dharma)'],
    activePrinciplesHi: ['ईश्वर समर्पण', 'निस्वार्थ सेवा भाव', 'सदाचार पालन'],
    growthKeywordsEn: ['Deepened meditative stillness', 'Guru grace receptivity', 'Pilgrimage and charitable works'],
    growthKeywordsHi: ['गहन ध्यान शांति', 'गुरु कृपा की प्राप्ति', 'तीर्थाटन व सेवा भाव'],
    cautionKeywordsEn: ['Ground spiritual contemplation in worldly duties', 'Avoid spiritual pride'],
    cautionKeywordsHi: ['कर्तव्यों के साथ साधना का सामंजस्य', 'आध्यात्मिक अहंकार से मुक्ति']
  },
  major_life_theme: {
    themeId: 'major_life_theme',
    nameEn: 'Mahā-Karmic Arc / Foundational Life Direction',
    nameHi: 'महा-कर्मिक रेखा व प्रमुख जीवन दिशा',
    sanskritName: 'Lagna & Bhagya Trikona (1st, 9th & 10th Bhavas)',
    primaryHouses: [1, 9, 10],
    supportingHouses: [5, 11],
    primarySignificator: 'Sun',
    jaiminiSignificator: 'Atmakaraka (AK)',
    meaningEn: 'The convergence of Lagna (self and vitality), 9th Bhava (destiny and grace), and 10th Bhava (manifest worldly purpose) defines the macro-karmic trajectory of the incarnation.',
    meaningHi: 'लग्न (स्वयं), भाग्य (नवम) एवं कर्म (दशम) का संश्लेष व्यक्ति के समग्र जीवन उद्देश्य एवं नियति को प्रकट करता है।',
    karmicDomainNoteEn: 'Governs fundamental life transitions, soul alignment, destiny milestones, and overall life evolution.',
    karmicDomainNoteHi: 'यह जीवन के प्रमुख मोड़ों, आत्म-उन्नति एवं भाग्य के पूर्ण प्रकटीकरण से संबंधित है।',
    activePrinciplesEn: ['Uncompromising ethical integrity', 'Courage in fulfilling one’s unique calling', 'Alignment with cosmic order (Rta)'],
    activePrinciplesHi: ['सत्य एवं निष्ठा', 'स्वधर्म पालन', 'धैर्यपूर्वक आगे बढ़ना'],
    growthKeywordsEn: ['Pivotal milestone consolidation', 'Authentic soul expression', 'Long-range destiny alignment'],
    growthKeywordsHi: ['महत्वपूर्ण पड़ाव की सिद्धि', 'आत्म-स्वरूप का प्रकटीकरण', 'दीर्घकालिक लक्ष्य संरेखण'],
    cautionKeywordsEn: ['Stay rooted in ethical principles', 'Pace major life commitments deliberately'],
    cautionKeywordsHi: ['सदाचार व धर्म पर अडिग रहें', 'महत्वपूर्ण निर्णयों में संतुलन रखें']
  }
};

// ============================================================================
// RESOLVE 5-LEVEL DASHA FOR ANY TARGET DATE (WITH SANDHI & TRANSITIONS)
// ============================================================================
export function resolve5LevelDashaForDate(
  dashas: DashaPeriod[],
  targetDate: Date | string
): Dasha5LevelResolution {
  const targetMs = new Date(targetDate).getTime();

  // 1. Mahadasha (Level 1)
  let md = dashas.find(d => {
    const s = new Date(d.startDate).getTime();
    const e = new Date(d.endDate).getTime();
    return targetMs >= s && targetMs <= e;
  });

  if (!md) {
    md = targetMs < new Date(dashas[0].startDate).getTime() ? dashas[0] : dashas[dashas.length - 1];
  }

  const mdIndex = dashas.findIndex(d => d.planet === md!.planet && d.startDate === md!.startDate);
  const prevMd = mdIndex > 0 ? dashas[mdIndex - 1] : undefined;
  const nextMd = mdIndex >= 0 && mdIndex < dashas.length - 1 ? dashas[mdIndex + 1] : undefined;

  const mdStartMs = new Date(md.startDate).getTime();
  const mdEndMs = new Date(md.endDate).getTime();
  const mdDurationMs = Math.max(1, mdEndMs - mdStartMs);
  const mdElapsedMs = Math.max(0, targetMs - mdStartMs);
  const mdProgress = Math.min(100, Math.max(0, Math.round((mdElapsedMs / mdDurationMs) * 100)));
  const mdDaysRemaining = Math.max(0, Math.ceil((mdEndMs - targetMs) / (1000 * 60 * 60 * 24)));

  const mdSandhiMarginDays = 180;
  const mdDaysElapsed = Math.round(mdElapsedMs / (1000 * 60 * 60 * 24));
  const isMdSandhi = mdDaysElapsed <= mdSandhiMarginDays || mdDaysRemaining <= mdSandhiMarginDays;
  const mdSandhiType = mdDaysElapsed <= mdSandhiMarginDays ? 'Entering' : mdDaysRemaining <= mdSandhiMarginDays ? 'Exiting' : 'None';

  // 2. Antardasha (Level 2)
  const adList = md.antardashas || [];
  let ad = adList.find(a => {
    const s = new Date(a.startDate).getTime();
    const e = new Date(a.endDate).getTime();
    return targetMs >= s && targetMs <= e;
  }) || adList[0] || {
    planet: md.planet,
    startDate: md.startDate,
    endDate: md.endDate
  };

  const adIndex = adList.findIndex(a => a.planet === ad.planet && a.startDate === ad.startDate);
  const prevAd = adIndex > 0 ? adList[adIndex - 1] : undefined;
  const nextAd = adIndex >= 0 && adIndex < adList.length - 1 ? adList[adIndex + 1] : undefined;

  const adStartMs = new Date(ad.startDate).getTime();
  const adEndMs = new Date(ad.endDate).getTime();
  const adDurationMs = Math.max(1, adEndMs - adStartMs);
  const adElapsedMs = Math.max(0, targetMs - adStartMs);
  const adProgress = Math.min(100, Math.max(0, Math.round((adElapsedMs / adDurationMs) * 100)));
  const adDaysRemaining = Math.max(0, Math.ceil((adEndMs - targetMs) / (1000 * 60 * 60 * 24)));

  const adSandhiMarginDays = 30;
  const adDaysElapsed = Math.round(adElapsedMs / (1000 * 60 * 60 * 24));
  const isAdSandhi = adDaysElapsed <= adSandhiMarginDays || adDaysRemaining <= adSandhiMarginDays;
  const adSandhiType = adDaysElapsed <= adSandhiMarginDays ? 'Entering' : adDaysRemaining <= adSandhiMarginDays ? 'Exiting' : 'None';

  // 3. Pratyantardasha (Level 3)
  const pdList = calculatePratyantardashas(ad.planet, new Date(ad.startDate), new Date(ad.endDate));
  let pd = pdList.find(p => {
    const s = new Date(p.startDate).getTime();
    const e = new Date(p.endDate).getTime();
    return targetMs >= s && targetMs <= e;
  }) || pdList[0] || {
    planet: ad.planet,
    startDate: ad.startDate,
    endDate: ad.endDate,
    durationDays: 30
  };

  const pdIndex = pdList.findIndex(p => p.planet === pd.planet && p.startDate === pd.startDate);
  const prevPd = pdIndex > 0 ? pdList[pdIndex - 1] : undefined;
  const nextPd = pdIndex >= 0 && pdIndex < pdList.length - 1 ? pdList[pdIndex + 1] : undefined;

  const pdStartMs = new Date(pd.startDate).getTime();
  const pdEndMs = new Date(pd.endDate).getTime();
  const pdDurationMs = Math.max(1, pdEndMs - pdStartMs);
  const pdElapsedMs = Math.max(0, targetMs - pdStartMs);
  const pdProgress = Math.min(100, Math.max(0, Math.round((pdElapsedMs / pdDurationMs) * 100)));
  const pdDaysRemaining = Math.max(0, Math.ceil((pdEndMs - targetMs) / (1000 * 60 * 60 * 24)));
  const isPdSandhi = pdDaysRemaining <= 5;

  // 4. Sūkshma Dasha (Level 4)
  const sdList = calculateSookshmaDashas(pd.planet, new Date(pd.startDate), new Date(pd.endDate));
  let sd = sdList.find(s => {
    const st = new Date(s.startDate).getTime();
    const et = new Date(s.endDate).getTime();
    return targetMs >= st && targetMs <= et;
  }) || sdList[0] || {
    planet: pd.planet,
    startDate: pd.startDate,
    endDate: pd.endDate
  };

  const sdStartMs = new Date(sd.startDate).getTime();
  const sdEndMs = new Date(sd.endDate).getTime();
  const sdDurationMs = Math.max(1, sdEndMs - sdStartMs);
  const sdElapsedMs = Math.max(0, targetMs - sdStartMs);
  const sdProgress = Math.min(100, Math.max(0, Math.round((sdElapsedMs / sdDurationMs) * 100)));
  const sdHoursRemaining = Math.max(0, Math.ceil((sdEndMs - targetMs) / (1000 * 60 * 60)));

  // 5. Prāṇa Dasha (Level 5)
  const prdList = calculatePranaDashas(sd.planet, new Date(sd.startDate), new Date(sd.endDate));
  let prd = prdList.find(p => {
    const st = new Date(p.startDate).getTime();
    const et = new Date(p.endDate).getTime();
    return targetMs >= st && targetMs <= et;
  }) || prdList[0] || {
    planet: sd.planet,
    startDate: sd.startDate,
    endDate: sd.endDate
  };

  const prdStartMs = new Date(prd.startDate).getTime();
  const prdEndMs = new Date(prd.endDate).getTime();
  const prdDurationMs = Math.max(1, prdEndMs - prdStartMs);
  const prdElapsedMs = Math.max(0, targetMs - prdStartMs);
  const prdProgress = Math.min(100, Math.max(0, Math.round((prdElapsedMs / prdDurationMs) * 100)));
  const prdHoursRemaining = Math.max(0, Math.ceil((prdEndMs - targetMs) / (1000 * 60 * 60)));

  const activePathSummary = `${md.planet} → ${ad.planet} → ${pd.planet} → ${sd.planet} → ${prd.planet}`;
  const isDashaSandhiActive = isMdSandhi || isAdSandhi || isPdSandhi;

  let dashaSandhiNoteEn: string | undefined;
  let dashaSandhiNoteHi: string | undefined;

  if (isMdSandhi) {
    dashaSandhiNoteEn = `Mahadasha Sandhi (${mdSandhiType} Phase): Transition between ${prevMd?.planet || md.planet} and ${md.planet}. Energy realignment across broad life patterns.`;
    dashaSandhiNoteHi = `महादशा संधि (${mdSandhiType === 'Entering' ? 'प्रवेश काल' : 'समापन काल'}): ${prevMd?.planet || md.planet} एवं ${md.planet} के मध्य ऊर्जा संक्रांति।`;
  } else if (isAdSandhi) {
    dashaSandhiNoteEn = `Antardasha Sandhi (${adSandhiType} Phase): Transitioning towards ${nextAd?.planet || 'upcoming lord'}. Old phase culminating.`;
    dashaSandhiNoteHi = `अंतर्दशा संधि: ${ad.planet} अंतर्दशा का समापन काल। आगामी ${nextAd?.planet || 'दशानाथ'} की ओर अग्रसर।`;
  }

  const mdLevel: DashaLevelInfo = {
    levelName: 'Mahadasha',
    levelCode: 'MD',
    planet: md.planet,
    startDate: md.startDate,
    endDate: md.endDate,
    progressPercent: mdProgress,
    durationFormatted: `${Math.round(mdDurationMs / (86400000 * 365.25))} Years`,
    daysRemaining: mdDaysRemaining,
    isSandhi: isMdSandhi,
    sandhiType: mdSandhiType,
    previousPlanet: prevMd?.planet,
    nextPlanet: nextMd?.planet,
    transitionDate: md.endDate
  };

  const adLevel: DashaLevelInfo = {
    levelName: 'Antardasha',
    levelCode: 'AD',
    planet: ad.planet,
    startDate: ad.startDate,
    endDate: ad.endDate,
    progressPercent: adProgress,
    durationFormatted: `${Math.round(adDurationMs / (86400000 * 30.4375))} Months`,
    daysRemaining: adDaysRemaining,
    isSandhi: isAdSandhi,
    sandhiType: adSandhiType,
    previousPlanet: prevAd?.planet,
    nextPlanet: nextAd?.planet,
    transitionDate: ad.endDate
  };

  const pdLevel: DashaLevelInfo = {
    levelName: 'Pratyantardasha',
    levelCode: 'PD',
    planet: pd.planet,
    startDate: pd.startDate,
    endDate: pd.endDate,
    progressPercent: pdProgress,
    durationFormatted: `${pd.durationDays || Math.round(pdDurationMs / 86400000)} Days`,
    daysRemaining: pdDaysRemaining,
    isSandhi: isPdSandhi,
    previousPlanet: prevPd?.planet,
    nextPlanet: nextPd?.planet,
    transitionDate: pd.endDate
  };

  const sdLevel: DashaLevelInfo = {
    levelName: 'Sūkshma Dasha',
    levelCode: 'SD',
    planet: sd.planet,
    startDate: sd.startDate,
    endDate: sd.endDate,
    progressPercent: sdProgress,
    durationFormatted: `${Math.round(sdDurationMs / 3600000)} Hours`,
    hoursRemaining: sdHoursRemaining,
    isSandhi: false,
    transitionDate: sd.endDate
  };

  const prdLevel: DashaLevelInfo = {
    levelName: 'Prāṇa Dasha',
    levelCode: 'PRD',
    planet: prd.planet,
    startDate: prd.startDate,
    endDate: prd.endDate,
    progressPercent: prdProgress,
    durationFormatted: `${Number((prdDurationMs / 3600000).toFixed(1))} Hours`,
    hoursRemaining: prdHoursRemaining,
    isSandhi: false,
    transitionDate: prd.endDate
  };

  return {
    md: mdLevel,
    ad: adLevel,
    pd: pdLevel,
    sd: sdLevel,
    prd: prdLevel,
    activePathSummary,
    isDashaSandhiActive,
    dashaSandhiNoteEn,
    dashaSandhiNoteHi
  };
}

// ============================================================================
// EVALUATE ACTIVE GOCHAR TRIGGERS FOR TARGET DATE
// ============================================================================
export function evaluateGocharTriggersForDate(
  kundali: CompleteKundali,
  targetDate: Date | string
): GocharTriggerInfo[] {
  const targetObj = new Date(targetDate);
  const convention = kundali.nodeConvention || kundali.birthDetails?.nodeConvention || 'mean';
  const ephemArray = calculateLivePlanetaryEphemeris(targetObj, convention);
  const liveEphem: Record<string, typeof ephemArray[0]> = {};
  ephemArray.forEach(p => {
    liveEphem[p.planet] = p;
  });
  const triggers: GocharTriggerInfo[] = [];

  const ascPlanet = kundali.planets?.find(p => p.name === 'Ascendant');
  const natalAscHouse = kundali.houses?.find(h => h.houseNumber === 1);
  const ascSign = ascPlanet?.sign || natalAscHouse?.sign || 'Aries';
  const ascIndex = ascPlanet?.signIndex ?? (natalAscHouse ? natalAscHouse.signIndex : ZODIAC_SIGNS.indexOf(ascSign));

  // Monitor all key planets including Moon for fast short-duration lived states
  const majorWatchPlanets: PlanetName[] = ['Saturn', 'Jupiter', 'Rahu', 'Ketu', 'Mars', 'Sun', 'Mercury', 'Venus', 'Moon'];

  for (const pName of majorWatchPlanets) {
    const transPos = liveEphem[pName];
    if (!transPos) continue;

    const transSignIdx = transPos.signIndex;
    const transDeg = transPos.degreeInSign;
    const isRetro = transPos.isRetrograde || false;

    // Transiting Bhava relative to Lagna
    const transitingHouse = ((transSignIdx - ascIndex + 12) % 12) + 1;

    // Evaluate special aspects & conjunctions
    const aspectsToEvaluate: Array<{ offset: number; aspectType: GocharTriggerInfo['aspectType'] }> = [
      { offset: 0, aspectType: 'Conjunction (Yuti)' },
      { offset: 6, aspectType: 'Opposite (7th)' }
    ];

    if (pName === 'Saturn') {
      aspectsToEvaluate.push({ offset: 2, aspectType: 'Special_3rd' });
      aspectsToEvaluate.push({ offset: 9, aspectType: 'Special_10th' });
    } else if (pName === 'Jupiter') {
      aspectsToEvaluate.push({ offset: 4, aspectType: 'Special_5th' });
      aspectsToEvaluate.push({ offset: 8, aspectType: 'Special_9th' });
    } else if (pName === 'Mars') {
      aspectsToEvaluate.push({ offset: 3, aspectType: 'Special_4th' });
      aspectsToEvaluate.push({ offset: 7, aspectType: 'Special_8th' });
    }

    for (const { offset, aspectType } of aspectsToEvaluate) {
      const aspectedHouse = ((transitingHouse - 1 + offset) % 12) + 1;
      const natalTargetPlanet = kundali.planets?.find(p => p.house === aspectedHouse && p.name !== 'Ascendant');

      let aspectPushed = false;
      if (natalTargetPlanet) {
        const natalDeg = natalTargetPlanet.degreeInSign || 15;
        const orb = Math.abs(transDeg - natalDeg);

        if (orb <= 8.0) {
          const isExact = orb <= 1.5;
          const isApproaching = isRetro ? transDeg > natalDeg : transDeg < natalDeg;
          const phase: TransitTriggerPhase = isExact ? 'Exact' : isRetro ? 'Repeating' : isApproaching ? 'Approaching' : 'Separating';

          const impactEn = `${pName} (${isRetro ? 'Vakri ' : ''}${transPos.formattedDegree}) is ${phase.toLowerCase()} ${aspectType} to natal ${natalTargetPlanet.name} in H${aspectedHouse}.`;
          const impactHi = `${pName} (${isRetro ? 'वक्री ' : ''}${transPos.formattedDegree}) भाव ${aspectedHouse} में स्थित जन्मकालीन ${natalTargetPlanet.name} पर ${aspectType} दृष्टि डाल रहा है (${phase})।`;

          triggers.push({
            planet: pName,
            transitingSign: transPos.sign,
            transitingSignIndex: transSignIdx,
            degreeFormatted: transPos.formattedDegree,
            degreeNumber: transDeg,
            isRetrograde: isRetro,
            natalTarget: {
              type: 'Natal_Planet',
              targetName: natalTargetPlanet.name,
              targetHouse: aspectedHouse,
              exactDegree: natalDeg
            },
            aspectType,
            orbDegrees: Number(orb.toFixed(2)),
            phase,
            isExact,
            isApproaching,
            isRepeating: isRetro,
            impactSummaryEn: impactEn,
            impactSummaryHi: impactHi
          });
          aspectPushed = true;
        }
      }

      if (offset === 0 && !aspectPushed) {
        triggers.push({
          planet: pName,
          transitingSign: transPos.sign,
          transitingSignIndex: transSignIdx,
          degreeFormatted: transPos.formattedDegree,
          degreeNumber: transDeg,
          isRetrograde: isRetro,
          natalTarget: {
            type: 'Bhava',
            targetName: `House ${transitingHouse}`,
            targetHouse: transitingHouse
          },
          aspectType: 'Conjunction (Yuti)',
          orbDegrees: 0,
          phase: 'Exact',
          isExact: true,
          isApproaching: false,
          isRepeating: isRetro,
          impactSummaryEn: `${pName} is actively transiting House ${transitingHouse} (${transPos.sign} ${transPos.formattedDegree}).`,
          impactSummaryHi: `${pName} वर्तमान में भाव ${transitingHouse} (${transPos.sign}) में गोचर कर रहा है।`
        });
      }
    }
  }

  return triggers;
}

// ============================================================================
// TEMPORAL STATUS & DYNAMIC ACTIVATION WINDOW CALCULATOR
// Computes absolute dates, dynamic duration, and relative timing from Focus Date
// ============================================================================
export function calculateTemporalStatusAndWindow(
  isoStartDate: string,
  isoEndDate: string,
  focusDate: Date | string,
  lifecycleState: ActivationLifecycleState = 'Active',
  peakDateStr?: string
): {
  temporalStatus: TemporalStatus;
  temporalTier: AnubhavaTemporalTier;
  statusLabelEn: string;
  statusLabelHi: string;
  startDateFormatted: string;
  endDateFormatted: string;
  peakDateFormatted?: string;
  isoStartDate: string;
  isoEndDate: string;
  durationDays: number;
  durationLabelEn: string;
  durationLabelHi: string;
  relativeTimingEn: string;
  relativeTimingHi: string;
  currentStateEligible: boolean;
} {
  const start = new Date(isoStartDate);
  const end = new Date(isoEndDate);
  const focus = new Date(focusDate);

  const durationDays = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000));
  
  // Format dates strictly (authoritative representation)
  const startDateFormatted = formatDate(start);
  const endDateFormatted = formatDate(end);
  const peakDateFormatted = peakDateStr ? formatDate(new Date(peakDateStr)) : undefined;

  // Duration Label dynamically derived from actual interval
  let durationLabelEn = '';
  let durationLabelHi = '';
  if (durationDays <= 3) {
    durationLabelEn = `${durationDays}-day acute window`;
    durationLabelHi = `${durationDays} दिवसीय सूक्ष्म कालखंड`;
  } else if (durationDays <= 14) {
    durationLabelEn = `~${durationDays}-day window`;
    durationLabelHi = `~${durationDays} दिवसीय कालखंड`;
  } else if (durationDays <= 70) {
    const weeks = Math.round(durationDays / 7);
    durationLabelEn = `~${weeks}-week activation window (${durationDays} days)`;
    durationLabelHi = `~${weeks} सप्ताह का सक्रिय कालखंड (${durationDays} दिन)`;
  } else {
    const months = Number((durationDays / 30.4).toFixed(1));
    durationLabelEn = `~${months}-month activation window (${durationDays} days)`;
    durationLabelHi = `~${months} माह का सक्रिय कालखंड (${durationDays} दिन)`;
  }

  // Authoritative Temporal Tier derived strictly from window duration
  let temporalTier: AnubhavaTemporalTier = 'Macro (1+ Years)';
  if (durationDays <= 3) {
    temporalTier = 'Immediate (1-3 Days)';
  } else if (durationDays <= 35) {
    temporalTier = 'Short-Term (1-4 Weeks)';
  } else if (durationDays <= 240) {
    temporalTier = 'Medium-Term (1-6 Months)';
  } else {
    temporalTier = 'Macro (1+ Years)';
  }

  // Focus Date relative comparison
  const msUntilStart = start.getTime() - focus.getTime();
  const msRemaining = end.getTime() - focus.getTime();

  let temporalStatus: TemporalStatus = 'ACTIVE';
  let statusLabelEn = 'ACTIVE NOW';
  let statusLabelHi = 'वर्तमान में सक्रिय';
  let relativeTimingEn = '';
  let relativeTimingHi = '';
  let currentStateEligible = false;

  if (focus.getTime() < start.getTime()) {
    const daysUntilStart = Math.ceil(msUntilStart / 86400000);
    if (daysUntilStart <= 3) {
      temporalStatus = 'EMERGING';
      statusLabelEn = 'EMERGING';
      statusLabelHi = 'उदीयमान';
    } else {
      temporalStatus = 'UPCOMING';
      statusLabelEn = 'UPCOMING';
      statusLabelHi = 'आगामी';
    }

    if (daysUntilStart <= 1) {
      relativeTimingEn = 'Starts tomorrow';
      relativeTimingHi = 'कल से प्रारंभ';
    } else if (daysUntilStart <= 14) {
      relativeTimingEn = `Starts in ~${daysUntilStart} days`;
      relativeTimingHi = `~${daysUntilStart} दिनों में प्रारंभ`;
    } else if (daysUntilStart <= 60) {
      const wks = Math.round(daysUntilStart / 7);
      relativeTimingEn = `Starts in ~${wks} weeks`;
      relativeTimingHi = `~${wks} सप्ताह में प्रारंभ`;
    } else {
      const mos = Number((daysUntilStart / 30.4).toFixed(1));
      relativeTimingEn = `Starts in ~${mos} months`;
      relativeTimingHi = `~${mos} माह में प्रारंभ`;
    }
    currentStateEligible = daysUntilStart <= 1; // adjacent proximity can be emerging
  } else if (focus.getTime() >= start.getTime() && focus.getTime() <= end.getTime()) {
    const daysRemaining = Math.max(0, Math.ceil(msRemaining / 86400000));

    if (daysRemaining <= 3 && durationDays > 5) {
      temporalStatus = 'RELEASING';
      statusLabelEn = 'RELEASING';
      statusLabelHi = 'समापन काल';
      relativeTimingEn = `Releasing · ~${daysRemaining} days remaining`;
      relativeTimingHi = `समापन की ओर · ~${daysRemaining} दिन शेष`;
    } else {
      if (lifecycleState === 'Peak') {
        temporalStatus = 'PEAK';
        statusLabelEn = 'PEAK ACTIVE';
        statusLabelHi = 'शिखर पर सक्रिय';
      } else if (lifecycleState === 'Intensifying') {
        temporalStatus = 'INTENSIFYING';
        statusLabelEn = 'INTENSIFYING';
        statusLabelHi = 'तीव्रगामी';
      } else {
        temporalStatus = 'ACTIVE';
        statusLabelEn = 'ACTIVE NOW';
        statusLabelHi = 'वर्तमान में सक्रिय';
      }

      if (daysRemaining > 14) {
        const weeksLeft = Math.round(daysRemaining / 7);
        relativeTimingEn = `Active for ~${weeksLeft} more weeks (${daysRemaining} days left)`;
        relativeTimingHi = `आगामी ~${weeksLeft} सप्ताह सक्रिय (${daysRemaining} दिन शेष)`;
      } else {
        relativeTimingEn = `Active now · ~${daysRemaining} days remaining`;
        relativeTimingHi = `वर्तमान में सक्रिय · ~${daysRemaining} दिन शेष`;
      }
    }
    currentStateEligible = true;
  } else {
    const daysAgo = Math.floor((focus.getTime() - end.getTime()) / 86400000);
    temporalStatus = 'ENDED';
    statusLabelEn = 'ENDED';
    statusLabelHi = 'समाप्त';
    relativeTimingEn = `Concluded ~${daysAgo} days ago`;
    relativeTimingHi = `~${daysAgo} दिन पूर्व समाप्त`;
    currentStateEligible = false;
  }

  return {
    temporalStatus,
    temporalTier,
    statusLabelEn,
    statusLabelHi,
    startDateFormatted,
    endDateFormatted,
    peakDateFormatted,
    isoStartDate,
    isoEndDate,
    durationDays,
    durationLabelEn,
    durationLabelHi,
    relativeTimingEn,
    relativeTimingHi,
    currentStateEligible
  };
}

// ============================================================================
// EVENT SIGNATURE & VARTAMĀNA ANUBHAVA ENGINE (SHORT-TERM LIVED EXPERIENCES)
// ============================================================================
export function detectEventSignaturesAndLivedStates(
  kundali: CompleteKundali,
  dashaResolution: Dasha5LevelResolution,
  gocharTriggers: GocharTriggerInfo[],
  targetDate: Date | string = new Date(),
  horizon: VartamanaHorizon = '30D'
): {
  livedExperiences: VartamanaAnubhavaSignature[];
  convergenceSignatures: VartamanaAnubhavaSignature[];
  hasLivedExperiences: boolean;
} {
  const targetObj = new Date(targetDate);
  const convention = kundali.nodeConvention || kundali.birthDetails?.nodeConvention || 'mean';
  const ephemArray = calculateLivePlanetaryEphemeris(targetObj, convention);
  const liveEphem: Record<string, typeof ephemArray[0]> = {};
  ephemArray.forEach(p => {
    liveEphem[p.planet] = p;
  });
  const livedExperiences: VartamanaAnubhavaSignature[] = [];
  const convergenceSignatures: VartamanaAnubhavaSignature[] = [];

  // Ascendant & House Mapping
  const ascHouse = kundali.houses?.find(h => h.houseNumber === 1);
  const ascSign = (ascHouse?.sign || 'Aries') as ZodiacSign;
  const ascIndex = ascHouse?.signIndex ?? ZODIAC_SIGNS.indexOf(ascSign);

  const houseLordsMap: Record<number, PlanetName> = {};
  kundali.houses?.forEach(h => {
    houseLordsMap[h.houseNumber] = (h.signLord || ZODIAC_LORDS[h.sign] || 'Mars') as PlanetName;
  });

  const natalMoon = kundali.planets?.find(p => p.name === 'Moon');
  const natalMoonHouse = natalMoon?.house || 1;
  const natalMoonSign = natalMoon?.sign || 'Taurus';
  const natalMoonNakshatra = natalMoon?.nakshatra || 'Rohini';

  const transMoon = liveEphem['Moon'];
  const transMoonSign = transMoon?.sign || 'Taurus';
  const transMoonHouse = transMoon ? (((transMoon.signIndex - ascIndex + 12) % 12) + 1) : 1;
  const transMoonFromNatalMoon = transMoon && natalMoon ? (((transMoon.signIndex - natalMoon.signIndex + 12) % 12) + 1) : 1;

  const mdPlanet = dashaResolution.md.planet;
  const adPlanet = dashaResolution.ad.planet;
  const pdPlanet = dashaResolution.pd.planet;
  const sdPlanet = dashaResolution.sd.planet;
  const prdPlanet = dashaResolution.prd.planet;

  // Check transits aspecting/conjuncting transiting Moon
  const transSaturn = liveEphem['Saturn'];
  const transRahu = liveEphem['Rahu'];
  const transKetu = liveEphem['Ketu'];
  const transMars = liveEphem['Mars'];

  const isMoonConjunctMalefic = transMoon && [transSaturn, transRahu, transKetu, transMars].some(mal => mal && mal.sign === transMoon.sign);
  const isChandrashtama = transMoonFromNatalMoon === 8 || transMoonHouse === 8 || transMoonHouse === 12;
  const isChandraDashaResonance = [pdPlanet, sdPlanet, prdPlanet].includes('Moon') || [pdPlanet, sdPlanet, prdPlanet].includes('Ketu');

  // ==========================================================================
  // SIGNATURE 1: MANAS / CHITTA — HEIGHTENED EMOTIONAL SENSITIVITY (NAVRATAN CASE)
  // ==========================================================================
  if (isChandrashtama || isMoonConjunctMalefic || (isChandraDashaResonance && (transMoonHouse === 6 || transMoonHouse === 8 || transMoonHouse === 12 || isMoonConjunctMalefic))) {
    const triggerDesc = isChandrashtama
      ? `Chandra transiting House ${transMoonHouse} (${transMoonSign}) [${transMoonFromNatalMoon}th from natal Moon].`
      : `Chandra transiting ${transMoonSign} under ${sdPlanet} SD & ${prdPlanet} PRD alignment.`;

    const isoStart = targetObj.toISOString();
    const isoEnd = new Date(targetObj.getTime() + 2 * 86400000).toISOString();
    const temporal = calculateTemporalStatusAndWindow(isoStart, isoEnd, targetObj, 'Active');

    livedExperiences.push({
      signatureId: 'manas_emotional_sensitivity',
      category: 'MANAS_CHITTA',
      categoryLabelEn: 'Manas / Emotional Mind',
      categoryLabelHi: 'मनस / भावनात्मक चित्त',
      titleEn: 'Heightened Emotional Sensitivity & Reflective Inflow',
      titleHi: 'भावनात्मक संवेदनशीलता व अंतर्मुखी प्रवाह',
      shortDescriptionEn: 'Temporary amplification of emotional permeability and subconscious receptivity over the immediate 1–2 day lunar transit.',
      shortDescriptionHi: 'चंद्र गोचर के प्रभाव से तात्कालिक १-२ दिनों में भावनात्मक संवेदनशीलता एवं अंतर्मुखी चित्त की सक्रियता।',
      temporalTier: 'Immediate (1-3 Days)',
      priority: 'Short-Term',
      temporalStatus: temporal.temporalStatus,
      statusLabelEn: temporal.statusLabelEn,
      statusLabelHi: temporal.statusLabelHi,
      startDate: temporal.startDateFormatted,
      endDate: temporal.endDateFormatted,
      isoStartDate: isoStart,
      isoEndDate: isoEnd,
      durationDays: temporal.durationDays,
      durationLabelEn: temporal.durationLabelEn,
      durationLabelHi: temporal.durationLabelHi,
      relativeTimingEn: temporal.relativeTimingEn,
      relativeTimingHi: temporal.relativeTimingHi,
      currentStateEligible: temporal.currentStateEligible,
      isImmediateToday: true,
      evidenceStrength: 'Strong',
      sthiti: {
        astrologicalConditionEn: `Transiting Chandra occupies House ${transMoonHouse} (${transMoonSign} ${transMoon?.formattedDegree}) in resonance with ${sdPlanet} Sūkshma & ${prdPlanet} Prāṇa Dasha.`,
        astrologicalConditionHi: `गोचर चंद्र भाव ${transMoonHouse} (${transMoonSign}) में स्थित होकर ${sdPlanet} सूक्ष्म एवं ${prdPlanet} प्राण दशा के संरेखण में सक्रिय है।`,
        triggerPlanets: ['Moon', sdPlanet, prdPlanet],
        activatedHouses: [transMoonHouse, 4, 12],
        transitTriggerDetails: triggerDesc,
        dashaSanctionLevel: `${sdPlanet} Sūkshma & ${prdPlanet} Prāṇa fine-timing support.`
      },
      artha: {
        meaningEn: 'In classical Jyotish, Chandra governs the fluid emotional mind (Manas) and sensory responsiveness. A sensitive transit does not indicate a negative state, but rather a temporary period of deep emotional receptivity where subtle impressions are felt vividly.',
        meaningHi: 'ज्योतिष में चंद्रमा मन और संवेदना का स्वामी है। यह स्थिति किसी नकारात्मक विकार की नहीं, अपितु गहन आंतरिक संवेदनशीलता एवं सूक्ष्म भावनाओं के प्रति सजगता की सूचक है।',
        classicalPrincipleEn: 'Chandra Sanchara (Lunar transit mechanics) modulates diurnal mental rhythm and subjective perception.',
        classicalPrincipleHi: 'चंद्र संचार तात्कालिक मानसिक गति एवं अनुभूति को प्रभावित करता है।'
      },
      phala: {
        manifestationEn: 'You may notice heightened intuition, deep sensitivity to social environments, or an instinct to reflect quietly. It is an ideal window for creative expression, contemplative reading, and gentle pacing.',
        manifestationHi: 'यह समय अंतःप्रेरणा, गहन चिंतन एवं शांत वातावरण में समय व्यतीत करने हेतु अनुकूल है। कलात्मक सृजन एवं ध्यान के लिए उत्तम कालखंड।',
        actionableGuidanceEn: [
          'Allow space for restorative quietude and gentle pacing',
          'Channel emotional depth into writing, art, or contemplative journaling',
          'Defer sharp interpersonal confrontations to a more neutral transit window'
        ],
        actionableGuidanceHi: [
          'शांत व विश्रामपूर्ण दिनचर्या को प्राथमिकता दें',
          'भावनात्मक ऊर्जा को सृजनात्मक लेखन या कला में लगाएं',
          'तीखे वाद-विवाद से बचें एवं निर्णयों में जल्दबाजी न करें'
        ]
      },
      evidenceChain: [
        {
          hierarchyTier: 'Sūkshma/Prāṇa',
          factor: `${sdPlanet} SD • ${prdPlanet} PRD`,
          descriptionEn: `Sub-period fine timing activates emotional responsiveness without requiring a macro dasha shift.`,
          descriptionHi: `सूक्ष्म एवं प्राण दशा तात्कालिक संवेदना को जागृत कर रही हैं।`,
          supportWeight: 80
        },
        {
          hierarchyTier: 'Gochar Trigger',
          factor: `Chandra in ${transMoonSign} (H${transMoonHouse})`,
          descriptionEn: `${triggerDesc}`,
          descriptionHi: `चंद्रमा का भाव ${transMoonHouse} में गोचर।`,
          supportWeight: 90
        }
      ],
      supportingFactors: [
        `Chandra transiting sensitive angle (House ${transMoonHouse} from Lagna / ${transMoonFromNatalMoon}th from natal Moon).`,
        `Resonance with current ${sdPlanet} Sūkshma Dasha.`
      ],
      limitingFactors: [
        'Short-duration transit (~48–54 hours); resolves naturally as Moon advances into subsequent sign.'
      ],
      technicalContext: {
        chandraSign: transMoonSign,
        chandraHouse: transMoonHouse,
        chandraNakshatra: natalMoonNakshatra,
        savPoints: transMoon ? getSavBySign(kundali.ashtakvarga, transMoon.signIndex) : 28
      }
    });
  }

  // ==========================================================================
  // SIGNATURE 2: KARMA / CAREER — PROFESSIONAL TRANSITION & ROLE SHIFT (AJITESH CASE)
  // ==========================================================================
  const h10Lord = houseLordsMap[10] || 'Saturn';
  const h6Lord = houseLordsMap[6] || 'Mars';
  const h8Lord = houseLordsMap[8] || 'Venus';

  const isCareerDashaActive = [mdPlanet, adPlanet, pdPlanet].includes(h10Lord) || [mdPlanet, adPlanet, pdPlanet].includes('Saturn') || [mdPlanet, adPlanet, pdPlanet].includes('Sun');
  const isTransitionAxisActive = [adPlanet, pdPlanet].includes(h6Lord) || [adPlanet, pdPlanet].includes(h8Lord) || dashaResolution.isDashaSandhiActive;

  const careerGocharTrigger = gocharTriggers.find(gt => gt.natalTarget.targetHouse === 10 || gt.natalTarget.targetHouse === 6 || gt.planet === h10Lord);

  let isCareerTransitionActive = false;
  let careerSigRef: VartamanaAnubhavaSignature | undefined;

  if ((isCareerDashaActive && isTransitionAxisActive) || (careerGocharTrigger && isCareerDashaActive) || (dashaResolution.ad.isSandhi && [h10Lord, 'Saturn'].includes(adPlanet))) {
    isCareerTransitionActive = true;
    const isoStart = dashaResolution.ad.startDate;
    const isoEnd = dashaResolution.ad.endDate;
    const temporal = calculateTemporalStatusAndWindow(isoStart, isoEnd, targetObj, 'Active');

    const careerSig: VartamanaAnubhavaSignature = {
      signatureId: 'karma_professional_transition',
      category: 'KARMA_CAREER',
      categoryLabelEn: 'Karma / Career & Role',
      categoryLabelHi: 'कर्म / आजीविका व पद',
      titleEn: 'Professional Transition, Organizational Shift or Role Pivot',
      titleHi: 'व्यावसायिक परिवर्तन, पद-फेरबदल व दायित्व पुनर्गठन',
      shortDescriptionEn: 'Active astrological alignment consistent with concluding an organizational role, stepping into new terms, or restructuring career trajectory.',
      shortDescriptionHi: 'कार्यक्षेत्र में पूर्व पद से मुक्ति, नए अनुबंध की ओर अग्रसर होना अथवा आजीविका में संरचनात्मक परिवर्तन का काल।',
      temporalTier: temporal.temporalTier,
      priority: 'Primary',
      temporalStatus: temporal.temporalStatus,
      statusLabelEn: temporal.statusLabelEn,
      statusLabelHi: temporal.statusLabelHi,
      startDate: temporal.startDateFormatted,
      endDate: temporal.endDateFormatted,
      isoStartDate: isoStart,
      isoEndDate: isoEnd,
      durationDays: temporal.durationDays,
      durationLabelEn: temporal.durationLabelEn,
      durationLabelHi: temporal.durationLabelHi,
      relativeTimingEn: temporal.relativeTimingEn,
      relativeTimingHi: temporal.relativeTimingHi,
      currentStateEligible: temporal.currentStateEligible,
      isImmediateToday: false,
      evidenceStrength: 'Strongly Corroborated',
      sthiti: {
        astrologicalConditionEn: `10th Bhava (Karma Sthana ruled by ${h10Lord}) and 6th/8th transition axis are engaged under ${adPlanet} AD & ${pdPlanet} PD with ${careerGocharTrigger ? careerGocharTrigger.planet : 'Saturn'} transit contact.`,
        astrologicalConditionHi: `दशम भाव (कर्मेश ${h10Lord}) एवं षष्ठ/अष्टम संक्रांति अक्ष ${adPlanet} अंतर्दशा एवं ${pdPlanet} प्रत्यंतर्दशा के अंतर्गत सक्रिय हैं।`,
        triggerPlanets: [h10Lord, adPlanet, pdPlanet],
        activatedHouses: [10, 6, 8],
        transitTriggerDetails: careerGocharTrigger ? careerGocharTrigger.impactSummaryEn : 'Active transit aspect on career axis.',
        dashaSanctionLevel: `${adPlanet} Antardasha direct activation.`
      },
      artha: {
        meaningEn: 'In Vedic state analysis, the convergence of the 10th house with transition houses (6th/8th) signifies the culmination of an established duty cycle and the necessary restructuring required to step into a higher vocational mandate.',
        meaningHi: 'वैदिक ज्योतिष में दशम भाव का संक्रांति भावों (षष्ठ/अष्टम) के साथ संरेखण पूर्व कर्म चक्र की पूर्णता एवं नवीन दायित्व के आरंभ का संकेत देता है।',
        classicalPrincipleEn: 'Karma Parivartana: Resolution of past institutional agreements paving path for fresh mandate.',
        classicalPrincipleHi: 'कर्म परिवर्तन: पूर्व अनुबंधों का समापन एवं नई जिम्मेदारियों का उदय।'
      },
      phala: {
        manifestationEn: 'Astrological conditions are consistent with practical career movements—such as formal resignation, transition between employers, role scope expansion, or reorienting vocational strategy. Pacing exits with dignity yields optimal long-term fruit.',
        manifestationHi: 'यह समय संस्थागत पद-त्याग, नए पद ग्रहण अथवा कार्यक्षेत्र में रणनीतिक बदलाव के लिए उपयुक्त है। गरिमापूर्ण विदाई एवं स्पष्ट संवाद से आगामी मार्ग प्रशस्त होगा।',
        actionableGuidanceEn: [
          'Review all contractual and separation terms with meticulous care',
          'Maintain transparent, respectful communication with outgoing leadership',
          'Document institutional handover systematically to ensure clean closure'
        ],
        actionableGuidanceHi: [
          'सभी अनुबंधों व विलेखों की सावधानीपूर्वक जांच करें',
          'वरिष्ठों के साथ सम्मानजनक एवं स्पष्ट संवाद बनाए रखें',
          'कार्यों का व्यवस्थित हस्तांतरण सुनिश्चित करें'
        ]
      },
      evidenceChain: [
        {
          hierarchyTier: 'Antardasha',
          factor: `${adPlanet} Antardasha`,
          descriptionEn: `Direct activation of professional axis under ${mdPlanet} Mahadasha.`,
          descriptionHi: `${mdPlanet} महादशा में ${adPlanet} अंतर्दशा का प्रत्यक्ष प्रभाव।`,
          supportWeight: 85
        },
        {
          hierarchyTier: 'Gochar Trigger',
          factor: careerGocharTrigger ? `${careerGocharTrigger.planet} Transit` : `10th House Aspect`,
          descriptionEn: careerGocharTrigger ? careerGocharTrigger.impactSummaryEn : 'Transit focus on 10th Bhava cusp.',
          descriptionHi: 'दशम भाव पर गोचर दृष्टि।',
          supportWeight: 80
        }
      ],
      supportingFactors: [
        `10th Bhava Lord (${h10Lord}) active in Dasha hierarchy.`,
        `Transition house resonance (6th/8th axis indicating contractual separation/shift).`,
        `Dasha Sandhi phase indicating natural chapter conclusion.`
      ],
      limitingFactors: [
        'Requires deliberate administrative patience during transitional buffer.'
      ],
      technicalContext: {
        savPoints: getSavByBhava(kundali.ashtakvarga, 10, kundali.houses)
      }
    };
    careerSigRef = careerSig;
    livedExperiences.push(careerSig);
  }

  // ==========================================================================
  // SIGNATURE 3: GṚHA / RESIDENCE — RELOCATION & RESIDENTIAL TRANSITION (AJITESH CASE)
  // ==========================================================================
  const h4Lord = houseLordsMap[4] || 'Moon';
  const h3Lord = houseLordsMap[3] || 'Mercury';
  const h12Lord = houseLordsMap[12] || 'Jupiter';

  const isResidenceDashaActive = [mdPlanet, adPlanet, pdPlanet].includes(h4Lord) || [mdPlanet, adPlanet, pdPlanet].includes('Mars') || [mdPlanet, adPlanet, pdPlanet].includes(h12Lord) || [mdPlanet, adPlanet, pdPlanet].includes(h3Lord);
  const residenceGocharTrigger = gocharTriggers.find(gt => gt.natalTarget.targetHouse === 4 || gt.natalTarget.targetHouse === 12 || gt.natalTarget.targetHouse === 3);

  let isResidenceTransitionActive = false;
  let residenceSigRef: VartamanaAnubhavaSignature | undefined;

  if ((isResidenceDashaActive && (residenceGocharTrigger || [h3Lord, h12Lord].includes(adPlanet) || [h3Lord, h12Lord].includes(pdPlanet)))) {
    isResidenceTransitionActive = true;
    const isoStart = dashaResolution.ad.startDate;
    const isoEnd = dashaResolution.ad.endDate;
    const temporal = calculateTemporalStatusAndWindow(isoStart, isoEnd, targetObj, 'Active');

    const residenceSig: VartamanaAnubhavaSignature = {
      signatureId: 'griha_relocation_transition',
      category: 'GRIHA_RESIDENCE',
      categoryLabelEn: 'Gṛha / Residence & Space',
      categoryLabelHi: 'गृह / भूमि व आवास',
      titleEn: 'Residence Transition, Relocation & Domestic Reorganization',
      titleHi: 'आवास परिवर्तन, स्थानांतरण व घरेलू पुनर्व्यवस्था',
      shortDescriptionEn: 'Active astrological alignment consistent with moving residence, spatial relocation preparation, and establishing a refreshed domestic sanctuary.',
      shortDescriptionHi: 'स्थान परिवर्तन, गृह परिवर्तन की तैयारी एवं नए आवासीय परिवेश की स्थापना का काल।',
      temporalTier: temporal.temporalTier,
      priority: 'Secondary',
      temporalStatus: temporal.temporalStatus,
      statusLabelEn: temporal.statusLabelEn,
      statusLabelHi: temporal.statusLabelHi,
      startDate: temporal.startDateFormatted,
      endDate: temporal.endDateFormatted,
      isoStartDate: isoStart,
      isoEndDate: isoEnd,
      durationDays: temporal.durationDays,
      durationLabelEn: temporal.durationLabelEn,
      durationLabelHi: temporal.durationLabelHi,
      relativeTimingEn: temporal.relativeTimingEn,
      relativeTimingHi: temporal.relativeTimingHi,
      currentStateEligible: temporal.currentStateEligible,
      isImmediateToday: false,
      evidenceStrength: 'Strong',
      sthiti: {
        astrologicalConditionEn: `4th Bhava (Sukha/Griha Sthana ruled by ${h4Lord}) and 3rd/12th Deshantara axis engaged under ${adPlanet} AD & ${pdPlanet} PD.`,
        astrologicalConditionHi: `चतुर्थ भाव (गृहेश ${h4Lord}) एवं तृतीय/द्वादश देशांतर अक्ष ${adPlanet} अंतर्दशा एवं ${pdPlanet} प्रत्यंतर्दशा के अंतर्गत सक्रिय हैं।`,
        triggerPlanets: [h4Lord, 'Mars', adPlanet],
        activatedHouses: [4, 3, 12],
        transitTriggerDetails: residenceGocharTrigger ? residenceGocharTrigger.impactSummaryEn : 'Spatial movement transit activation.',
        dashaSanctionLevel: `${adPlanet} / ${pdPlanet} spatial movement timing.`
      },
      artha: {
        meaningEn: 'The 4th house (physical home sanctuary) in dynamic contact with 3rd (movement of base) and 12th (new geographical boundaries) signifies the physical migration of one’s emotional and material anchor.',
        meaningHi: 'चतुर्थ भाव (गृह सुख) का तृतीय (स्थान परिवर्तन) एवं द्वादश (नवीन परिवेश) से संबंध भौतिक आवास में बदलाव का शास्त्रीय आधार है।',
        classicalPrincipleEn: 'Sthana Parivartana: Dynamic relocation of living quarters to harmonize with evolving karmic duties.',
        classicalPrincipleHi: 'स्थान परिवर्तन: कर्मिक आवश्यकताओं के अनुरूप नए आवास की स्थापना।'
      },
      phala: {
        manifestationEn: 'Astrological conditions support residential packing, logistics of shifting base, signing lease or purchase agreements, and settling into a renewed domestic environment.',
        manifestationHi: 'यह समय आवास परिवर्तन की योजना, सामान की व्यवस्था, विलेखों की पूर्णता एवं नए गृह में व्यवस्थित होने हेतु अनुकूल है।',
        actionableGuidanceEn: [
          'Verify all residential leases, title deeds, and utility transfers meticulously',
          'Allow adequate buffer time for packing and physical moving logistics',
          'Perform a peaceful Griha Shanti or cleansing prayer in the new space'
        ],
        actionableGuidanceHi: [
          'आवास अनुबंधों एवं विलेखों की पूर्ण जांच करें',
          'स्थानांतरण में समय का उचित संतुलन रखें',
          'नवीन गृह में शांति एवं शुद्धि हेतु प्रार्थना करें'
        ]
      },
      evidenceChain: [
        {
          hierarchyTier: 'Antardasha',
          factor: `${adPlanet} Antardasha • H${kundali.planets?.find(p => p.name === adPlanet)?.house || 4}`,
          descriptionEn: `Dasha period supports spatial reordering and domestic transition.`,
          descriptionHi: `दशा कालखंड आवासीय पुनर्व्यवस्था का समर्थन करता है।`,
          supportWeight: 80
        }
      ],
      supportingFactors: [
        `4th House Lord (${h4Lord}) & Deshantara axis active.`,
        `Movement indicators aligned across Dasha and transit.`
      ],
      limitingFactors: [
        'Requires logistical patience during initial packing and unpacking phase.'
      ],
      technicalContext: {
        savPoints: getSavByBhava(kundali.ashtakvarga, 4, kundali.houses)
      }
    };
    residenceSigRef = residenceSig;
    livedExperiences.push(residenceSig);
  }

  // ==========================================================================
  // SIGNATURE 4: CONVERGENCE SIGNATURE — WORK + RESIDENCE CONVERGENCE (AJITESH CASE)
  // ==========================================================================
  if (isCareerTransitionActive && isResidenceTransitionActive && careerSigRef && residenceSigRef) {
    const careerStartMs = new Date(careerSigRef.isoStartDate).getTime();
    const careerEndMs = new Date(careerSigRef.isoEndDate).getTime();
    const resStartMs = new Date(residenceSigRef.isoStartDate).getTime();
    const resEndMs = new Date(residenceSigRef.isoEndDate).getTime();

    const convStartMs = Math.max(careerStartMs, resStartMs);
    const convEndMs = Math.min(careerEndMs, resEndMs);

    if (convEndMs > convStartMs) {
      const isoStart = new Date(convStartMs).toISOString();
      const isoEnd = new Date(convEndMs).toISOString();
      const temporal = calculateTemporalStatusAndWindow(isoStart, isoEnd, targetObj, 'Active');

      convergenceSignatures.push({
        signatureId: 'convergence_work_residence_transition',
        category: 'CONVERGENCE',
        categoryLabelEn: 'Mahā-Convergence / Structural Life Pivot',
        categoryLabelHi: 'महा-संश्लेषण / जीवन का दोहरा मोड़',
        titleEn: 'Dual Convergence: Major Structural Transition Across Work & Residence',
        titleHi: 'दोहरा मोड़: कार्यक्षेत्र एवं आवास का एक साथ पुनर्गठन',
        shortDescriptionEn: 'Simultaneous activation of Karma Sthana (10th) and Griha Sthana (4th), indicating coordinated restructuring of both professional station and physical base.',
        shortDescriptionHi: 'दशम भाव (कार्यक्षेत्र) एवं चतुर्थ भाव (आवास) का एक साथ सक्रिय होना, जो कार्य एवं घर दोनों के युगपत पुनर्गठन का संकेत देता है।',
        temporalTier: 'Medium-Term (1-6 Months)',
        priority: 'Primary',
        temporalStatus: temporal.temporalStatus,
        statusLabelEn: temporal.temporalStatus === 'UPCOMING' ? 'UPCOMING CONVERGENCE' : 'ACTIVE CONVERGENCE',
        statusLabelHi: temporal.temporalStatus === 'UPCOMING' ? 'आगामी दोहरा मोड़' : 'सक्रिय दोहरा मोड़',
        startDate: temporal.startDateFormatted,
        endDate: temporal.endDateFormatted,
        isoStartDate: isoStart,
        isoEndDate: isoEnd,
        durationDays: temporal.durationDays,
        durationLabelEn: temporal.durationLabelEn,
        durationLabelHi: temporal.durationLabelHi,
        relativeTimingEn: temporal.relativeTimingEn,
        relativeTimingHi: temporal.relativeTimingHi,
        currentStateEligible: temporal.currentStateEligible,
        isImmediateToday: false,
        evidenceStrength: 'Strongly Corroborated',
        sthiti: {
          astrologicalConditionEn: `Simultaneous activation of 10th Bhava (Karma Sthana) and 4th/12th Bhavas (Griha & Deshantara) under ${dashaResolution.activePathSummary}.`,
          astrologicalConditionHi: `दशम भाव (कर्म) एवं चतुर्थ/द्वादश भाव (गृह व देशांतर) का ${dashaResolution.activePathSummary} दशा क्रम के अंतर्गत एक साथ सक्रिय होना।`,
          triggerPlanets: [h10Lord, h4Lord, adPlanet, pdPlanet],
          activatedHouses: [10, 4, 6, 8, 12],
          transitTriggerDetails: 'Coordinated transit triggers across 4th-10th axis.',
          dashaSanctionLevel: `${adPlanet} AD dual house rulership/aspect convergence.`
        },
        artha: {
          meaningEn: 'In Jyotish architecture, the 4th-10th axis represents the fundamental foundation-to-apex pillar (Nadir to Zenith). When both ends activate simultaneously, it marks a comprehensive life-pivot where external social role and internal physical habitat transform together.',
          meaningHi: 'चतुर्थ-दशम अक्ष जीवन की आधारशिला एवं शिखर का प्रतीक है। जब दोनों भाव एक साथ सक्रिय होते हैं, तो यह बाहरी कर्म एवं आंतरिक गृह दोनों के समन्वित कायाकल्प का स्पष्ट संकेत है।',
          classicalPrincipleEn: 'Axis Convergence (Kendra Samyasa): Coordinated realignment of domestic shelter and executive duty.',
          classicalPrincipleHi: 'केंद्र साम्य: गृह एवं कर्म का एक साथ संतुलन।'
        },
        phala: {
          manifestationEn: 'Professional role realignment and residential or domestic transition themes become simultaneously prominent under this dual activation. Focus on coordinated planning rather than treating them as unrelated pressures.',
          manifestationHi: 'इस दोहरे सक्रियण के अंतर्गत कार्यक्षेत्र में दायित्व परिवर्तन एवं गृह-निवास से जुड़े बदलाव एक साथ महत्वपूर्ण हो जाते हैं। दोनों क्षेत्रों में चरणबद्ध संतुलन बनाकर आगे बढ़ें।',
          actionableGuidanceEn: [
            'Maintain a master checklist integrating both career milestones and moving logistics',
            'Pace energy deliberately—avoid over-committing to new social projects until both base and job settle',
            'Cultivate grounded equanimity; structural pivots naturally require a 4–8 week settling period'
          ],
          actionableGuidanceHi: [
            'कार्य एवं गृह दोनों के कार्यों की एक संयुक्त समय-सारणी बनाएं',
            'ऊर्जा का संतुलन रखें—जब तक दोनों क्षेत्र स्थिर न हों, नए दायित्वों से बचें',
            'धैर्य बनाए रखें; दोहरे बदलाव को पूर्ण रूप से स्थापित होने में ४-८ सप्ताह लगते हैं'
          ]
        },
        evidenceChain: [
          {
            hierarchyTier: 'Natal Promise',
            factor: '4th & 10th Kendra Axis',
            descriptionEn: 'Foundational root-to-apex structural axis in natal chart.',
            descriptionHi: 'मूल कुंडली में चतुर्थ-दशम केंद्र अक्ष का आधार।',
            supportWeight: 95
          },
          {
            hierarchyTier: 'Antardasha',
            factor: `${adPlanet} AD • Dual Activation`,
            descriptionEn: `${adPlanet} Antardasha bridges career transition and residential migration.`,
            descriptionHi: `${adPlanet} अंतर्दशा दोनों क्षेत्रों को एक साथ सक्रिय कर रही है।`,
            supportWeight: 90
          }
        ],
        supportingFactors: [
          'Simultaneous 10th house career transition signature.',
          'Simultaneous 4th house relocation transition signature.',
          'Synchronized Dasha Sanction bridging both domains.'
        ],
        limitingFactors: [
          'Demands high mental discipline to manage dual-track administrative logistics.'
        ],
        technicalContext: {
          savPoints: 28,
          d9Confirmation: true
        }
      });
    }
  }

  // ==========================================================================
  // SIGNATURE 5: SAMBANDHA — INTERPERSONAL ALIGNMENT & CONTRACTUAL FOCUS
  // ==========================================================================
  const h7Lord = houseLordsMap[7] || 'Venus';
  const isRelationshipDashaActive = [adPlanet, pdPlanet].includes(h7Lord) || [adPlanet, pdPlanet].includes('Venus');
  const relationshipGochar = gocharTriggers.find(gt => gt.natalTarget.targetHouse === 7 || gt.planet === h7Lord);

  if (isRelationshipDashaActive && relationshipGochar) {
    const isoStart = dashaResolution.ad.startDate;
    const isoEnd = dashaResolution.ad.endDate;
    const temporal = calculateTemporalStatusAndWindow(isoStart, isoEnd, targetObj, 'Active');

    livedExperiences.push({
      signatureId: 'sambandha_relational_focus',
      category: 'SAMBANDHA',
      categoryLabelEn: 'Sambandha / Partnership & Union',
      categoryLabelHi: 'संबंध / दांपत्य व साझेदारी',
      titleEn: 'Heightened Relationship Focus & Alliance Clarification',
      titleHi: 'पारस्परिक संबंधों में गहनता व अनुबंध स्पष्टता',
      shortDescriptionEn: 'Active astrological alignment consistent with clarifying partnership agreements, deepening mutual accord, and focused interpersonal dialogue.',
      shortDescriptionHi: 'साझेदारी एवं दांपत्य में स्पष्ट संवाद, विश्वास सुदृढ़ीकरण एवं पारस्परिक समझौतों का कालखंड।',
      temporalTier: temporal.temporalTier,
      priority: 'Secondary',
      temporalStatus: temporal.temporalStatus,
      statusLabelEn: temporal.statusLabelEn,
      statusLabelHi: temporal.statusLabelHi,
      startDate: temporal.startDateFormatted,
      endDate: temporal.endDateFormatted,
      isoStartDate: isoStart,
      isoEndDate: isoEnd,
      durationDays: temporal.durationDays,
      durationLabelEn: temporal.durationLabelEn,
      durationLabelHi: temporal.durationLabelHi,
      relativeTimingEn: temporal.relativeTimingEn,
      relativeTimingHi: temporal.relativeTimingHi,
      currentStateEligible: temporal.currentStateEligible,
      isImmediateToday: false,
      evidenceStrength: 'Moderate',
      sthiti: {
        astrologicalConditionEn: `7th Bhava (Kalatra/Jaya Sthana ruled by ${h7Lord}) active under ${adPlanet} AD & ${relationshipGochar.planet} transit contact.`,
        astrologicalConditionHi: `सप्तम भाव (भावेश ${h7Lord}) ${adPlanet} अंतर्दशा एवं ${relationshipGochar.planet} गोचर संपर्क से सक्रिय है।`,
        triggerPlanets: [h7Lord, 'Venus', adPlanet],
        activatedHouses: [7, 2, 11],
        transitTriggerDetails: relationshipGochar.impactSummaryEn,
        dashaSanctionLevel: `${adPlanet} AD relational activation.`
      },
      artha: {
        meaningEn: 'The 7th house governs one-on-one partnerships and the mirror of the self. This period calls for mutual transparency, active listening, and clarifying joint responsibilities.',
        meaningHi: 'सप्तम भाव सार्वजनिक एवं व्यक्तिगत साझेदारी का स्थान है। यह समय परस्पर विश्वास, स्पष्ट अपेक्षाओं एवं सहयोग की मांग करता है।',
        classicalPrincipleEn: 'Sambandha Shuddhi: Harmonizing interpersonal dynamics through empathetic reciprocity.',
        classicalPrincipleHi: 'संबंध शुद्धि: संवाद एवं संतुलन द्वारा संबंधों में मधुरता।'
      },
      phala: {
        manifestationEn: 'Auspicious timing window for resolving lingering relational questions, negotiating clear mutual agreements, and renewing emotional warmth with key partners.',
        manifestationHi: 'यह समय मतभेदों को सुलझाने, स्पष्ट समझौते करने एवं संबंधों में नई आत्मीयता स्थापित करने हेतु अनुकूल है।',
        actionableGuidanceEn: [
          'Practice empathetic active listening during delicate discussions',
          'Put important business or collaborative agreements in writing',
          'Honor emotional boundaries while extending warmth'
        ],
        actionableGuidanceHi: [
          'संवाद में धैर्यपूर्वक सुनने का अभ्यास करें',
          'महत्वपूर्ण समझौतों को लिखित रूप दें',
          'पारस्परिक मर्यादा का सम्मान करें'
        ]
      },
      evidenceChain: [
        {
          hierarchyTier: 'Gochar Trigger',
          factor: `${relationshipGochar.planet} in 7th Axis`,
          descriptionEn: relationshipGochar.impactSummaryEn,
          descriptionHi: relationshipGochar.impactSummaryHi,
          supportWeight: 75
        }
      ],
      supportingFactors: [
        `7th House Lord (${h7Lord}) engaged in Dasha/Transit.`
      ],
      limitingFactors: [
        'Avoid making assumptions without explicit verbal confirmation.'
      ]
    });
  }

  // Sort lived experiences by priority & immediacy
  const tierRank: Record<AnubhavaTemporalTier, number> = {
    'Immediate (1-3 Days)': 4,
    'Short-Term (1-4 Weeks)': 3,
    'Medium-Term (1-6 Months)': 2,
    'Macro (1+ Years)': 1
  };

  livedExperiences.sort((a, b) => {
    if (a.isImmediateToday !== b.isImmediateToday) return a.isImmediateToday ? -1 : 1;
    return tierRank[b.temporalTier] - tierRank[a.temporalTier];
  });

  const hasLivedExperiences = livedExperiences.length > 0 || convergenceSignatures.length > 0;

  return {
    livedExperiences,
    convergenceSignatures,
    hasLivedExperiences
  };
}

// ============================================================================
// CONVERGE LIFE THEMES (VARTAMĀNA JEEVAN THEMES — NATAL + DASHA + GOCHAR)
// ============================================================================
export function evaluateVartamanaLifeThemes(
  kundali: CompleteKundali,
  dashaResolution: Dasha5LevelResolution,
  gocharTriggers: GocharTriggerInfo[],
  targetDate: Date | string = new Date(),
  horizon: VartamanaHorizon = '30D'
): {
  activeThemes: VartamanaActiveTheme[];
  emergingThemes: VartamanaActiveTheme[];
  peakingThemes: VartamanaActiveTheme[];
  releasingThemes: VartamanaActiveTheme[];
  dormantThemes: VartamanaActiveTheme[];
} {
  const activeThemes: VartamanaActiveTheme[] = [];
  const emergingThemes: VartamanaActiveTheme[] = [];
  const peakingThemes: VartamanaActiveTheme[] = [];
  const releasingThemes: VartamanaActiveTheme[] = [];
  const dormantThemes: VartamanaActiveTheme[] = [];

  const mdPlanet = dashaResolution.md.planet;
  const adPlanet = dashaResolution.ad.planet;
  const pdPlanet = dashaResolution.pd.planet;
  const sdPlanet = dashaResolution.sd.planet;
  const prdPlanet = dashaResolution.prd.planet;

  // Prastara & SAV
  const prastaraTables = calculatePrastaraTables(kundali);
  const shadbalaData = kundali.shadbala || calculateShadbala(kundali.planets, kundali.houses, {
    birthDate: getBirthUtcDate(kundali.birthDetails),
    latitude: kundali.birthDetails.latitude,
    longitude: kundali.birthDetails.longitude
  });

  // House Lords mapping
  const houseLordsMap: Record<number, PlanetName> = {};
  kundali.houses?.forEach(h => {
    houseLordsMap[h.houseNumber] = (h.signLord || ZODIAC_LORDS[h.sign] || 'Mars') as PlanetName;
  });

  const ascHouse = kundali.houses?.find(h => h.houseNumber === 1);
  const ascSign = (ascHouse?.sign || 'Aries') as ZodiacSign;
  const lagnaLord = (ascHouse?.signLord || ZODIAC_LORDS[ascSign] || 'Mars') as PlanetName;

  for (const themeKey of Object.keys(LIFE_THEME_CATALOG) as LifeAreaThemeId[]) {
    const meta = LIFE_THEME_CATALOG[themeKey];
    const primaryHouses = meta.primaryHouses;
    const primaryLord1 = houseLordsMap[primaryHouses[0]] || 'Mars';
    const primaryLord2 = primaryHouses[1] ? houseLordsMap[primaryHouses[1]] : undefined;

    // 1. Natal Promise (D1 Foundation)
    const primaryHouseObj = kundali.houses?.find(h => h.houseNumber === primaryHouses[0]);
    const houseSign = (primaryHouseObj?.sign || 'Aries') as ZodiacSign;
    const savPoints = getSavByBhava(kundali.ashtakvarga, primaryHouses[0], kundali.houses);
    const isSAVStrong = savPoints >= 28;

    const lordPlanetObj = kundali.planets?.find(p => p.name === primaryLord1);
    const lordShadbala = shadbalaData?.planets?.find(s => s.planet === primaryLord1);
    const shadbalaScoreRupa = lordShadbala?.totalRupas || (lordShadbala?.ratio ? Number((lordShadbala.ratio * 6.0).toFixed(2)) : 6.5);
    const shadbalaRatio = lordShadbala?.ratio ?? 1.0;
    const isShadbalaAdequate = shadbalaRatio >= 1.0;

    const lordHouseNumber = lordPlanetObj?.house || primaryHouses[0];
    const lordSign = (lordPlanetObj?.sign || houseSign) as ZodiacSign;
    const lordDegreeStr = lordPlanetObj?.degreeFormatted || '15° 00\'';
    const lordNakshatra = lordPlanetObj?.nakshatra || 'Ashwini';
    const lordPada = lordPlanetObj?.pada || 1;
    const lordDignity = lordPlanetObj?.dignity || 'Friend';

    // Navamsha D9
    const d9Sign = lordPlanetObj ? calculateD9Navamsha(lordPlanetObj.longitude).sign : 'Aries';
    const d9Confirmation = d9Sign ? !['Scorpio', 'Capricorn'].includes(d9Sign) : true;
    const d9Dignity = d9Confirmation ? 'Supportive / Dignified' : 'Sensitive Navamsha disposition';

    // 2. Dasha Sanction Evaluation (Hierarchical: MD -> AD -> PD -> SD -> PRD)
    let dashaScore = 0;
    let mdSupport = false;
    let adSupport = false;
    let pdSupport = false;
    let sdPranaSupport = false;

    if (mdPlanet === primaryLord1 || mdPlanet === primaryLord2 || mdPlanet === meta.primarySignificator) {
      dashaScore += 45;
      mdSupport = true;
    } else {
      const mdHouse = kundali.planets?.find(p => p.name === mdPlanet)?.house;
      if (mdHouse && primaryHouses.includes(mdHouse)) {
        dashaScore += 35;
        mdSupport = true;
      }
    }

    if (adPlanet === primaryLord1 || adPlanet === primaryLord2 || adPlanet === meta.primarySignificator) {
      dashaScore += 30;
      adSupport = true;
    } else {
      const adHouse = kundali.planets?.find(p => p.name === adPlanet)?.house;
      if (adHouse && primaryHouses.includes(adHouse)) {
        dashaScore += 25;
        adSupport = true;
      }
    }

    if (pdPlanet === primaryLord1 || pdPlanet === primaryLord2 || pdPlanet === meta.primarySignificator) {
      dashaScore += 15;
      pdSupport = true;
    }

    if (sdPlanet === primaryLord1 || prdPlanet === primaryLord1) {
      dashaScore += 10;
      sdPranaSupport = true;
    }

    // 3. Gochar Trigger Evaluation
    const relevantGochar = gocharTriggers.filter(gt => {
      const targetHouse = gt.natalTarget.targetHouse;
      return primaryHouses.includes(targetHouse) || gt.planet === primaryLord1;
    });

    const hasExactGochar = relevantGochar.some(g => g.isExact);
    const hasApproachingGochar = relevantGochar.some(g => g.isApproaching);
    const hasActiveGochar = relevantGochar.length > 0;
    const topGochar = relevantGochar[0];

    // 4. Evidence Convergence & Lifecycle State
    let lifecycleState: ActivationLifecycleState = 'Dormant';
    let evidenceStrength: EvidenceStrength = 'Limited';

    const supportingFactors: string[] = [];
    const limitingFactors: string[] = [];
    const unresolvedFactors: string[] = [];
    const evidenceChain: EvidenceChainStep[] = [];

    // Step 1: Natal Promise
    evidenceChain.push({
      hierarchyTier: 'Natal Promise',
      factor: `Bhava ${primaryHouses.join(', ')} & Bhavesh ${primaryLord1}`,
      descriptionEn: `Foundational natal root in H${primaryHouses.join(', ')} (${houseSign}) ruled by ${primaryLord1} in H${lordHouseNumber} (Shadbala: ${shadbalaRatio.toFixed(2)}x, SAV: ${savPoints} bindus).`,
      descriptionHi: `मूल जन्मकुंडली में भाव ${primaryHouses.join(', ')} (${houseSign}) एवं भावेश ${primaryLord1} (भाव ${lordHouseNumber}, षड्बल: ${shadbalaRatio.toFixed(2)}x, सर्वाष्टक: ${savPoints} बिंदु)।`,
      supportWeight: isSAVStrong && isShadbalaAdequate ? 85 : 55
    });

    if (isSAVStrong) supportingFactors.push(`Strong Ashtakavarga cushion (${savPoints} bindus in Bhava ${primaryHouses[0]}).`);
    else limitingFactors.push(`Sensitive Ashtakavarga baseline (${savPoints} bindus). Demands disciplined preparation.`);

    if (isShadbalaAdequate) supportingFactors.push(`Bhavesh ${primaryLord1} operates with solid Shadbala (${shadbalaRatio.toFixed(2)}x ratio).`);
    else limitingFactors.push(`Bhavesh ${primaryLord1} operates at moderate Shadbala ratio (${shadbalaRatio.toFixed(2)}x).`);

    // Step 2: Mahadasha
    if (mdSupport) {
      evidenceChain.push({
        hierarchyTier: 'Mahadasha',
        factor: `${mdPlanet} Mahadasha`,
        descriptionEn: `Mahadasha of ${mdPlanet} provides overarching temporal sanction for ${meta.nameEn}.`,
        descriptionHi: `${mdPlanet} महादशा इस जीवन क्षेत्र के कर्मिक कालखंड को व्यापक अनुमति प्रदान कर रही है।`,
        supportWeight: 90
      });
      supportingFactors.push(`${mdPlanet} Mahadasha macro sanction.`);
    } else {
      limitingFactors.push(`${mdPlanet} Mahadasha is not direct lord of this house; unfolds via sub-period alignment.`);
    }

    // Step 3: Antardasha
    if (adSupport) {
      evidenceChain.push({
        hierarchyTier: 'Antardasha',
        factor: `${adPlanet} Antardasha`,
        descriptionEn: `Antardasha of ${adPlanet} activates this theme directly, establishing operational priority.`,
        descriptionHi: `${adPlanet} अंतर्दशा इस क्षेत्र को वर्तमान में व्यावहारिक रूप से सक्रिय कर रही है।`,
        supportWeight: 80
      });
      supportingFactors.push(`${adPlanet} Antardasha direct operational focus.`);
    }

    // Step 4: Fine Timing
    if (pdSupport || sdPranaSupport) {
      evidenceChain.push({
        hierarchyTier: 'Sūkshma/Prāṇa',
        factor: `${pdPlanet} PD • ${sdPlanet} SD`,
        descriptionEn: `Pratyantardasha & Sūkshma Dasha fine-tune immediate trigger windows with high precision.`,
        descriptionHi: `प्रत्यंतर्दशा एवं सूक्ष्म दशा तात्कालिक संरेखण प्रदान कर रही हैं।`,
        supportWeight: 70
      });
      supportingFactors.push(`Sūkshma timing resonance (${pdPlanet}/${sdPlanet}).`);
    }

    // Step 5: Gochar
    if (hasActiveGochar && topGochar) {
      evidenceChain.push({
        hierarchyTier: 'Gochar Trigger',
        factor: `${topGochar.planet} Transit`,
        descriptionEn: `${topGochar.impactSummaryEn} (${topGochar.phase} phase, orb: ${topGochar.orbDegrees}°).`,
        descriptionHi: `${topGochar.impactSummaryHi}`,
        supportWeight: topGochar.isExact ? 95 : 75
      });
      supportingFactors.push(`Gochar trigger: ${topGochar.planet} in H${topGochar.natalTarget.targetHouse} (${topGochar.phase}).`);
    }

    // Step 6: Corroboration
    if (d9Confirmation) {
      evidenceChain.push({
        hierarchyTier: 'Corroboration',
        factor: `D9 Navamsha (${d9Sign}) & Jaimini`,
        descriptionEn: `Navamsha position validates planetary integrity. Corroborated with ${meta.jaiminiSignificator}.`,
        descriptionHi: `नवमांश स्थिति (${d9Sign}) गरिमा की पुष्टि करती है। जैमिनी ${meta.jaiminiSignificator} से समर्थित।`,
        supportWeight: 75
      });
    }

    // Lifecycle determination
    const isPeakNow = (mdSupport || adSupport) && (hasExactGochar || (pdSupport && hasActiveGochar));
    const isIntensifying = (mdSupport || adSupport) && hasApproachingGochar;
    const isReleasing = dashaResolution.ad.daysRemaining !== undefined && dashaResolution.ad.daysRemaining < 45 && dashaResolution.ad.isSandhi;
    const isEmerging = !mdSupport && adSupport && dashaResolution.ad.progressPercent < 25;

    if (isPeakNow) {
      lifecycleState = 'Peak';
      evidenceStrength = supportingFactors.length >= 4 ? 'Strongly Corroborated' : 'Strong';
    } else if (isIntensifying) {
      lifecycleState = 'Intensifying';
      evidenceStrength = 'Strong';
    } else if (isEmerging) {
      lifecycleState = 'Emerging';
      evidenceStrength = 'Moderate';
    } else if (isReleasing) {
      lifecycleState = 'Releasing';
      evidenceStrength = 'Moderate';
    } else if (dashaScore >= 45 || hasActiveGochar) {
      lifecycleState = 'Active';
      evidenceStrength = supportingFactors.length >= 3 ? 'Strong' : 'Moderate';
    } else if (dashaScore >= 20 || hasApproachingGochar) {
      lifecycleState = 'Emerging';
      evidenceStrength = 'Limited';
    } else {
      lifecycleState = 'Dormant';
      evidenceStrength = 'Limited';
    }

    if (supportingFactors.length >= 2 && limitingFactors.length >= 2) {
      evidenceStrength = 'Mixed / Conflicted';
      unresolvedFactors.push(`Dasha sanction and transit constraints show contrasting vectors. Deliberate effort required rather than effortless fruition.`);
    }

    // Sthiti description
    const sthitiDescEn = `Mechanics: House ${primaryHouses.join(', ')} (${houseSign}) active under ${dashaResolution.activePathSummary}. Primary Bhavesh ${primaryLord1} (placed in H${lordHouseNumber}) is engaged with ${relevantGochar.length > 0 ? `${relevantGochar[0].planet} Gochar contact` : 'internal Dasha unfolding'}.`;
    const sthitiDescHi = `ग्रहीय स्थिति: भाव ${primaryHouses.join(', ')} (${houseSign}) वर्तमान में ${dashaResolution.activePathSummary} दशा क्रम के अंतर्गत सक्रिय है। भावेश ${primaryLord1} (भाव ${lordHouseNumber} में स्थित) ${relevantGochar.length > 0 ? `${relevantGochar[0].planet} गोचर संपर्क` : 'आंतरिक दशा फल'} से संबद्ध है।`;

    // Phala description
    const phalaDescEn = `Potential Manifestation: Astrologically, this alignment corresponds with heightened focus on ${meta.nameEn.toLowerCase()}. It indicates a structured time window for ${meta.growthKeywordsEn[0].toLowerCase()} and ${meta.growthKeywordsEn[1]?.toLowerCase() || 'progress'}. Astrological indicators inform; they do not dictate arbitrary outcomes.`;
    const phalaDescHi = `फलित संभावना: शास्त्रीय दृष्टि से यह कालखंड ${meta.nameHi} के प्रति सजग प्रयास का संकेत देता है। यह ${meta.growthKeywordsHi[0]} एवं ${meta.growthKeywordsHi[1] || 'सकारात्मक प्रगति'} के अनुकूल अवसर प्रदान कर सकता है।`;

    // Acharya Theme Deep Dive Data
    const bavPoints = prastaraTables?.[primaryLord1]?.signTotals?.[primaryHouseObj?.signIndex ?? 0] ?? 4;
    const yutiPlanets = kundali.planets?.filter(p => p.house === lordHouseNumber && p.name !== primaryLord1 && p.name !== 'Ascendant').map(p => p.name) || [];
    const drishtiPlanets = kundali.planets?.filter(p => [1, 5, 9, 7].includes(((primaryHouses[0] - p.house + 12) % 12) + 1) && p.name !== 'Ascendant').map(p => p.name) || [];

    const acharyaDeepDive: AcharyaThemeDeepDive = {
      d1Mechanics: {
        lagna: ascSign,
        lagnaLord,
        bhava: primaryHouses[0],
        bhavaSign: houseSign,
        bhavesh: primaryLord1,
        bhaveshHouse: lordHouseNumber,
        bhaveshSign: lordSign,
        bhaveshDegree: lordDegreeStr,
        bhaveshNakshatra: lordNakshatra,
        bhaveshPada: lordPada,
        dignity: lordDignity,
        drishtiOnBhava: drishtiPlanets.length > 0 ? drishtiPlanets : ['Neutral aspect field'],
        yutiWithBhavesh: yutiPlanets.length > 0 ? yutiPlanets : ['Solitary placement in Bhava'],
        relevantYoga: (() => {
          const matchingYoga = (kundali as any).yogas?.find((y: any) => 
            y.participatingHouses?.includes(primaryHouses[0]) || 
            (primaryLord1 && y.participatingPlanets?.includes(primaryLord1))
          );
          if (matchingYoga) {
            return `${matchingYoga.name} (${matchingYoga.fruitionStatus.replace(/_/g, ' ')})`;
          }
          return isSAVStrong && isShadbalaAdequate ? 'Strong supporting SAV & Shadbala factors (No classical yoga)' : 'Standard Bhava Disposition';
        })()
      },
      dashaSanction: {
        mdPlanet: dashaResolution.md.planet,
        mdPeriod: `${formatDate(dashaResolution.md.startDate)} → ${formatDate(dashaResolution.md.endDate)}`,
        adPlanet: dashaResolution.ad.planet,
        adPeriod: `${formatDate(dashaResolution.ad.startDate)} → ${formatDate(dashaResolution.ad.endDate)}`,
        pdPlanet: dashaResolution.pd.planet,
        pdPeriod: `${formatDate(dashaResolution.pd.startDate)} → ${formatDate(dashaResolution.pd.endDate)}`,
        sdPlanet: dashaResolution.sd.planet,
        sdPeriod: `${formatDate(dashaResolution.sd.startDate)} → ${formatDate(dashaResolution.sd.endDate)}`,
        prdPlanet: dashaResolution.prd.planet,
        prdPeriod: `${formatDate(dashaResolution.prd.startDate)} → ${formatDate(dashaResolution.prd.endDate)}`,
        dashaSandhiStatus: dashaResolution.isDashaSandhiActive ? 'Sandhi active (energy reconfiguration)' : 'Stable Dasha trajectory'
      },
      gocharPrecision: {
        transitingPlanet: topGochar ? topGochar.planet : 'Jupiter',
        transitingSign: topGochar ? topGochar.transitingSign : 'Aries',
        transitingBhava: topGochar ? topGochar.natalTarget.targetHouse : primaryHouses[0],
        transitingDegree: topGochar ? topGochar.degreeFormatted : '15° 00\'',
        isRetrograde: topGochar ? topGochar.isRetrograde : false,
        aspectOrYuti: topGochar ? topGochar.aspectType : 'Harmonious ambient field',
        orbExactness: topGochar ? `${topGochar.orbDegrees}° (${topGochar.isExact ? 'Exact Cusp' : 'Ambient'})` : 'No exact direct affliction',
        phase: topGochar ? topGochar.phase : 'Approaching'
      },
      corroborationDetails: {
        d9Sign,
        d9Dignity,
        shadbalaScoreRupa: Number(shadbalaScoreRupa.toFixed(2)),
        shadbalaRatio: Number(shadbalaRatio.toFixed(2)),
        ashtakavargaSAV: savPoints,
        ashtakavargaBAV: bavPoints,
        jaiminiKarakaRole: meta.jaiminiSignificator
      },
      reasoningChain: {
        step1NatalPromise: `Natal H${primaryHouses[0]} (${houseSign}) ruled by ${primaryLord1} in H${lordHouseNumber} establishes baseline capacity with ${savPoints} SAV bindus & ${shadbalaRatio.toFixed(2)}x Shadbala.`,
        step2DashaSanction: mdSupport ? `${mdPlanet} MD provides macro sanction; ${adPlanet} AD directs operational focus.` : `${adPlanet} AD generates temporal momentum without full macro sanction.`,
        step3GocharTrigger: topGochar ? `${topGochar.planet} in H${topGochar.natalTarget.targetHouse} (${topGochar.phase}) provides dynamic trigger catalyst.` : `Transit field provides ambient support without acute disruptions.`,
        step4Synthesis: `Convergence of D1 promise, Dasha sanction, and Gochar triggers yields "${lifecycleState}" state with "${evidenceStrength}" evidence.`
      },
      sixPartEvidenceTaxonomy: {
        calculatedFact: `H${primaryHouses[0]} (${houseSign}) Lord ${primaryLord1} in H${lordHouseNumber} (${lordSign} ${lordDegreeStr}), SAV: ${savPoints} bindus, Shadbala: ${shadbalaRatio.toFixed(2)}x. Dasha: ${dashaResolution.md.planet}–${dashaResolution.ad.planet}–${dashaResolution.pd.planet}.`,
        classicalRule: `Parashara BPHS Ch. 12/24: Bhava lord in Kendra/Trikona or Upachaya with >28 SAV points grants fruition when activated by Dasha lord.`,
        interpretation: `Current ${lifecycleState.toLowerCase()} activation indicates favorable opportunities for ${meta.growthKeywordsEn[0] || 'growth'} through disciplined initiative.`,
        inference: `Native is likely experiencing focused thematic attention around ${meta.nameEn} during this active Antardasha window.`,
        speculation: 'Speculation: None identified (grounded in deterministic Parashari and live ephemeris indicators).',
        uncertainty: 'Uncertainty: Birth time sensitivity may alter sub-divisional (D9/D60) fine placements; D1 planetary signs and Mahadasha remain robust.'
      }
    };

    const expressionPolarity: ExpressionPolarity = isSAVStrong && isShadbalaAdequate ? 'Constructive' : dashaResolution.isDashaSandhiActive ? 'Transitional' : limitingFactors.length > supportingFactors.length ? 'Demanding' : 'Mixed';
    const manifestationSpecificity: ManifestationSpecificity = hasExactGochar && (mdSupport || adSupport) ? 'High Specificity' : (mdSupport || adSupport) ? 'Moderate Specificity' : 'Broad Archetypal';

    const themeTemporal = calculateTemporalStatusAndWindow(
      dashaResolution.ad.startDate,
      dashaResolution.ad.endDate,
      targetDate,
      lifecycleState,
      isPeakNow ? formatDate(targetDate) : undefined
    );

    const themeItem: VartamanaActiveTheme = {
      themeId: themeKey,
      nameEn: meta.nameEn,
      nameHi: meta.nameHi,
      sanskritName: meta.sanskritName,
      relevantHouses: primaryHouses,
      primarySignificator: meta.primarySignificator,
      lifecycleState,
      evidenceStrength,
      expressionPolarity,
      manifestationSpecificity,
      whatEvidenceDoesNotEstablishEn: 'Does not establish an unavoidable material outcome; indicates an active Jyotish potential requiring conscious effort and discernment.',
      whatEvidenceDoesNotEstablishHi: 'यह किसी अनिवार्य या निश्चित घटना का दावा नहीं करता; यह केवल सजग प्रयास व विवेक की मांग करने वाले सक्रिय ज्योतिषीय प्रभाव को दर्शाता है।',
      sthiti: {
        astrologicalConditionEn: sthitiDescEn,
        astrologicalConditionHi: sthitiDescHi,
        primaryBhavaRole: `House ${primaryHouses[0]} (${houseSign})`,
        bhaveshCondition: `${primaryLord1} in H${lordHouseNumber} (Shadbala: ${shadbalaRatio.toFixed(2)}x, SAV: ${savPoints})`,
        dashaSanctionEn: mdSupport ? `${mdPlanet} MD provides macro sanction; ${adPlanet} AD directs operational focus.` : `${adPlanet} AD creates temporal activation.`,
        dashaSanctionHi: mdSupport ? `${mdPlanet} महादशा मुख्य अनुमति दे रही है; ${adPlanet} अंतर्दशा इसे केंद्रित कर रही है।` : `${adPlanet} अंतर्दशा तात्कालिक सक्रियता बना रही है।`,
        gocharInfluenceEn: relevantGochar.length > 0 ? relevantGochar.map(g => g.impactSummaryEn).join(' ') : 'No adverse transits directly afflicting house cusp.',
        gocharInfluenceHi: relevantGochar.length > 0 ? relevantGochar.map(g => g.impactSummaryHi).join(' ') : 'वर्तमान में कोई प्रतिकूल गोचर दृष्टि इस भाव पर नहीं है।'
      },
      artha: {
        meaningEn: meta.meaningEn,
        meaningHi: meta.meaningHi,
        karmicDomainNoteEn: meta.karmicDomainNoteEn,
        karmicDomainNoteHi: meta.karmicDomainNoteHi,
        activePrinciplesEn: meta.activePrinciplesEn,
        activePrinciplesHi: meta.activePrinciplesHi
      },
      phala: {
        potentialManifestationEn: phalaDescEn,
        potentialManifestationHi: phalaDescHi,
        growthAvenuesEn: meta.growthKeywordsEn,
        growthAvenuesHi: meta.growthKeywordsHi,
        cautionaryGuidanceEn: meta.cautionKeywordsEn,
        cautionaryGuidanceHi: meta.cautionKeywordsHi,
        isMedicalCaution: themeKey === 'health_vitality'
      },
      evidenceChain,
      supportingFactors,
      limitingContradictoryFactors: limitingFactors,
      unresolvedFactors,
      temporalStatus: themeTemporal.temporalStatus,
      statusLabelEn: themeTemporal.statusLabelEn,
      statusLabelHi: themeTemporal.statusLabelHi,
      durationDays: themeTemporal.durationDays,
      durationLabelEn: themeTemporal.durationLabelEn,
      durationLabelHi: themeTemporal.durationLabelHi,
      relativeTimingEn: themeTemporal.relativeTimingEn,
      relativeTimingHi: themeTemporal.relativeTimingHi,
      currentStateEligible: themeTemporal.currentStateEligible,
      startDate: dashaResolution.ad.startDate,
      peakDate: isPeakNow ? formatDate(targetDate) : undefined,
      endDate: dashaResolution.ad.endDate,
      isPeakPeriodNow: isPeakNow,
      corroboration: {
        d9NavamshaConfirmation: d9Confirmation,
        d9NoteEn: d9Confirmation ? `Lord ${primaryLord1} confirmed in Navamsha (${d9Sign}).` : undefined,
        ashtakavargaSAV: savPoints,
        ashtakavargaBAV: bavPoints,
        isSAVStrong,
        shadbalaRatio: Number(shadbalaRatio.toFixed(2)),
        isShadbalaAdequate
      },
      acharyaDeepDive
    };

    if (lifecycleState === 'Peak') {
      peakingThemes.push(themeItem);
      activeThemes.push(themeItem);
    } else if (lifecycleState === 'Active' || lifecycleState === 'Intensifying') {
      activeThemes.push(themeItem);
    } else if (lifecycleState === 'Emerging') {
      emergingThemes.push(themeItem);
    } else if (lifecycleState === 'Releasing' || (lifecycleState as string) === 'Fading') {
      releasingThemes.push(themeItem);
    } else {
      dormantThemes.push(themeItem);
    }
  }

  activeThemes.sort((a, b) => {
    const rank: Record<EvidenceStrength, number> = {
      'Strongly Corroborated': 5,
      'Strong': 4,
      'Mixed / Conflicted': 3,
      'Moderate': 2,
      'Limited': 1
    };
    return rank[b.evidenceStrength] - rank[a.evidenceStrength];
  });

  return {
    activeThemes,
    emergingThemes,
    peakingThemes,
    releasingThemes,
    dormantThemes
  };
}

// ============================================================================
// CALENDAR-AWARE DATE ARITHMETIC HELPERS
// ============================================================================
/**
 * Adds calendar years to a base date with full leap-year awareness.
 * If baseDate is Feb 29 and the target year is not a leap year, gracefully resolves to Feb 28.
 */
export function addCalendarYears(baseDate: Date, years: number): Date {
  const result = new Date(baseDate.getTime());
  const targetYear = result.getFullYear() + years;
  const currentMonth = result.getMonth();
  const currentDate = result.getDate();

  result.setFullYear(targetYear);
  if (result.getMonth() !== currentMonth) {
    result.setDate(0); // Last day of previous month (Feb 28 in non-leap year)
  } else {
    result.setDate(currentDate);
  }
  return result;
}

/**
 * Adds calendar months to a base date with month-end rollover protection.
 */
export function addCalendarMonths(baseDate: Date, months: number): Date {
  const result = new Date(baseDate.getTime());
  const targetMonth = result.getMonth() + months;
  const currentDate = result.getDate();

  result.setMonth(targetMonth);
  if (result.getDate() < currentDate) {
    result.setDate(0); // Last day of previous month
  }
  return result;
}

/**
 * Adds calendar days to a base date.
 */
export function addCalendarDays(baseDate: Date, days: number): Date {
  return new Date(baseDate.getTime() + days * 86400000);
}

// ============================================================================
// PERIOD-DRIVEN & CALENDAR TEMPORAL BOUNDARIES RESOLVER
// ============================================================================
export function resolveHorizonSpanDays(
  kundali: CompleteKundali,
  horizon: VartamanaHorizon,
  refDate: Date = new Date()
): { spanDays: number; intervalLabelEn: string; intervalLabelHi: string } {
  const refMs = refDate.getTime();
  const mdList = kundali.dashas || [];
  const currentMd = mdList.find(d => {
    const s = new Date(d.startDate).getTime();
    const e = new Date(d.endDate).getTime();
    return refMs >= s && refMs <= e;
  }) || mdList[0];

  const currentAd = currentMd?.antardashas?.find(a => {
    const s = new Date(a.startDate).getTime();
    const e = new Date(a.endDate).getTime();
    return refMs >= s && refMs <= e;
  }) || currentMd?.antardashas?.[0];

  let spanDays = 30;
  let labelEn = 'Active Analysis Horizon';
  let labelHi = 'सक्रिय विश्लेषण कालखंड';

  // 1. User-Selectable Calendar Product Horizons
  if (horizon === '7D') {
    spanDays = 7;
    labelEn = '7-Day Analysis Horizon';
    labelHi = '७-दिवसीय विश्लेषण कालखंड';
  } else if (horizon === '30D') {
    spanDays = 30;
    labelEn = '30-Day Analysis Horizon';
    labelHi = '३०-दिवसीय विश्लेषण कालखंड';
  } else if (horizon === '90D') {
    spanDays = 90;
    labelEn = '90-Day (Quarterly) Analysis Horizon';
    labelHi = '९०-दिवसीय (त्रैमासिक) विश्लेषण कालखंड';
  } else if (horizon === '1Y') {
    const targetDate = addCalendarYears(refDate, 1);
    spanDays = Math.max(1, Math.round((targetDate.getTime() - refMs) / 86400000));
    labelEn = '1-Year Annual Analysis Horizon';
    labelHi = '१-वर्षीय वार्षिक विश्लेषण कालखंड';
  } else if (horizon === '3Y') {
    const targetDate = addCalendarYears(refDate, 3);
    spanDays = Math.max(1, Math.round((targetDate.getTime() - refMs) / 86400000));
    labelEn = '3-Year Medium-Term Analysis Horizon';
    labelHi = '३-वर्षीय मध्यम-कालिक विश्लेषण कालखंड';
  } else if (horizon === '10Y') {
    const targetDate = addCalendarYears(refDate, 10);
    spanDays = Math.max(1, Math.round((targetDate.getTime() - refMs) / 86400000));
    labelEn = '10-Year Decadal Macro Analysis Horizon';
    labelHi = '१०-वर्षीय दशकीय दीर्घ विश्लेषण कालखंड';
  }
  // 2. Backward-Compatible Legacy Aliases
  else if (horizon === '6M') {
    const targetDate = addCalendarMonths(refDate, 6);
    spanDays = Math.max(1, Math.round((targetDate.getTime() - refMs) / 86400000));
    labelEn = '6-Month Analysis Horizon';
    labelHi = '६-माह विश्लेषण कालखंड';
  } else if (horizon === '5Y') {
    const targetDate = addCalendarYears(refDate, 5);
    spanDays = Math.max(1, Math.round((targetDate.getTime() - refMs) / 86400000));
    labelEn = '5-Year Analysis Horizon';
    labelHi = '५-वर्षीय विश्लेषण कालखंड';
  }
  // 3. Astrological Event Boundaries (Dasha / Gochar Cycles)
  else if (horizon === 'current_pd') {
    spanDays = 30;
    labelEn = `Active Pratyantar Window (${currentMd?.planet || 'MD'}–${currentAd?.planet || 'AD'})`;
    labelHi = `सक्रिय प्रत्यंतर काल (${currentMd?.planet || 'महा'}–${currentAd?.planet || 'अंतर'})`;
  } else if (horizon === 'current_ad') {
    if (currentAd?.endDate) {
      const endMs = new Date(currentAd.endDate).getTime();
      const diffDays = Math.round((endMs - refMs) / 86400000);
      spanDays = Math.max(14, diffDays > 0 ? diffDays : 90);
    } else {
      spanDays = 90;
    }
    labelEn = `Active Antardasha (${currentMd?.planet || 'MD'}–${currentAd?.planet || 'AD'}) Epoch`;
    labelHi = `सक्रिय अंतर्दशा (${currentMd?.planet || 'महा'}–${currentAd?.planet || 'अंतर'}) काल`;
  } else if (horizon === 'ad_transition') {
    if (currentAd?.endDate) {
      const endMs = new Date(currentAd.endDate).getTime();
      const diffDays = Math.round((endMs - refMs) / 86400000) + 180;
      spanDays = Math.max(90, diffDays);
    } else {
      spanDays = 270;
    }
    labelEn = `Upcoming Antardasha Transition Gate (${currentMd?.planet || 'MD'} Cycle)`;
    labelHi = `आगामी अंतर्दशा संक्रान्ति व संधि (${currentMd?.planet || 'महादशा'})`;
  } else if (horizon === 'double_transit') {
    // F-L8-07 remediation: Dynamically derive double transit window from live planetary ephemeris
    const convention = kundali.nodeConvention || kundali.birthDetails?.nodeConvention || 'mean';
    const liveEph = calculateLivePlanetaryEphemeris(refDate, convention);
    const jup = liveEph.find(p => p.planet === 'Jupiter');
    const sat = liveEph.find(p => p.planet === 'Saturn');

    const jupDeg = typeof jup?.degreeInSign === 'number' && !isNaN(jup.degreeInSign) ? jup.degreeInSign : 15;
    const jupDegRemaining = Math.max(0.5, 30 - (jupDeg % 30));
    const jupSpeed = 0.083;
    const jupDaysRemaining = Math.round(jupDegRemaining / jupSpeed);

    const satDeg = typeof sat?.degreeInSign === 'number' && !isNaN(sat.degreeInSign) ? sat.degreeInSign : 15;
    const satDegRemaining = Math.max(0.5, 30 - (satDeg % 30));
    const satSpeed = 0.033;
    const satDaysRemaining = Math.round(satDegRemaining / satSpeed);

    spanDays = Math.max(30, Math.min(jupDaysRemaining, satDaysRemaining));
    labelEn = `Jupiter–Saturn Joint Gochar Epoch (${spanDays} Days in Current Sign Concurrence)`;
    labelHi = `गुरु-शनि संयुक्त गोचर सक्रियता चक्र (वर्तमान राशि संमिलन में ${spanDays} दिन शेष)`;
  } else if (horizon === 'mahadasha_arc') {
    if (currentMd?.endDate) {
      const endMs = new Date(currentMd.endDate).getTime();
      const diffDays = Math.round((endMs - refMs) / 86400000);
      spanDays = Math.max(365, diffDays > 0 ? diffDays : 1095);
    } else {
      spanDays = 1095;
    }
    labelEn = `Full ${currentMd?.planet || 'Active'} Mahadasha Lifecycle Arc`;
    labelHi = `संपूर्ण ${currentMd?.planet || 'सक्रिय'} महादशा महाचक्र`;
  }

  return { spanDays, intervalLabelEn: labelEn, intervalLabelHi: labelHi };
}

// ============================================================================
// CUMULATIVE HORIZON TEMPORAL ENGINE (DAY 0 → DAY X ANALYSIS)
// ============================================================================
export function buildHorizonAnalysis(
  kundali: CompleteKundali,
  focusDasha: Dasha5LevelResolution,
  allThemes: {
    activeThemes: VartamanaActiveTheme[];
    emergingThemes: VartamanaActiveTheme[];
    peakingThemes: VartamanaActiveTheme[];
    releasingThemes: VartamanaActiveTheme[];
    dormantThemes: VartamanaActiveTheme[];
  },
  referenceDate: Date | string,
  horizon: VartamanaHorizon
): HorizonAnalysisResult {
  const refDateObj = new Date(referenceDate);
  const refMs = refDateObj.getTime();

  const { spanDays, intervalLabelEn: dynamicLabelEn, intervalLabelHi: dynamicLabelHi } = resolveHorizonSpanDays(kundali, horizon, refDateObj);
  const horizonEndMs = refMs + spanDays * 86400000;
  const horizonEndDateObj = new Date(horizonEndMs);

  const startDateStr = refDateObj.toISOString();
  const endDateStr = horizonEndDateObj.toISOString();
  const intervalLabelEn = `${dynamicLabelEn}: ${formatDate(refDateObj)} → ${formatDate(horizonEndDateObj)}`;
  const intervalLabelHi = `${dynamicLabelHi}: ${formatDate(refDateObj)} → ${formatDate(horizonEndDateObj)}`;

  const transitionsList: HorizonTransitionEvent[] = [];
  const whatIsChangingEn: string[] = [];
  const whatIsChangingHi: string[] = [];
  const whatComesNextEn: string[] = [];
  const whatComesNextHi: string[] = [];

  // 1. Dasha Transitions within Horizon Interval [refMs, horizonEndMs]
  const mdList = kundali.dashas || [];
  for (const md of mdList) {
    const mdStart = new Date(md.startDate).getTime();

    if (mdStart > refMs && mdStart <= horizonEndMs) {
      const daysFromToday = Math.round((mdStart - refMs) / 86400000);
      transitionsList.push({
        id: `trans_md_${md.planet}_${md.startDate}`,
        date: md.startDate,
        daysFromToday,
        category: 'DASHA_SHIFT',
        titleEn: `Major Mahadasha Ingress: ${md.planet}`,
        titleHi: `प्रमुख महादशा प्रवेश: ${md.planet}`,
        descriptionEn: `Major multi-year life chapter opens under ${md.planet} Mahadasha (${formatDate(md.startDate)}).`,
        descriptionHi: `${md.planet} महादशा का आरंभ। जीवन के एक नए युग का सूत्रपात।`,
        involvedPlanet: md.planet,
        significanceEn: `Shifts macro life priorities and planetary rulership across the entire natal blueprint.`,
        significanceHi: `संपूर्ण कुंडली में प्राथमिक कर्मिक दिशा का महा-परिवर्तन।`
      });
      whatIsChangingEn.push(`In ${daysFromToday} days (${formatDate(md.startDate)}): Major Mahadasha shifts to ${md.planet}, opening an overarching new life chapter.`);
      whatIsChangingHi.push(`${daysFromToday} दिनों में (${formatDate(md.startDate)}): प्रमुख ${md.planet} महादशा का आरंभ, जो एक नए युग का सूत्रपात करेगी।`);
    }

    if (md.antardashas) {
      for (const ad of md.antardashas) {
        const adStart = new Date(ad.startDate).getTime();
        const adEnd = new Date(ad.endDate).getTime();

        if (adStart > refMs && adStart <= horizonEndMs) {
          const daysFromToday = Math.round((adStart - refMs) / 86400000);
          transitionsList.push({
            id: `trans_ad_${ad.planet}_${ad.startDate}`,
            date: ad.startDate,
            daysFromToday,
            category: 'DASHA_SHIFT',
            titleEn: `Antardasha Transition: ${ad.planet}`,
            titleHi: `अंतर्दशा परिवर्तन: ${ad.planet}`,
            descriptionEn: `Transition into ${ad.planet} Antardasha under ${md.planet} Mahadasha (${formatDate(ad.startDate)}).`,
            descriptionHi: `${md.planet} महादशा में ${ad.planet} अंतर्दशा का आरंभ (${formatDate(ad.startDate)})।`,
            involvedPlanet: ad.planet,
            significanceEn: `Activates new house lords and alters the immediate operational focus.`,
            significanceHi: `नए भाव स्वामियों को सक्रिय करता है एवं व्यावहारिक प्राथमिकताओं को बदलता है।`
          });
          whatIsChangingEn.push(`In ${daysFromToday} days (${formatDate(ad.startDate)}): Antardasha transitions to ${ad.planet}, reorienting operational focus.`);
          whatIsChangingHi.push(`${daysFromToday} दिनों में (${formatDate(ad.startDate)}): ${ad.planet} अंतर्दशा आरंभ होकर व्यावहारिक प्राथमिकताओं को नया मोड़ देगी।`);
        }

        if (adEnd > refMs && adEnd <= horizonEndMs) {
          const daysFromToday = Math.round((adEnd - refMs) / 86400000);
          transitionsList.push({
            id: `trans_ad_sandhi_${ad.planet}_${ad.endDate}`,
            date: ad.endDate,
            daysFromToday,
            category: 'SANDHI_PHASE',
            titleEn: `${ad.planet} Sub-Period Conclusion & Sandhi`,
            titleHi: `${ad.planet} अंतर्दशा पूर्णता व संधि काल`,
            descriptionEn: `Culmination of ${ad.planet} sub-period energy before subsequent transition (${formatDate(ad.endDate)}).`,
            descriptionHi: `${ad.planet} अंतर्दशा की पूर्णता एवं आगामी दशा हेतु संधि प्रभाव।`,
            involvedPlanet: ad.planet,
            significanceEn: `Themes governed by ${ad.planet} enter release/stabilization phase.`,
            significanceHi: `${ad.planet} से जुड़े विषय पूर्णता एवं संचित फल प्राप्ति की ओर अग्रसर होते हैं।`
          });
        }

        if (spanDays <= 180) {
          const pdList = calculatePratyantardashas(ad.planet, new Date(ad.startDate), new Date(ad.endDate));
          for (const pd of pdList) {
            const pdStart = new Date(pd.startDate).getTime();
            if (pdStart > refMs && pdStart <= horizonEndMs) {
              const daysFromToday = Math.round((pdStart - refMs) / 86400000);
              transitionsList.push({
                id: `trans_pd_${pd.planet}_${pd.startDate}`,
                date: pd.startDate,
                daysFromToday,
                category: 'DASHA_SHIFT',
                titleEn: `Pratyantardasha Shift: ${pd.planet}`,
                titleHi: `प्रत्यंतर्दशा परिवर्तन: ${pd.planet}`,
                descriptionEn: `Fine timing transition to ${pd.planet} Pratyantardasha (${formatDate(pd.startDate)}).`,
                descriptionHi: `${pd.planet} प्रत्यंतर्दशा का आरंभ (${formatDate(pd.startDate)})।`,
                involvedPlanet: pd.planet,
                significanceEn: `Fine-tunes the immediate trigger window and cognitive responsiveness.`,
                significanceHi: `तात्कालिक संरेखण एवं निर्णय क्षमता को गति प्रदान करता है।`
              });
              if (whatIsChangingEn.length < 5) {
                whatIsChangingEn.push(`In ${daysFromToday} days (${formatDate(pd.startDate)}): ${pd.planet} Pratyantardasha begins, modulating immediate micro-triggers.`);
                whatIsChangingHi.push(`${daysFromToday} दिनों में (${formatDate(pd.startDate)}): ${pd.planet} प्रत्यंतर्दशा आरंभ होकर तात्कालिक घटनाओं को प्रभावित करेगी।`);
              }
            }
          }
        }
      }
    }
  }

  // 2. Gochar Transitions across Horizon
  const convention = kundali.nodeConvention || kundali.birthDetails?.nodeConvention || 'mean';
  const currentEphemList = calculateLivePlanetaryEphemeris(refDateObj, convention);
  const endEphemList = calculateLivePlanetaryEphemeris(horizonEndDateObj, convention);
  const currentEphem: Record<string, (typeof currentEphemList)[0]> = {};
  const endEphem: Record<string, (typeof endEphemList)[0]> = {};
  currentEphemList.forEach(p => { currentEphem[p.planet] = p; });
  endEphemList.forEach(p => { endEphem[p.planet] = p; });

  for (const pName of ['Jupiter', 'Saturn', 'Rahu', 'Ketu', 'Mars', 'Sun', 'Mercury', 'Venus'] as PlanetName[]) {
    const startPos = currentEphem[pName];
    const endPos = endEphem[pName];
    if (!startPos || !endPos) continue;

    if (startPos.sign !== endPos.sign) {
      transitionsList.push({
        id: `trans_gochar_ingress_${pName}_${horizon}`,
        date: formatDate(new Date(refMs + Math.round(spanDays * 0.5) * 86400000)),
        daysFromToday: Math.round(spanDays * 0.5),
        category: 'GOCHAR_INGRESS',
        titleEn: `${pName} Ingress into ${endPos.sign}`,
        titleHi: `${pName} का ${endPos.sign} राशि में गोचर प्रवेश`,
        descriptionEn: `Transiting ${pName} shifts from ${startPos.sign} to ${endPos.sign} across this cumulative horizon.`,
        descriptionHi: `${pName} इस समयावधि में ${startPos.sign} से ${endPos.sign} में गोचर करेगा।`,
        involvedPlanet: pName,
        significanceEn: `Activates a new natal house and alters transit Drishti angles.`,
        significanceHi: `कुंडली के नए भाव को सक्रिय करेगा एवं दृष्टि कोणों में बदलाव लाएगा।`
      });
      whatIsChangingEn.push(`Across ${spanDays}D Horizon: ${pName} transits into ${endPos.sign}, activating fresh Bhava influences.`);
      whatIsChangingHi.push(`${spanDays} दिवसीय क्षितिज में: ${pName} ${endPos.sign} में प्रवेश कर नए भाव प्रभाव सक्रिय करेगा।`);
    }

    if (startPos.isRetrograde !== endPos.isRetrograde) {
      const isGoingRetro = endPos.isRetrograde;
      transitionsList.push({
        id: `trans_retro_${pName}_${horizon}`,
        date: formatDate(new Date(refMs + Math.round(spanDays * 0.4) * 86400000)),
        daysFromToday: Math.round(spanDays * 0.4),
        category: 'RETROGRADE_STATION',
        titleEn: `${pName} ${isGoingRetro ? 'Vakri (Retrograde) Station' : 'Mārgi (Direct) Station'}`,
        titleHi: `${pName} ${isGoingRetro ? 'वक्री अवस्था' : 'मार्गी अवस्था'}`,
        descriptionEn: `${pName} changes motion status from ${startPos.isRetrograde ? 'Retrograde' : 'Direct'} to ${isGoingRetro ? 'Retrograde' : 'Direct'}.`,
        descriptionHi: `${pName} की गति में परिवर्तन (${isGoingRetro ? 'मार्गी से वक्री' : 'वक्री से मार्गी'})।`,
        involvedPlanet: pName,
        significanceEn: isGoingRetro ? 'Deepens introspection, revision of past endeavors, and internal karmic processing.' : 'Direct momentum restored; delayed matters gain forward acceleration.',
        significanceHi: isGoingRetro ? 'आत्म-चिंतन एवं पूर्व कार्यों की समीक्षा का काल।' : 'कार्यों में गतिशीलता एवं विलंब की समाप्ति।'
      });
    }
  }

  transitionsList.sort((a, b) => a.daysFromToday - b.daysFromToday);

  // 3. What Comes Next Projections
  const currentAdEnd = focusDasha.ad.endDate;
  const currentAdDaysLeft = focusDasha.ad.daysRemaining || Math.max(1, Math.round((new Date(currentAdEnd).getTime() - refMs) / 86400000));
  const nextAdPlanet = focusDasha.ad.nextPlanet || 'subsequent lord';

  whatComesNextEn.push(`Current ${focusDasha.ad.planet} Antardasha runs until ${formatDate(currentAdEnd)} (~${currentAdDaysLeft} days remaining).`);
  whatComesNextEn.push(`Following period introduces ${nextAdPlanet} Antardasha, shifting focus towards its natal house rulership.`);
  if (focusDasha.md.daysRemaining && focusDasha.md.daysRemaining <= spanDays) {
    whatComesNextEn.push(`Within this horizon (${spanDays}D), ${focusDasha.md.planet} Mahadasha reaches conclusion, paving way for ${focusDasha.md.nextPlanet || 'next MD'}.`);
  }

  whatComesNextHi.push(`वर्तमान ${focusDasha.ad.planet} अंतर्दशा ${formatDate(currentAdEnd)} तक सक्रिय है (~${currentAdDaysLeft} दिन शेष)।`);
  whatComesNextHi.push(`इसके पश्चात ${nextAdPlanet} अंतर्दशा का प्रभाव आरंभ होगा, जिससे संबंधित भावों में नई ऊर्जा का संचार होगा।`);

  // 4. Horizon Themes Trajectory
  const emergingThemesInHorizon = allThemes.emergingThemes;
  const strengtheningThemesInHorizon = allThemes.activeThemes.filter(t => t.lifecycleState === 'Intensifying' || t.lifecycleState === 'Active');
  const peakingThemesInHorizon = allThemes.peakingThemes;
  const releasingThemesInHorizon = allThemes.releasingThemes;

  const hasMajorTransitions = transitionsList.length > 0 || whatIsChangingEn.length > 0;

  const horizonSummaryEn = `Cumulative ${spanDays}-Day Horizon (${intervalLabelEn}) encapsulates ${transitionsList.length} distinct Jyotish milestones across ${focusDasha.md.planet} Mahadasha. ${peakingThemesInHorizon.length > 0 ? `Peak focus centered on ${peakingThemesInHorizon.map(t => t.nameEn).join(', ')}.` : 'Active themes proceed with sustained operational momentum.'}`;
  const horizonSummaryHi = `${spanDays}-दिवसीय संचयी कालखंड (${intervalLabelHi}) ${focusDasha.md.planet} महादशा के अंतर्गत ${transitionsList.length} प्रमुख ज्योतिषीय पड़ावों को समाहित करता है। ${peakingThemesInHorizon.length > 0 ? `शिखर सक्रियता ${peakingThemesInHorizon.map(t => t.nameHi).join(', ')} पर केंद्रित है।` : 'सक्रिय क्षेत्र स्थिर गति से आगे बढ़ रहे हैं।'}`;

  let noTransitionNoticeEn: string | undefined;
  let noTransitionNoticeHi: string | undefined;

  if (!hasMajorTransitions) {
    noTransitionNoticeEn = 'No additional major Jyotish transition is detected within this extended horizon.';
    noTransitionNoticeHi = 'इस विस्तारित काल क्षितिज में कोई अतिरिक्त प्रमुख ज्योतिषीय परिवर्तन परिलक्षित नहीं होता।';
  }

  return {
    horizon,
    spanDays,
    startDate: startDateStr,
    endDate: endDateStr,
    intervalLabelEn,
    intervalLabelHi,
    whatIsChangingEn: whatIsChangingEn.length > 0 ? whatIsChangingEn : ['Planetary alignments maintain steady ongoing momentum across primary houses.'],
    whatIsChangingHi: whatIsChangingHi.length > 0 ? whatIsChangingHi : ['प्रमुख भावों में ग्रहीय स्थिति स्थिर एवं संतुलित गति से अग्रसर है।'],
    whatComesNextEn,
    whatComesNextHi,
    horizonSummaryEn,
    horizonSummaryHi,
    transitionsList,
    emergingThemesInHorizon,
    strengtheningThemesInHorizon,
    peakingThemesInHorizon,
    releasingThemesInHorizon,
    hasMajorTransitions,
    noTransitionNoticeEn,
    noTransitionNoticeHi
  };
}

// ============================================================================
// DYNAMIC TIMELINE MARKERS BUILDER (ATĪTA ← VARTAMĀNA → ĀGĀMI)
// ============================================================================
export function buildDynamicTimelineMarkers(
  kundali: CompleteKundali,
  dashaResolution: Dasha5LevelResolution,
  targetDate: Date | string,
  horizon: VartamanaHorizon,
  horizonAnalysis: HorizonAnalysisResult
): DynamicTimelineMarker[] {
  const targetMs = new Date(targetDate).getTime();
  const markers: DynamicTimelineMarker[] = [];

  const { spanDays: horizonDays } = resolveHorizonSpanDays(kundali, horizon, new Date(targetDate));
  const horizonEndMs = targetMs + horizonDays * 86400000;
  const pastLookbackMs = targetMs - Math.min(90, Math.round(horizonDays * 0.35)) * 86400000;

  // 1. Dasha Antardasha Transitions
  const md = kundali.dashas?.find(d => {
    const s = new Date(d.startDate).getTime();
    const e = new Date(d.endDate).getTime();
    return targetMs >= s && targetMs <= e;
  }) || kundali.dashas?.[0];

  if (md && md.antardashas) {
    for (const ad of md.antardashas) {
      const adStart = new Date(ad.startDate).getTime();
      const adEnd = new Date(ad.endDate).getTime();

      if (adStart >= pastLookbackMs && adStart <= horizonEndMs) {
        const isPast = adStart < targetMs - 86400000;
        const isCurrent = Math.abs(adStart - targetMs) <= 86400000;
        const daysDiff = Math.round((adStart - targetMs) / 86400000);

        markers.push({
          id: `marker_ad_start_${ad.planet}_${ad.startDate}`,
          date: ad.startDate,
          relativeTimeTagEn: isCurrent ? 'Presently Active' : daysDiff < 0 ? `${Math.abs(daysDiff)} Days Ago` : `In ${daysDiff} Days`,
          relativeTimeTagHi: isCurrent ? 'वर्तमान में सक्रिय' : daysDiff < 0 ? `${Math.abs(daysDiff)} दिन पूर्व` : `${daysDiff} दिन पश्चात`,
          markerType: 'Dasha_Parivartana',
          titleEn: `${ad.planet} Antardasha Ingress`,
          titleHi: `${ad.planet} अंतर्दशा आरंभ`,
          descriptionEn: `Transition into ${ad.planet} Antardasha under ${md.planet} Mahadasha. Shifts operational emphasis.`,
          descriptionHi: `${md.planet} महादशा में ${ad.planet} अंतर्दशा का आरंभ। कर्मिक प्राथमिकताओं का नया विन्यास।`,
          associatedPlanet: ad.planet,
          isPast,
          isCurrent,
          isFuture: !isPast && !isCurrent
        });
      }

      if (adEnd >= targetMs && adEnd <= horizonEndMs) {
        const daysToTransition = Math.round((adEnd - targetMs) / 86400000);
        markers.push({
          id: `marker_ad_end_${ad.planet}_${ad.endDate}`,
          date: ad.endDate,
          relativeTimeTagEn: `In ${daysToTransition} Days`,
          relativeTimeTagHi: `${daysToTransition} दिन पश्चात`,
          markerType: 'Dasha_Sandhi',
          titleEn: `${ad.planet} Sub-Period Conclusion & Sandhi`,
          titleHi: `${ad.planet} अंतर्दशा समापन व संधि काल`,
          descriptionEn: `Culmination of ${ad.planet} sub-period. Releasing themes before subsequent activation.`,
          descriptionHi: `${ad.planet} अंतर्दशा का पूर्णता काल। आगामी फलित से पूर्व संधि प्रभाव।`,
          associatedPlanet: ad.planet,
          isPast: false,
          isCurrent: false,
          isFuture: true
        });
      }
    }
  }

  // 2. Add Ingress / Retrograde markers from Horizon Analysis
  for (const ev of horizonAnalysis.transitionsList) {
    if (ev.category === 'GOCHAR_INGRESS' || ev.category === 'RETROGRADE_STATION') {
      markers.push({
        id: `marker_event_${ev.id}`,
        date: ev.date,
        relativeTimeTagEn: `In ${ev.daysFromToday} Days`,
        relativeTimeTagHi: `${ev.daysFromToday} दिन पश्चात`,
        markerType: 'Gochar_Pravesha',
        titleEn: ev.titleEn,
        titleHi: ev.titleHi,
        descriptionEn: ev.descriptionEn,
        descriptionHi: ev.descriptionHi,
        associatedPlanet: ev.involvedPlanet,
        isPast: false,
        isCurrent: false,
        isFuture: true
      });
    }
  }

  // 3. Add Current Sthiti Anchor Marker
  markers.push({
    id: `marker_focus_date_anchor`,
    date: new Date(targetDate).toISOString(),
    relativeTimeTagEn: 'Focus Date Anchor',
    relativeTimeTagHi: 'वर्तमान केंद्रित तिथि',
    markerType: 'Major_Trigger',
    titleEn: `Vartamāna Sthiti: ${dashaResolution.activePathSummary}`,
    titleHi: `वर्तमान स्थिति: ${dashaResolution.activePathSummary}`,
    descriptionEn: `Exact state calculated for ${formatDate(targetDate)}. Operating under ${dashaResolution.md.planet} MD & ${dashaResolution.ad.planet} AD.`,
    descriptionHi: `${formatDate(targetDate)} हेतु यथार्थ ग्रहीय गणना। ${dashaResolution.md.planet} महादशा एवं ${dashaResolution.ad.planet} अंतर्दशा सक्रिय।`,
    isPast: false,
    isCurrent: true,
    isFuture: false
  });

  markers.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  return markers;
}

// ============================================================================
// COMPARE WITH TODAY DIFF CALCULATOR
// ============================================================================
export function calculateCompareWithToday(
  todayResolution: Dasha5LevelResolution,
  focusResolution: Dasha5LevelResolution,
  todayActiveThemes: VartamanaActiveTheme[],
  focusActiveThemes: VartamanaActiveTheme[],
  todayTriggers: GocharTriggerInfo[],
  focusTriggers: GocharTriggerInfo[],
  focusDate: Date | string
): TodayComparisonDiff {
  const focusDateStr = formatDate(focusDate);
  const todayDateStr = formatDate(new Date());
  const isFocusDateToday = focusDateStr === todayDateStr;

  const shiftedLevels: string[] = [];
  if (todayResolution.md.planet !== focusResolution.md.planet) shiftedLevels.push('Mahadasha (MD)');
  if (todayResolution.ad.planet !== focusResolution.ad.planet) shiftedLevels.push('Antardasha (AD)');
  if (todayResolution.pd.planet !== focusResolution.pd.planet) shiftedLevels.push('Pratyantardasha (PD)');
  if (todayResolution.sd.planet !== focusResolution.sd.planet) shiftedLevels.push('Sūkshma (SD)');
  if (todayResolution.prd.planet !== focusResolution.prd.planet) shiftedLevels.push('Prāṇa (PRD)');

  const todayThemeIds = new Set(todayActiveThemes.map(t => t.themeId));
  const focusThemeIds = new Set(focusActiveThemes.map(t => t.themeId));

  const newlyEmergingThemes = focusActiveThemes
    .filter(t => !todayThemeIds.has(t.themeId))
    .map(t => `${t.nameEn} (${t.lifecycleState})`);

  const weakeningOrReleasingThemes = todayActiveThemes
    .filter(t => !focusThemeIds.has(t.themeId))
    .map(t => t.nameEn);

  const strengtheningThemes = focusActiveThemes
    .filter(t => todayThemeIds.has(t.themeId) && t.lifecycleState === 'Peak')
    .map(t => t.nameEn);

  const unchangedThemes = focusActiveThemes
    .filter(t => todayThemeIds.has(t.themeId) && t.lifecycleState !== 'Peak')
    .map(t => t.nameEn);

  const todayIngresses = new Set(todayTriggers.map(t => `${t.planet}_in_${t.natalTarget.targetHouse}`));
  const focusIngresses = focusTriggers.map(t => `${t.planet}_in_${t.natalTarget.targetHouse}`);
  const newIngresses = focusIngresses.filter(fi => !todayIngresses.has(fi));

  return {
    focusDateFormatted: focusDateStr,
    isFocusDateToday,
    dashaDiff: {
      todayDasha: todayResolution.activePathSummary,
      focusDasha: focusResolution.activePathSummary,
      hasLevelShift: shiftedLevels.length > 0,
      shiftedLevels
    },
    gocharDiff: {
      newIngresses: newIngresses.length > 0 ? newIngresses : ['No sign-level shift; degree movement progressing.'],
      retrogradeStatusChanges: [],
      exactTriggersDifference: focusTriggers.filter(t => t.isExact).map(t => t.impactSummaryEn)
    },
    themeDiff: {
      newlyEmergingThemes,
      strengtheningThemes,
      weakeningOrReleasingThemes,
      unchangedThemes
    }
  };
}

// ============================================================================
// FOCUS-DATE CURRENT LIFE STATE ENGINE (VARTAMĀNA STHITI)
// Calculates state anchored strictly to Focus Date, completely independent of Horizon
// ============================================================================
export function calculateVartamanaCurrentState(
  kundali: CompleteKundali,
  focusDate: Date | string = new Date(),
  language: Language = 'en'
): VartamanaCurrentFocusState {
  const focusObj = new Date(focusDate);
  const isHi = language === 'hi';

  // 1. Resolve 5-Level Dasha for Focus Date
  const focusDasha = resolve5LevelDashaForDate(kundali.dashas || [], focusObj);

  // 2. Evaluate Gochar for Focus Date
  const focusGochar = evaluateGocharTriggersForDate(kundali, focusObj);

  // 3. Converge Life Themes (Macro Vartamāna Jeevan Themes) at Focus Date
  const focusThemes = evaluateVartamanaLifeThemes(kundali, focusDasha, focusGochar, focusObj);

  // 4. Detect Event Signatures & Lived States (Vartamāna Anubhava) at Focus Date
  const anubhavaResult = detectEventSignaturesAndLivedStates(kundali, focusDasha, focusGochar, focusObj);

  // 5. Snapshot & Primary Theme Assembly
  const primaryTheme = focusThemes.activeThemes[0] || {
    themeId: 'karma_career',
    nameEn: 'Karma / Career & Vocation',
    nameHi: 'कर्म एवं आजीविका',
    lifecycleState: 'Active',
    evidenceStrength: 'Strong'
  };

  const secondaryTheme = focusThemes.activeThemes[1];
  const emergingTheme = focusThemes.emergingThemes[0];
  const releasingTheme = focusThemes.releasingThemes[0];

  const topGochar = focusGochar.find(g => g.isExact) || focusGochar[0];

  // 6. Current State Validity Window (derived from Dasha PD/SD & transit trigger orb)
  const validityStartDate = focusDasha.pd.startDate || focusDasha.ad.startDate;
  const validityEndDate = focusDasha.pd.endDate || focusDasha.ad.endDate;

  const validityWindow: CurrentStateValidityWindow = {
    startDate: formatDate(validityStartDate),
    endDate: formatDate(validityEndDate),
    durationLabelEn: `Active from ${formatDate(validityStartDate)} → ${formatDate(validityEndDate)}`,
    durationLabelHi: `${formatDate(validityStartDate)} से ${formatDate(validityEndDate)} तक सक्रिय`,
    determiningFactorEn: `Governed by ${focusDasha.md.planet}-${focusDasha.ad.planet}-${focusDasha.pd.planet} Pratyantardasha & current ${topGochar ? topGochar.planet : 'transit'} trigger orb`,
    determiningFactorHi: `${focusDasha.md.planet}-${focusDasha.ad.planet}-${focusDasha.pd.planet} प्रत्यंतर्दशा एवं गोचर संरेखण द्वारा निर्धारित`,
    currentPhase: primaryTheme.lifecycleState,
    immediateTriggerWindowEn: `${formatDate(focusDasha.sd.startDate)} → ${formatDate(focusDasha.sd.endDate)} (${focusDasha.sd.planet} SD & Gochar trigger)`,
    immediateTriggerWindowHi: `${formatDate(focusDasha.sd.startDate)} → ${formatDate(focusDasha.sd.endDate)} (${focusDasha.sd.planet} सूक्ष्म दशा)`
  };

  const snapshot: VartamanaSthitiSnapshot = {
    focusDateFormatted: formatDate(focusObj),
    selectedHorizon: '30D',
    horizonDaysSpan: 30,
    primaryActiveTheme: {
      themeId: primaryTheme.themeId,
      nameEn: primaryTheme.nameEn,
      nameHi: primaryTheme.nameHi,
      lifecycleState: primaryTheme.lifecycleState,
      evidenceStrength: primaryTheme.evidenceStrength
    },
    secondaryActiveTheme: secondaryTheme ? {
      themeId: secondaryTheme.themeId,
      nameEn: secondaryTheme.nameEn,
      nameHi: secondaryTheme.nameHi,
      lifecycleState: secondaryTheme.lifecycleState,
      evidenceStrength: secondaryTheme.evidenceStrength
    } : undefined,
    emergingTheme: emergingTheme ? {
      themeId: emergingTheme.themeId,
      nameEn: emergingTheme.nameEn,
      nameHi: emergingTheme.nameHi,
      projectedDate: emergingTheme.startDate
    } : undefined,
    releasingTheme: releasingTheme ? {
      themeId: releasingTheme.themeId,
      nameEn: releasingTheme.nameEn,
      nameHi: releasingTheme.nameHi,
      releaseDate: releasingTheme.endDate
    } : undefined,
    currentMD: focusDasha.md.planet,
    currentAD: focusDasha.ad.planet,
    currentPD: focusDasha.pd.planet,
    currentSD: focusDasha.sd.planet,
    currentPrana: focusDasha.prd.planet,
    majorGocharTrigger: topGochar ? topGochar.impactSummaryEn : 'Harmonious transit horizon across primary houses.',
    majorGocharTriggerHi: topGochar ? topGochar.impactSummaryHi : 'प्रमुख भावों में गोचर ऊर्जा संतुलित बनी हुई है।',
    dashaSandhiAlert: focusDasha.dashaSandhiNoteEn
  };

  // 6b. Authoritative Panchanga Grounding for Focus Date
  const coords: PanchangaCoordinates = {
    latitude: kundali.birthDetails?.latitude || 28.6139,
    longitude: kundali.birthDetails?.longitude || 77.2090,
    timezoneOffsetMinutes: kundali.birthDetails?.timezoneOffsetMinutes ?? 330
  };

  const focusPanchanga = calculateAuthoritativePanchanga(focusObj, coords);

  const activeObsEn: string[] = [];
  const activeObsHi: string[] = [];

  if (focusPanchanga.vratObservance.hasVrat && focusPanchanga.vratObservance.vratName) {
    activeObsEn.push(focusPanchanga.vratObservance.vratName);
    activeObsHi.push(focusPanchanga.vratObservance.vratNameHi);
  }

  if (focusPanchanga.sankranti.isSankrantiDay) {
    activeObsEn.push(`${focusPanchanga.sankranti.sankrantiNameEn} (${focusPanchanga.sankranti.formattedPunyaKaal || 'Punya Kaal'})`);
    activeObsHi.push(`${focusPanchanga.sankranti.sankrantiNameHi} (${focusPanchanga.sankranti.formattedPunyaKaal || 'पुण्य काल'})`);
  }

  if (focusPanchanga.pitruPaksha.isActive) {
    activeObsEn.push(`Pitru Paksha: ${focusPanchanga.pitruPaksha.shraddhaTithiNameEn || 'Shraddha'}`);
    activeObsHi.push(`पितृ पक्ष: ${focusPanchanga.pitruPaksha.shraddhaTithiNameHi || 'श्राद्ध'}`);
  }

  const dailyAlignEn = activeObsEn.length > 0
    ? `Panchanga aligns with ${focusPanchanga.tithi.name} in ${focusPanchanga.nakshatra.name} Nakshatra (${activeObsEn.join(' | ')}). Supports mindful focus and constructive action.`
    : `Panchanga aligns with ${focusPanchanga.tithi.name} (${focusPanchanga.paksha}) in ${focusPanchanga.nakshatra.name} Nakshatra. Supports steady daily routine and disciplined effort.`;

  const dailyAlignHi = activeObsHi.length > 0
    ? `पंचांग अनुसार आज ${focusPanchanga.tithi.name}, ${focusPanchanga.nakshatra.name} नक्षत्र (${activeObsHi.join(' | ')}) का योग है, जो एकाग्रता एवं शुभ संकल्पों हेतु अनुकूल है।`
    : `पंचांग अनुसार आज ${focusPanchanga.tithi.name} (${focusPanchanga.paksha === 'Shukla Paksha' ? 'शुक्ल पक्ष' : 'कृष्ण पक्ष'}), ${focusPanchanga.nakshatra.name} नक्षत्र का योग है, जो नित्य कर्म एवं अनुशासन हेतु श्रेष्ठ है।`;

  const panchangaSummary: VartamanaPanchangaSummary = {
    tithiName: focusPanchanga.tithi.name,
    paksha: focusPanchanga.paksha,
    nakshatraName: focusPanchanga.nakshatra.name,
    yogaName: focusPanchanga.yoga.name,
    karanaName: focusPanchanga.karana.name,
    varaName: focusPanchanga.vara.nameEn,
    samvatString: focusPanchanga.samvatString,
    activeObservance: activeObsEn.length > 0 ? activeObsEn.join(' | ') : undefined,
    activeObservanceHi: activeObsHi.length > 0 ? activeObsHi.join(' | ') : undefined,
    isSankranti: Boolean(focusPanchanga.sankranti.isSankrantiDay),
    sankrantiNameEn: focusPanchanga.sankranti.isSankrantiDay ? focusPanchanga.sankranti.sankrantiNameEn : undefined,
    sankrantiNameHi: focusPanchanga.sankranti.isSankrantiDay ? focusPanchanga.sankranti.sankrantiNameHi : undefined,
    punyaKaalFormatted: focusPanchanga.sankranti.isSankrantiDay ? focusPanchanga.sankranti.formattedPunyaKaal : undefined,
    isPitruPaksha: Boolean(focusPanchanga.pitruPaksha.isActive),
    shraddhaTithiEn: focusPanchanga.pitruPaksha.isActive ? focusPanchanga.pitruPaksha.shraddhaTithiNameEn : undefined,
    shraddhaTithiHi: focusPanchanga.pitruPaksha.isActive ? focusPanchanga.pitruPaksha.shraddhaTithiNameHi : undefined,
    dailyAlignmentEn: dailyAlignEn,
    dailyAlignmentHi: dailyAlignHi
  };

  snapshot.panchangaTithi = focusPanchanga.tithi.name;
  snapshot.panchangaNakshatra = focusPanchanga.nakshatra.name;
  snapshot.panchangaObservance = activeObsEn.length > 0 ? activeObsEn.join(' | ') : undefined;
  snapshot.panchangaObservanceHi = activeObsHi.length > 0 ? activeObsHi.join(' | ') : undefined;

  // 7. Birth Time Rectification (BTR) Integration
  const btrDetails = kundali.birthDetails;
  const isStale = btrDetails?.btrStatus === 'STALE';
  // Stale values remain persisted for audit, but must not be presented as active confirmation.
  const isRectified = Boolean(btrDetails?.isRectified && !isStale);
  const recordedTime = btrDetails?.originalTime || btrDetails?.time || '12:00:00';
  const workingTime = isStale
    ? (btrDetails?.time || recordedTime)
    : (btrDetails?.workingTime || btrDetails?.rectifiedTime || (isRectified ? btrDetails?.time : recordedTime));
  const confirmedTime = isStale ? null : (btrDetails?.confirmedTime || null);

  // 8. Acharya Gateway Verification
  const isDashaConsistent = Boolean(kundali.dashas && kundali.dashas.length > 0 && focusDasha.md && focusDasha.ad);
  const calculationProvenance = `VedangJyotisham V6.0 Common Core • Ephemeris: Lahiri Ayanamsha (${kundali.panchang?.ayanamshaFormatted || '24° 07\''}) • 5-Level Vimshottari • Event Signature Lived-State Engine`;

  return {
    profileId: kundali.id || 'default_jataka',
    nativeName: kundali.birthDetails?.name || 'Jataka',
    calculationTimestamp: new Date().toISOString(),
    timezone: btrDetails?.timezoneName || (btrDetails?.timezoneOffsetMinutes !== undefined ? `UTC${btrDetails.timezoneOffsetMinutes >= 0 ? '+' : ''}${Math.floor(btrDetails.timezoneOffsetMinutes / 60)}:${Math.abs(btrDetails.timezoneOffsetMinutes % 60).toString().padStart(2, '0')}` : 'Asia/Kolkata'),
    focusDate: focusObj.toISOString(),
    focusDateFormatted: formatDate(focusObj),
    validityWindow,
    snapshot,
    panchangaSummary,
    dasha5Level: focusDasha,
    gocharTriggers: focusGochar,
    activeThemes: focusThemes.activeThemes,
    emergingThemes: focusThemes.emergingThemes,
    peakingThemes: focusThemes.peakingThemes,
    releasingThemes: focusThemes.releasingThemes,
    dormantThemes: focusThemes.dormantThemes,
    currentLivedExperiences: anubhavaResult.livedExperiences,
    convergenceSignatures: anubhavaResult.convergenceSignatures,
    hasLivedExperiences: anubhavaResult.hasLivedExperiences,
    noSignatureNoticeEn: !anubhavaResult.hasLivedExperiences ? 'No strong additional current-life signature detected on this date.' : undefined,
    noSignatureNoticeHi: !anubhavaResult.hasLivedExperiences ? 'वर्तमान में कोई अतिरिक्त विशिष्ट जीवन-अनुभूति हस्ताक्षर परिलक्षित नहीं होता।' : undefined,
    concernIntelligence: evaluateConcernIntelligence(kundali, focusObj, 'INNER_WELLBEING', undefined, language),
    btrStatus: {
      status: btrDetails?.btrStatus || 'NOT_STARTED',
      recordedTime,
      workingTime,
      confirmedTime,
      isRectified,
      rectificationConfidence: isStale
        ? 'Stale BTR — current chart input only; prior values retained for audit'
        : (isRectified ? 'Verified BTR (Working Time Active)' : 'Recorded Birth Time Baseline'),
      timeDeltaSeconds: isStale ? 0 : (btrDetails?.rectifiedDeltaSeconds || 0),
      uncertaintyNotice: isStale
        ? 'Previously stored BTR evidence is stale after a birth-date/time edit. Historical values are retained for audit; no prior candidate or confirmation is active.'
        : (!isRectified && btrDetails?.btrStatus === 'RECOMMENDED' ? 'Birth time rectification recommended for sub-minute Prāṇa Dasha precision.' : undefined)
    },
    acharyaGatewayStatus: {
      isPassed: isDashaConsistent,
      isPaused: !isDashaConsistent,
      pauseReason: !isDashaConsistent ? 'Inconsistent Dasha timeline detected; recalculation required.' : undefined,
      calculationProvenance,
      safetyComplianceNotice: 'Verified non-fatalistic Jyotish compliance: Mechanical Sthiti distinguished strictly from Phala. No medical or fatalistic outcomes claimed.'
    },
    acharyaGatewayVerification: {
      isPassed: isDashaConsistent,
      isPaused: !isDashaConsistent,
      pauseReason: !isDashaConsistent ? 'Inconsistent Dasha timeline detected; recalculation required.' : undefined,
      calculationProvenance,
      safetyComplianceNotice: 'Verified non-fatalistic Jyotish compliance: Mechanical Sthiti distinguished strictly from Phala. No medical or fatalistic outcomes claimed.'
    }
  };
}

// ============================================================================
// TEMPORAL EVOLUTION / TIME HORIZON ENGINE
// Calculates future cumulative timeline without mutating focus-date current state
// ============================================================================
export function calculateHorizonTemporalEvolution(
  kundali: CompleteKundali,
  currentState: VartamanaCurrentFocusState,
  horizon: VartamanaHorizon = '30D',
  focusDate: Date | string = new Date(),
  language: Language = 'en'
): {
  selectedHorizon: VartamanaHorizon;
  horizonStartDate: string;
  horizonEndDate: string;
  isCumulativeFromDay0: boolean;
  horizonAnalysis: HorizonAnalysisResult;
  timelineMarkers: DynamicTimelineMarker[];
  comparisonWithToday: TodayComparisonDiff;
} {
  const focusObj = new Date(focusDate);
  const todayObj = new Date();

  const { spanDays } = resolveHorizonSpanDays(kundali, horizon, focusObj);
  const horizonStartDate = focusObj.toISOString();
  const horizonEndDate = new Date(focusObj.getTime() + spanDays * 86400000).toISOString();

  // 1. Cumulative Horizon Analysis starting from focusDate (Day 0) to Day spanDays
  const horizonAnalysis = buildHorizonAnalysis(
    kundali,
    currentState.dasha5Level,
    {
      activeThemes: currentState.activeThemes,
      emergingThemes: currentState.emergingThemes,
      peakingThemes: currentState.peakingThemes,
      releasingThemes: currentState.releasingThemes,
      dormantThemes: currentState.dormantThemes
    },
    focusObj,
    horizon
  );

  // 2. Dynamic Timeline Markers within horizon
  const timelineMarkers = buildDynamicTimelineMarkers(
    kundali,
    currentState.dasha5Level,
    focusObj,
    horizon,
    horizonAnalysis
  );

  // 3. Compare with Today (if focusDate differs from today)
  const todayDasha = resolve5LevelDashaForDate(kundali.dashas || [], todayObj);
  const todayGochar = evaluateGocharTriggersForDate(kundali, todayObj);
  const todayThemes = evaluateVartamanaLifeThemes(kundali, todayDasha, todayGochar, todayObj);

  const comparisonWithToday = calculateCompareWithToday(
    todayDasha,
    currentState.dasha5Level,
    todayThemes.activeThemes,
    currentState.activeThemes,
    todayGochar,
    currentState.gocharTriggers,
    focusObj
  );

  return {
    selectedHorizon: horizon,
    horizonStartDate,
    horizonEndDate,
    isCumulativeFromDay0: true,
    horizonAnalysis,
    timelineMarkers,
    comparisonWithToday
  };
}

// ============================================================================
// MASTER CALCULATION GATEWAY: VARTAMĀNA STHITI
// ============================================================================
export function calculateVartamanaSthiti(
  kundali: CompleteKundali,
  horizon: VartamanaHorizon = 'current_ad',
  focusDate: Date | string = new Date(),
  language: Language = 'en'
): VartamanaSthitiResult {
  const currentState = calculateVartamanaCurrentState(kundali, focusDate, language);
  const evolution = calculateHorizonTemporalEvolution(kundali, currentState, horizon, focusDate, language);
  const { spanDays } = resolveHorizonSpanDays(kundali, horizon, new Date(focusDate));

  return {
    ...currentState,
    ...evolution,
    snapshot: {
      ...currentState.snapshot,
      selectedHorizon: horizon,
      horizonDaysSpan: spanDays
    }
  };
}

// ============================================================================
// CONCERN INTELLIGENCE ENGINE ("WHAT'S ON YOUR MIND?" — GOV-066)
// ============================================================================

export const CONCERN_DOMAIN_KEYWORDS: Record<ConcernDomainId, { en: string[]; hi: string[] }> = {
  INNER_WELLBEING: {
    en: ['peace', 'stress', 'anxiety', 'mind', 'mental', 'clarity', 'calm', 'sleep', 'restless', 'focus', 'overthinking', 'meditation', 'depression', 'peace of mind', 'headache', 'inner peace', 'sanity', 'tension'],
    hi: ['शांति', 'तनाव', 'चिंता', 'मन', 'मानसिक', 'स्पष्टता', 'नींद', 'एकाग्रता', 'चित्त', 'सुकून', 'मानसिक शांति', 'भय', 'घबराहट']
  },
  CAREER: {
    en: ['job', 'career', 'promotion', 'work', 'office', 'boss', 'business', 'profession', 'transfer', 'interview', 'salary', 'hiring', 'colleague', 'appraisal', 'unemployment', 'fired', 'resignation', 'new role'],
    hi: ['नौकरी', 'आजीविका', 'करियर', 'पदोन्नति', 'कार्य', 'व्यापार', 'व्यवसाय', 'दफ्तर', 'साक्षात्कार', 'वेतन', 'अधिकारी', 'स्थानांतरण', 'रोजगार']
  },
  FINANCES: {
    en: ['money', 'finance', 'wealth', 'debt', 'loan', 'investment', 'savings', 'expenses', 'income', 'loss', 'profit', 'shares', 'stocks', 'property sale', 'cash', 'liquidity', 'bankruptcy', 'credit'],
    hi: ['धन', 'वित्त', 'आय', 'बचत', 'कर्ज', 'ऋण', 'निवेश', 'खर्च', 'हानि', 'लाभ', 'शेयर', 'संपत्ति', 'आर्थिक', 'पैसा', 'रुपया']
  },
  HEALTH_VITALITY: {
    en: ['health', 'energy', 'vitality', 'fatigue', 'tired', 'illness', 'fitness', 'diet', 'recovery', 'body', 'stamina', 'physical', 'strength', 'immunity', 'disease', 'wellness'],
    hi: ['स्वास्थ्य', 'ऊर्जा', 'बल', 'थकान', 'बीमारी', 'रोग', 'शारीरिक', 'ताजगी', 'प्रतिरोधक', 'ओज', 'तंदुरुस्ती', 'शरीर']
  },
  RELATIONSHIPS: {
    en: ['relationship', 'marriage', 'partner', 'love', 'spouse', 'dating', 'breakup', 'divorce', 'harmony', 'wedding', 'husband', 'wife', 'argument', 'fight', 'in-laws', 'courtship', 'engagement'],
    hi: ['संबंध', 'विवाह', 'साझेदारी', 'प्रेम', 'दांपत्य', 'पति', 'पत्नी', 'तलाक', 'अलगाव', 'सगाई', 'पारस्परिक', 'रिश्ता']
  },
  FAMILY_HOME: {
    en: ['family', 'home', 'house', 'parents', 'mother', 'father', 'property', 'children', 'relocation', 'flat', 'land', 'domestic', 'ancestral', 'child', 'son', 'daughter'],
    hi: ['परिवार', 'गृह', 'घर', 'माता', 'पिता', 'मकान', 'भूमि', 'संतान', 'पुत्र', 'पुत्री', 'पैतृक', 'घरेलू', 'निवास']
  },
  SPIRITUAL_DIRECTION: {
    en: ['spiritual', 'purpose', 'meditation', 'dharma', 'guru', 'moksha', 'sadhana', 'karma', 'destiny', 'detachment', 'temple', 'mantra', 'soul', 'meaning', 'higher path'],
    hi: ['आध्यात्मिक', 'उद्देश्य', 'साधना', 'धर्म', 'गुरु', 'मोक्ष', 'कर्म', 'संन्यास', 'मंदिर', 'मंत्र', 'आत्मा', 'भक्ति', 'पूजा']
  }
};

/**
 * Free-Text Semantic Query Evaluation & Sanitizer
 * Strips script tags, special characters, normalizes tokens, and maps keywords with fallback.
 */
export function evaluateFreeTextQuery(query: string, language: Language = 'en'): FreeTextEvaluationResult {
  if (!query || typeof query !== 'string' || query.trim().length === 0) {
    return {
      rawQuery: '',
      sanitizedQuery: '',
      confidence: 'LOW',
      matchedKeywords: [],
      fallbackChipApplied: true,
      clarificationPromptEn: 'Please enter a concern keyword or select one of the 7 life domain chips above.',
      clarificationPromptHi: 'कृपया कोई मुख्य शब्द लिखें अथवा ऊपर दिए गए ७ जीवन क्षेत्रों में से एक का चयन करें।'
    };
  }

  // Sanitize: strip script tags, html tags, excessive punctuation, normalize whitespace (preserving Unicode letters and marks \p{L}\p{M})
  const sanitized = query
    .replace(/<[^>]*>?/gm, '')
    .replace(/[^\p{L}\p{M}\p{N}\s\-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  const queryTokens = sanitized.split(' ').filter(t => t.length > 1);

  // Score each domain based on keyword matches
  const scores: Record<ConcernDomainId, { count: number; matchedKeywords: string[] }> = {
    INNER_WELLBEING: { count: 0, matchedKeywords: [] },
    CAREER: { count: 0, matchedKeywords: [] },
    FINANCES: { count: 0, matchedKeywords: [] },
    HEALTH_VITALITY: { count: 0, matchedKeywords: [] },
    RELATIONSHIPS: { count: 0, matchedKeywords: [] },
    FAMILY_HOME: { count: 0, matchedKeywords: [] },
    SPIRITUAL_DIRECTION: { count: 0, matchedKeywords: [] }
  };

  const domainIds = Object.keys(CANONICAL_CONCERN_DOMAINS) as ConcernDomainId[];
  for (const dom of domainIds) {
    const kwObj = CONCERN_DOMAIN_KEYWORDS[dom];
    const allKeywords = [...kwObj.en, ...kwObj.hi];
    for (const kw of allKeywords) {
      const lowerKw = kw.toLowerCase();
      // Match whole word or exact phrase, avoiding false positives inside longer words
      const escapedKw = lowerKw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const wordBoundaryRegex = new RegExp(`(?:^|\\s)${escapedKw}(?:$|\\s)`);
      if (wordBoundaryRegex.test(sanitized)) {
        scores[dom].count += 2;
        if (!scores[dom].matchedKeywords.includes(kw)) {
          scores[dom].matchedKeywords.push(kw);
        }
      }
    }
  }

  // Find top scoring domain
  let topDomain: ConcernDomainId | undefined;
  let maxScore = 0;
  for (const dom of domainIds) {
    if (scores[dom].count > maxScore) {
      maxScore = scores[dom].count;
      topDomain = dom;
    }
  }

  if (maxScore >= 4) {
    return {
      rawQuery: query,
      sanitizedQuery: sanitized,
      confidence: 'HIGH',
      matchedDomain: topDomain,
      matchedKeywords: scores[topDomain!].matchedKeywords,
      fallbackChipApplied: false
    };
  } else if (maxScore >= 1) {
    return {
      rawQuery: query,
      sanitizedQuery: sanitized,
      confidence: 'MODERATE',
      matchedDomain: topDomain,
      matchedKeywords: scores[topDomain!].matchedKeywords,
      fallbackChipApplied: false,
      clarificationPromptEn: `Matched closest to "${CANONICAL_CONCERN_DOMAINS[topDomain!].labelEn}". You can explore this or select another chip.`,
      clarificationPromptHi: `निकटतम क्षेत्र "${CANONICAL_CONCERN_DOMAINS[topDomain!].labelHi}" से संरेखित। आप इसे देख सकते हैं या दूसरा चिप चुन सकते हैं।`
    };
  } else {
    return {
      rawQuery: query,
      sanitizedQuery: sanitized,
      confidence: 'LOW',
      matchedDomain: undefined,
      matchedKeywords: [],
      fallbackChipApplied: true,
      clarificationPromptEn: 'Query could not be mapped with certainty. Defaulting to Inner Wellbeing or please select a chip.',
      clarificationPromptHi: 'आपके प्रश्न को निश्चितता से वर्गीकृत नहीं किया जा सका। मानसिक स्थिति का चयन किया गया है, या कृपया ऊपर से चुनें।'
    };
  }
}

/**
 * Deduplicates timing event windows preserving distinct, repeated, and reversed event occurrences.
 * Only prunes redundant duplicate calculation passes with identical signature and date range.
 */
export function deduplicateEventWindows(windows: ConcernTimingWindow[]): ConcernTimingWindow[] {
  const seenKeys = new Set<string>();
  const deduplicated: ConcernTimingWindow[] = [];

  for (const win of windows) {
    const key = win.deduplicationKey || `${win.windowType}_${win.startDate}_${win.endDate}_${win.determiningAstrologicalFactorEn}_${win.eventSequenceIndex ?? 0}_${win.isReversedOrRepeated ? 'REV' : 'FWD'}`;
    if (!seenKeys.has(key)) {
      seenKeys.add(key);
      deduplicated.push(win);
    }
  }

  return deduplicated;
}

/**
 * Evaluates Concern Intelligence for a declared domain or query at the given focus date.
 * Strictly adheres to:
 * 1. Zero Hallucinated Symptoms (Reflective, non-presumptive)
 * 2. Multi-Window Timing (No arithmetic midpoint auto-adoption)
 * 3. 6-Tier Epistemic Labeling (100% of statements categorized)
 * 4. Psychological Safety (Planets do not cause diseases; non-guaranteed upayas)
 * 5. Seeker vs Acharya Parity (Common deterministic calculation core)
 */
export function evaluateConcernIntelligence(
  kundali: CompleteKundali,
  focusDate: Date | string = new Date(),
  domainId: ConcernDomainId = 'INNER_WELLBEING',
  freeTextQuery?: string,
  language: Language = 'en'
): VartmanConcernIntelligenceResult {
  const focusObj = new Date(focusDate);
  const isHi = language === 'hi';

  // 1. Evaluate free-text query if provided
  let freeTextEval: FreeTextEvaluationResult | undefined;
  let activeDomainId = domainId;
  if (freeTextQuery && freeTextQuery.trim().length > 0) {
    freeTextEval = evaluateFreeTextQuery(freeTextQuery, language);
    if ((freeTextEval.confidence === 'HIGH' || freeTextEval.confidence === 'MODERATE') && freeTextEval.matchedDomain) {
      activeDomainId = freeTextEval.matchedDomain;
    }
  }

  const domainMeta: ConcernDomainMeta = CANONICAL_CONCERN_DOMAINS[activeDomainId] || CANONICAL_CONCERN_DOMAINS.INNER_WELLBEING;

  // 2. Resolve Dasha and Gochar at Focus Date
  const focusDasha = resolve5LevelDashaForDate(kundali.dashas || [], focusObj);
  const focusGochar = evaluateGocharTriggersForDate(kundali, focusObj);

  // 3. Evaluate Natal Bhavas for this Concern Domain
  const housesMap = new Map<number, { sign: string; lord: PlanetName }>();
  if (kundali.houses && kundali.houses.length > 0) {
    for (const h of kundali.houses) {
      housesMap.set(h.houseNumber, { sign: h.sign, lord: (ZODIAC_LORDS[h.sign] || 'Mars') as PlanetName });
    }
  }

  const natalBhavasEvaluated: AcharyaConcernDeepDive['natalBhavasEvaluated'] = domainMeta.primaryHouses.map(houseNum => {
    const hData = housesMap.get(houseNum) || { sign: 'Aries', lord: 'Mars' as PlanetName };
    const occupants = (kundali.planets || []).filter(p => p.house === houseNum).map(p => p.name);
    const drishtiLords = (kundali.planets || [])
      .filter(p => p.house && getSpecialDrishtiHouses(p.name, p.house).includes(houseNum))
      .map(p => p.name);
    const bindus = getSavByBhava(kundali.ashtakvarga, houseNum, kundali.houses || []);

    return {
      house: houseNum,
      sign: hData.sign,
      lord: hData.lord,
      occupants,
      drishtiLords,
      ashtakavargaBindus: bindus
    };
  });

  // 4. Resolve Dasha State & Dignities
  const mahaLord = focusDasha.md.planet;
  const antarLord = focusDasha.ad.planet;
  const pratyantarLord = focusDasha.pd.planet;

  const mahaDignity = kundali.planets?.find(p => p.name === mahaLord)?.dignity || 'Direct';
  const antarDignity = kundali.planets?.find(p => p.name === antarLord)?.dignity || 'Direct';
  const pratyantarDignity = kundali.planets?.find(p => p.name === pratyantarLord)?.dignity || 'Direct';

  // 5. Evaluate Gochar Influences for Domain Karakas & House Lords
  const domainPlanets: PlanetName[] = [
    ...domainMeta.karakas,
    ...natalBhavasEvaluated.map(b => b.lord)
  ];
  const uniqueDomainPlanets = Array.from(new Set(domainPlanets));

  const gocharInfluences: AcharyaConcernDeepDive['gocharInfluences'] = [];
  for (const pName of uniqueDomainPlanets) {
    const trigger = focusGochar.find(g => g.planet === pName);
    if (trigger) {
      gocharInfluences.push({
        planet: pName,
        currentSign: trigger.transitingSign,
        currentHouse: trigger.natalTarget.targetHouse,
        exactDegree: trigger.degreeNumber || 0,
        transitAspectsToNatal: [trigger.impactSummaryEn],
        orbDegrees: trigger.orbDegrees || 1.5
      });
    }
  }

  // 6. Multi-Window Timing Assembly (STRICT NO-MIDPOINT AUTO-ADOPTION)
  // A single broad life theme can contain zero, one, or multiple distinct event windows,
  // including repeated or reversed events (e.g. Hyderabad -> Mumbai -> Hyderabad).
  const rawTimingWindows: ConcernTimingWindow[] = [];

  // Window Type 1: Active Dasha Pratyantardasha (ACTIVE_DASHA_PD)
  const pdStart = focusDasha.pd.startDate ? formatDate(focusDasha.pd.startDate) : formatDate(focusObj);
  const pdEnd = focusDasha.pd.endDate ? formatDate(focusDasha.pd.endDate) : formatDate(focusObj);
  rawTimingWindows.push({
    windowId: `PD_${activeDomainId}`,
    windowType: 'ACTIVE_DASHA_PD',
    titleEn: `Active Pratyantardasha: ${pratyantarLord}`,
    titleHi: `सक्रिय प्रत्यंतर्दशा: ${pratyantarLord}`,
    startDate: pdStart,
    endDate: pdEnd,
    peakDate: null, // STRICTLY NULL FOR CONTINUOUS PLATEAUS
    peakDateRationaleEn: 'Continuous activation plateau across the active Pratyantardasha window; mathematical midpoint is not adopted as a peak date.',
    peakDateRationaleHi: 'सक्रिय प्रत्यंतर्दशा के दौरान निरंतर प्रभाव; किसी मध्यबिंदु को कृत्रिम रूप से चरम तिथि नहीं माना गया है।',
    durationLabelEn: `${pdStart} → ${pdEnd}`,
    durationLabelHi: `${pdStart} से ${pdEnd}`,
    status: 'ACTIVE_NOW',
    epistemicCategory: 'FACT_CALCULATED',
    determiningAstrologicalFactorEn: `Pratyantardasha Lord ${pratyantarLord} active under ${mahaLord} MD & ${antarLord} AD`,
    determiningAstrologicalFactorHi: `${mahaLord} महादशा एवं ${antarLord} अंतर्दशा में ${pratyantarLord} प्रत्यंतर्दशा`,
    significanceEn: `Governs micro-timing and cognitive focus regarding ${domainMeta.labelEn.toLowerCase()}.`,
    significanceHi: `${domainMeta.labelHi} के संदर्भ में सूक्ष्म समय और मानसिक ध्यान को नियंत्रित करता है।`,
    eventSequenceIndex: 0,
    isReversedOrRepeated: false,
    eventPhaseDescriptorEn: 'Primary Dasha Horizon',
    eventPhaseDescriptorHi: 'प्राथमिक दशा कालखंड',
    deduplicationKey: `PD_${activeDomainId}_${pdStart}_${pdEnd}`
  });

  // Window Type 2: Transit Event Triggers (Zero, One, or Multiple Distinct Events)
  const domainTriggers = focusGochar.filter(g => uniqueDomainPlanets.includes(g.planet));
  if (domainTriggers.length > 0) {
    domainTriggers.forEach((trig, idx) => {
      const isExact = trig.isExact || (trig.orbDegrees && trig.orbDegrees <= 1.2);
      const isRev = trig.isRepeating || trig.isRetrograde;
      const phaseEn = isRev
        ? `Repeated / Oscillating Phase (${trig.isRetrograde ? 'Retrograde Arc' : 'Revisitation'})`
        : (idx === 0 ? 'Initial Transit Activation' : `Secondary Alignment Phase ${idx + 1}`);
      const phaseHi = isRev
        ? `पुनरावर्ती / प्रत्यावर्तन कालखंड (${trig.isRetrograde ? 'वक्री चक्र' : 'पुनः संरेखण'})`
        : (idx === 0 ? 'प्रारंभिक गोचर संरेखण' : `द्वितीयक गोचर चरण ${idx + 1}`);

      rawTimingWindows.push({
        windowId: `TRANSIT_${activeDomainId}_${trig.planet}_${idx}`,
        windowType: 'EXACT_TRANSIT_TRIGGER',
        titleEn: `Transit Event ${idx + 1}: ${trig.planet} (${trig.transitingSign})`,
        titleHi: `गोचर घटना ${idx + 1}: ${trig.planet} (${trig.transitingSign})`,
        startDate: formatDate(focusObj),
        endDate: formatDate(focusDasha.sd.endDate || focusObj),
        peakDate: isExact ? formatDate(focusObj) : null,
        peakDateRationaleEn: isExact
          ? `Exact astronomical transit trigger of ${trig.planet} across ${trig.transitingSign} within ${trig.orbDegrees.toFixed(1)}° orb.`
          : 'Continuous transit influence without an exact sub-1.2° orb conjunction at this specific moment.',
        peakDateRationaleHi: isExact
          ? `${trig.planet} का ${trig.transitingSign} में ${trig.orbDegrees.toFixed(1)}° सीमा के भीतर सटीक खगोलीय गोचर संरेखण।`
          : 'वर्तमान में किसी १.२° से कम सटीक युति के बिना सतत गोचर प्रभाव; कोई एकाकी शिखर नहीं है।',
        durationLabelEn: isExact ? `Exact on ${formatDate(focusObj)}` : `${formatDate(focusObj)} → ${formatDate(focusDasha.sd.endDate || focusObj)}`,
        durationLabelHi: isExact ? `${formatDate(focusObj)} को संरेखित` : `${formatDate(focusObj)} से ${formatDate(focusDasha.sd.endDate || focusObj)}`,
        status: 'ACTIVE_NOW',
        epistemicCategory: 'INFERENCE_SYNTHESIS',
        determiningAstrologicalFactorEn: `${trig.planet} transit ${trig.aspectType || 'aspect'} to ${trig.natalTarget?.targetName || 'natal point'} at ${trig.degreeNumber.toFixed(1)}°`,
        determiningAstrologicalFactorHi: `${trig.planet} का ${trig.natalTarget?.targetName || 'जन्म बिंदु'} पर गोचर दृष्टि/संरेखण ${trig.degreeNumber.toFixed(1)}° पर`,
        significanceEn: isRev
          ? `Represents a repeated, oscillating, or return phase (such as return relocation or reassessment) within the ${domainMeta.labelEn.toLowerCase()} theme.`
          : `Catalytic trigger event for ${domainMeta.labelEn.toLowerCase()}.`,
        significanceHi: isRev
          ? `${domainMeta.labelHi} के अंतर्गत पुनरावृत्ति, प्रत्यावर्तन अथवा वापसी का चरण।`
          : `${domainMeta.labelHi} हेतु उत्प्रेरक घटना।`,
        eventSequenceIndex: idx + 1,
        isReversedOrRepeated: isRev,
        eventPhaseDescriptorEn: phaseEn,
        eventPhaseDescriptorHi: phaseHi,
        deduplicationKey: `TRANSIT_${activeDomainId}_${trig.planet}_${trig.transitingSign}_${trig.natalTarget?.targetName || 'natal'}_${isRev ? 'REV' : 'FWD'}`
      });
    });
  } else {
    // Zero specific transit triggers: Provide ambient transit backdrop window
    const topTransit = focusGochar[0];
    if (topTransit) {
      rawTimingWindows.push({
        windowId: `TRANSIT_AMBIENT_${activeDomainId}`,
        windowType: 'EXACT_TRANSIT_TRIGGER',
        titleEn: `Ambient Transit Rhythm: ${topTransit.planet}`,
        titleHi: `परिवेशीय गोचर लय: ${topTransit.planet}`,
        startDate: formatDate(focusObj),
        endDate: formatDate(focusDasha.sd.endDate || focusObj),
        peakDate: null,
        peakDateRationaleEn: 'Continuous ambient transit influence; zero isolated transit triggers active.',
        peakDateRationaleHi: 'सतत परिवेशीय गोचर प्रभाव; कोई एकाकी उत्प्रेरक सक्रिय नहीं है।',
        durationLabelEn: `${formatDate(focusObj)} → ${formatDate(focusDasha.sd.endDate || focusObj)}`,
        durationLabelHi: `${formatDate(focusObj)} से ${formatDate(focusDasha.sd.endDate || focusObj)}`,
        status: 'ACTIVE_NOW',
        epistemicCategory: 'INFERENCE_SYNTHESIS',
        determiningAstrologicalFactorEn: `Ambient transit of ${topTransit.planet} across ${topTransit.transitingSign}`,
        determiningAstrologicalFactorHi: `${topTransit.planet} का ${topTransit.transitingSign} में परिवेशीय गोचर`,
        significanceEn: 'Ambient planetary backdrop providing general stability.',
        significanceHi: 'सामान्य स्थिरता प्रदान करने वाली परिवेशीय ग्रहीय पृष्ठभूमि।',
        eventSequenceIndex: 1,
        isReversedOrRepeated: false,
        eventPhaseDescriptorEn: 'Ambient Baseline',
        eventPhaseDescriptorHi: 'परिवेशीय आधार रेखा',
        deduplicationKey: `TRANSIT_AMBIENT_${activeDomainId}`
      });
    }
  }

  // Window Type 3: Consolidation Plateau (CONSOLIDATION_PLATEAU)
  const adStart = focusDasha.ad.startDate ? formatDate(focusDasha.ad.startDate) : formatDate(focusObj);
  const adEnd = focusDasha.ad.endDate ? formatDate(focusDasha.ad.endDate) : formatDate(focusObj);
  rawTimingWindows.push({
    windowId: `AD_PLATEAU_${activeDomainId}`,
    windowType: 'CONSOLIDATION_PLATEAU',
    titleEn: `Consolidation Cycle: ${antarLord} Antardasha`,
    titleHi: `समन्वय कालखंड: ${antarLord} अंतर्दशा`,
    startDate: adStart,
    endDate: adEnd,
    peakDate: null, // STRICTLY NULL FOR CONTINUOUS PLATEAUS
    peakDateRationaleEn: 'Broad Antardasha consolidation cycle; continuous integration without an arithmetic midpoint.',
    peakDateRationaleHi: 'दीर्घकालिक अंतर्दशा समन्वय कालखंड; बिना किसी कृत्रिम मध्यबिंदु के सतत प्रभाव।',
    durationLabelEn: `${adStart} → ${adEnd}`,
    durationLabelHi: `${adStart} से ${adEnd}`,
    status: 'CONSOLIDATING',
    epistemicCategory: 'UNCERTAINTY_BOUNDARY',
    determiningAstrologicalFactorEn: `Broader ${antarLord} Antardasha consolidation cycle`,
    determiningAstrologicalFactorHi: `दीर्घकालिक ${antarLord} अंतर्दशा का समन्वय कालखंड`,
    significanceEn: `Broader evolutionary arch establishing thematic foundation for ${domainMeta.labelEn.toLowerCase()}.`,
    significanceHi: `${domainMeta.labelHi} के लिए आधारभूत स्थिरता निर्मित करने वाला व्यापक विकासात्मक चक्र।`,
    eventSequenceIndex: rawTimingWindows.length,
    isReversedOrRepeated: false,
    eventPhaseDescriptorEn: 'Macro Integration Arc',
    eventPhaseDescriptorHi: 'दीर्घकालिक समन्वय चक्र',
    deduplicationKey: `AD_PLATEAU_${activeDomainId}_${adStart}_${adEnd}`
  });

  const timingWindows = deduplicateEventWindows(rawTimingWindows);

  // 7. 6-Tier Epistemic Insight Statements (100% COVERAGE)
  const avgBindus = natalBhavasEvaluated.length > 0
    ? (natalBhavasEvaluated.reduce((acc, b) => acc + b.ashtakavargaBindus, 0) / natalBhavasEvaluated.length).toFixed(1)
    : '28.0';

  const epistemicInsights: EpistemicInsightStatement[] = [
    // 1. [FACT_CALCULATED]
    {
      id: `FACT_${activeDomainId}`,
      category: 'FACT_CALCULATED',
      labelTag: EPISTEMIC_CLASSIFICATION_REGISTRY.FACT_CALCULATED.tag,
      titleEn: 'Astronomical Baseline & House Placements',
      titleHi: 'खगोलीय स्थिति एवं भाव विन्यास',
      narrativeEn: `Domain correlates with Bhavas [${domainMeta.primaryHouses.join(', ')}] with lords [${natalBhavasEvaluated.map(b => b.lord).join(', ')}]. Active Vimshottari progression at Focus Date: ${mahaLord} MD → ${antarLord} AD → ${pratyantarLord} PD.`,
      narrativeHi: `यह क्षेत्र भाव [${domainMeta.primaryHouses.join(', ')}] एवं स्वामियों [${natalBhavasEvaluated.map(b => b.lord).join(', ')}] से संरेखित है। केंद्रित तिथि पर सक्रिय विंशोत्तरी दशा: ${mahaLord} महादशा → ${antarLord} अंतर्दशा → ${pratyantarLord} प्रत्यंतर्दशा।`
    },
    // 2. [RULE_CLASSICAL]
    {
      id: `RULE_${activeDomainId}`,
      category: 'RULE_CLASSICAL',
      labelTag: EPISTEMIC_CLASSIFICATION_REGISTRY.RULE_CLASSICAL.tag,
      titleEn: 'Classical Shastric Framework',
      titleHi: 'शास्त्रीय सिद्धांत एवं सूत्र',
      narrativeEn: getDomainShastricRuleEn(activeDomainId),
      narrativeHi: getDomainShastricRuleHi(activeDomainId),
      shastraReference: getDomainShastraReference(activeDomainId)
    },
    // 3. [INTERPRETATION_PARAM]
    {
      id: `PARAM_${activeDomainId}`,
      category: 'INTERPRETATION_PARAM',
      labelTag: EPISTEMIC_CLASSIFICATION_REGISTRY.INTERPRETATION_PARAM.tag,
      titleEn: 'Parametric & Calculation Standard',
      titleHi: 'अयनांश एवं गणना मानक',
      narrativeEn: 'Evaluated using True Chitrapaksha (Lahiri) Ayanamsha, Sripati / Bhava Chalita cusp boundaries, and a standard 365.2422-day solar year for Vimshottari period division.',
      narrativeHi: 'गणना सत्य चित्रपक्ष (लाहिड़ी) अयनांश, श्रीपति/भाव चलित सीमांकन, एवं विंशोत्तरी काल विभाजन हेतु ३६५.२४२२ दिवसीय सौर वर्ष पर आधारित है।'
    },
    // 4. [INFERENCE_SYNTHESIS]
    {
      id: `SYNTHESIS_${activeDomainId}`,
      category: 'INFERENCE_SYNTHESIS',
      labelTag: EPISTEMIC_CLASSIFICATION_REGISTRY.INFERENCE_SYNTHESIS.tag,
      titleEn: 'Synthesized Jyotish Assessment',
      titleHi: 'समन्वित ज्योतिषीय फलित निष्कर्ष',
      narrativeEn: `Dasha convergence of ${mahaLord}-${antarLord}-${pratyantarLord} combined with an average Ashtakavarga strength of ${avgBindus} bindus across Bhavas [${domainMeta.primaryHouses.join(', ')}] indicates ${parseFloat(avgBindus) >= 28 ? 'favorable structural resilience' : 'a phase calling for deliberate focus and mindful patience'}.`,
      narrativeHi: `${mahaLord}-${antarLord}-${pratyantarLord} दशा संरेखण तथा भाव [${domainMeta.primaryHouses.join(', ')}] में औसत ${avgBindus} अष्टकवर्ग रेखाएं यह दर्शाती हैं कि यह कालखंड ${parseFloat(avgBindus) >= 28 ? 'सकारात्मक संरचनात्मक सामर्थ्य' : 'धैर्य, सतर्कता एवं सचेत आत्म-संयम'} की मांग करता है।`
    },
    // 5. [SPECULATION_UNVERIFIED]
    {
      id: `SPEC_${activeDomainId}`,
      category: 'SPECULATION_UNVERIFIED',
      labelTag: EPISTEMIC_CLASSIFICATION_REGISTRY.SPECULATION_UNVERIFIED.tag,
      titleEn: 'Quarantined Interpretive Speculation',
      titleHi: 'पृथक्कृत व्याख्यात्मक अनुमान',
      narrativeEn: 'Individual life outcomes depend substantially on personal Purushartha (conscious effort), environmental circumstances, and emotional choices. Celestial configurations indicate systemic archetypes rather than fatalistic events.',
      narrativeHi: 'व्यक्तिगत जीवन के परिणाम पुरुषार्थ (सचेत प्रयास), परिवेशीय परिस्थितियों एवं निर्णयों पर निर्भर करते हैं। खगोलीय विन्यास सांकेतिक प्रवृत्तियां प्रस्तुत करते हैं, कोई अनिवार्य नियतिवादी घटना नहीं।'
    },
    // 6. [UNCERTAINTY_BOUNDARY]
    {
      id: `UNCERTAIN_${activeDomainId}`,
      category: 'UNCERTAINTY_BOUNDARY',
      labelTag: EPISTEMIC_CLASSIFICATION_REGISTRY.UNCERTAINTY_BOUNDARY.tag,
      titleEn: 'Analytical Boundary & Timing Limit',
      titleHi: 'विश्लेषणात्मक सीमा एवं समय सटीकता',
      narrativeEn: 'Pratyantardasha and minute transit triggers depend on accurate birth-time recording. Timing windows reflect evolutionary developmental plateaus rather than single deterministic turning points.',
      narrativeHi: 'प्रत्यंतर्दशा और सूक्ष्म गोचर संरेखण जन्म समय की सूक्ष्म सटीकता पर निर्भर हैं। समय सीमाएं किसी एकाकी मोड़ के बजाय सतत विकासात्मक अंतराल का प्रतिनिधित्व करती हैं।'
    }
  ];

  // 8. Psychological Safety Framing & Non-Causation Model
  const safetyFraming: PsychologicalSafetyFraming = {
    medicalDisclaimerEn: 'Astrological analysis is for contemplative and personal self-reflection only. It does not diagnose, treat, or prevent any illness, and does not substitute for qualified clinical, psychological, psychiatric, legal, or financial professional care.',
    medicalDisclaimerHi: 'ज्योतिषीय विश्लेषण केवल चिंतन और आत्म-बोध हेतु है। यह किसी भी शारीरिक या मानसिक बीमारी का निदान, उपचार या रोकथाम नहीं करता, और योग्य चिकित्सकीय, मनोवैज्ञानिक, कानूनी या वित्तीय सलाह का विकल्प नहीं है।',
    nonCausationStatementEn: 'Planets and astrological configurations do not causally produce illness, distress, or deterministic fate. They represent symbolic celestial timekeeping and contemplative archetypes.',
    nonCausationStatementHi: 'ग्रह अथवा खगोलीय योग किसी बीमारी या संकट का प्रत्यक्ष कारण नहीं बनते। ये प्रतीकात्मक कालखंड और आत्म-चिंतन के माध्यम हैं।',
    upayaFramingEn: 'All suggested practices are non-deterministic grounding and mindfulness exercises designed to support inner equilibrium.',
    upayaFramingHi: 'सुझाए गए अभ्यास आंतरिक संतुलन के लिए केवल ध्यान और आत्म-नियंत्रण के सहायक साधन हैं।'
  };

  // 9. Upaya Grounding Practices (Domain-specific, strictly non-guaranteed)
  const upayas: ConcernUpayaPractice[] = getDomainUpayas(activeDomainId);

  // 10. Acharya Deep-Dive Telemetry
  const acharyaTelemetry: AcharyaConcernDeepDive = {
    natalBhavasEvaluated,
    dashaState: {
      mahaLord,
      mahaDignity,
      antarLord,
      antarDignity,
      pratyantarLord,
      pratyantarDignity,
      mutualRelationship: `${mahaLord} and ${antarLord} in ${mahaDignity} / ${antarDignity} status`
    },
    gocharInfluences,
    classicalSutrasCited: getDomainClassicalSutras(activeDomainId)
  };

  return {
    declaredDomain: activeDomainId,
    freeTextEvaluation: freeTextEval,
    focusDateFormatted: formatDate(focusObj),
    focusDateIso: focusObj.toISOString(),
    mappedHouses: domainMeta.primaryHouses,
    mappedKarakas: domainMeta.karakas,
    activeDasha: {
      maha: mahaLord,
      antar: antarLord,
      pratyantar: pratyantarLord
    },
    timingWindows,
    epistemicInsights,
    safetyFraming,
    upayas,
    acharyaTelemetry
  };
}

// ----------------------------------------------------------------------------
// Domain Shastric Rules & Upaya Helper Functions
// ----------------------------------------------------------------------------

function getDomainShastricRuleEn(domain: ConcernDomainId): string {
  switch (domain) {
    case 'INNER_WELLBEING':
      return 'Brihat Parashara Hora Shastra (BPHS Ch. 24, Sloka 3-5): The Moon (Chandra) rules the emotional mind (Manas), while Mercury (Budha) rules discerning intellect (Buddhi), and the 4th house reflects inner tranquility (Sukhastana).';
    case 'CAREER':
      return 'Brihat Parashara Hora Shastra (BPHS Ch. 18, Sloka 1-4): The 10th house governs Karma, enterprise, authority, and public dignity. The Sun rules status and administrative strength, while Saturn rules endurance and professional discipline.';
    case 'FINANCES':
      return 'Brihat Parashara Hora Shastra (BPHS Ch. 13, Sloka 2-6): The 2nd house denotes accumulated wealth (Kosha/Dhana) and the 11th house signifies gain (Labha). Jupiter and Venus serve as classical significators of material abundance.';
    case 'HEALTH_VITALITY':
      return 'Brihat Parashara Hora Shastra (BPHS Ch. 14, Sloka 2-7): The 1st house governs bodily constitution (Tanu Bala), the 6th house reflects biological friction and recuperative effort (Roga/Ripu), and the Sun signifies primal vitality (Ojas).';
    case 'RELATIONSHIPS':
      return 'Brihat Parashara Hora Shastra (BPHS Ch. 15, Sloka 1-8): The 7th house rules union, partnerships, and spousal harmony (Jayabhava), with Venus (Shukra) as universal significator of relational empathy.';
    case 'FAMILY_HOME':
      return 'Brihat Parashara Hora Shastra (BPHS Ch. 12, Sloka 1-6): The 4th house denotes domestic stability, property, and maternal blessings (Matrubhava), with the Moon and Jupiter providing domestic peace.';
    case 'SPIRITUAL_DIRECTION':
      return 'Brihat Parashara Hora Shastra (BPHS Ch. 17, Sloka 1-5): The 9th house signifies Dharma, philosophical wisdom, and divine grace (Bhagyabhava), while the 12th house governs contemplative liberation (Moksha) under Ketu and Jupiter.';
  }
}

function getDomainShastricRuleHi(domain: ConcernDomainId): string {
  switch (domain) {
    case 'INNER_WELLBEING':
      return 'बृहत्पाराशर होराशास्त्र (अध्याय २४, श्लोक ३-५): चन्द्रमा मन का एवं बुध बुद्धि का कारक है। चतुर्थ भाव मानसिक सुख एवं चित्त शांति का आधार है।';
    case 'CAREER':
      return 'बृहत्पाराशर होराशास्त्र (अध्याय १८, श्लोक १-४): दशम भाव कर्म, पद-प्रतिष्ठा एवं आजीविका का नियंत्रक है। सूर्य प्रभुत्व का एवं शनि अध्यवसाय का कारक है।';
    case 'FINANCES':
      return 'बृहत्पाराशर होराशास्त्र (अध्याय १३, श्लोक २-६): द्वितीय भाव धन-संचय एवं एकादश भाव सर्व-लाभ का द्योतक है। गुरु एवं शुक्र धन कारक हैं।';
    case 'HEALTH_VITALITY':
      return 'बृहत्पाराशर होराशास्त्र (अध्याय १४, श्लोक २-७): प्रथम भाव शरीर बल (तनु), षष्ठ भाव रोग प्रतिरोधक क्षमता एवं संघर्ष, तथा सूर्य प्राण शक्ति (ओज) का अधिपति है।';
    case 'RELATIONSHIPS':
      return 'बृहत्पाराशर होराशास्त्र (अध्याय १५, श्लोक १-८): सप्तम भाव दांपत्य, साझेदारी एवं पारस्परिक समन्वय (जाया भाव) का स्थान है, तथा शुक्र संबंध कारक है।';
    case 'FAMILY_HOME':
      return 'बृहत्पाराशर होराशास्त्र (अध्याय १२, श्लोक १-६): चतुर्थ भाव गृह शांति, भूमि, वाहन एवं मातृ सुख (मातृ भाव) का नियंत्रक है।';
    case 'SPIRITUAL_DIRECTION':
      return 'बृहत्पाराशर होराशास्त्र (अध्याय १७, श्लोक १-५): नवम भाव धर्म, दर्शन एवं गुरु कृपा का, तथा द्वादश भाव मोक्ष एवं आत्म-विसर्जन का स्थान है।';
  }
}

function getDomainShastraReference(domain: ConcernDomainId): string {
  switch (domain) {
    case 'INNER_WELLBEING': return 'Brihat Parashara Hora Shastra (BPHS Ch. 24) & Phaladeepika Ch. 15';
    case 'CAREER': return 'Brihat Parashara Hora Shastra (BPHS Ch. 18) & Saravali Ch. 30';
    case 'FINANCES': return 'Brihat Parashara Hora Shastra (BPHS Ch. 13) & Jaimini Sutram 1.3';
    case 'HEALTH_VITALITY': return 'Brihat Parashara Hora Shastra (BPHS Ch. 14) & Phaladeepika Ch. 14';
    case 'RELATIONSHIPS': return 'Brihat Parashara Hora Shastra (BPHS Ch. 15) & Brihat Jataka Ch. 18';
    case 'FAMILY_HOME': return 'Brihat Parashara Hora Shastra (BPHS Ch. 12) & Phaladeepika Ch. 16';
    case 'SPIRITUAL_DIRECTION': return 'Brihat Parashara Hora Shastra (BPHS Ch. 17) & Uttara Kalamrita Ch. 4';
  }
}

function getDomainClassicalSutras(domain: ConcernDomainId): string[] {
  switch (domain) {
    case 'INNER_WELLBEING': return ['BPHS Ch. 24 (Chandra-Buddhi Karakatva)', 'Phaladeepika Ch. 15 (Chitta Sthiti)'];
    case 'CAREER': return ['BPHS Ch. 18 (Dashama Bhavaphala)', 'Saravali Ch. 30 (Karmaphala)'];
    case 'FINANCES': return ['BPHS Ch. 13 (Dhana Bhavaphala)', 'Jaimini Sutram 1.3 (Artha Yoga)'];
    case 'HEALTH_VITALITY': return ['BPHS Ch. 14 (Shashta Bhavaphala)', 'Phaladeepika Ch. 14 (Roga Vicara)'];
    case 'RELATIONSHIPS': return ['BPHS Ch. 15 (Saptama Bhavaphala)', 'Brihat Jataka Ch. 18 (Vivaha)'];
    case 'FAMILY_HOME': return ['BPHS Ch. 12 (Chaturtha Bhavaphala)', 'Phaladeepika Ch. 16 (Bandhu Sukha)'];
    case 'SPIRITUAL_DIRECTION': return ['BPHS Ch. 17 (Navama & Dwadasha Bhavaphala)', 'Uttara Kalamrita Ch. 4 (Moksha)'];
  }
}

function getDomainUpayas(domain: ConcernDomainId): ConcernUpayaPractice[] {
  const nonGuaranteeEn = 'Traditional contemplative, lifestyle, and ethical practices intended for psychological grounding and self-reflection. They do not constitute guaranteed outcomes, magical cures, or medical treatments.';
  const nonGuaranteeHi = 'पारंपरिक जीवनशैली, ध्यान एवं नैतिक आचरण अभ्यास जो मानसिक संतुलन हेतु हैं। ये कोई चमत्कारी उपाय, निश्चित परिणाम अथवा चिकित्सीय उपचार नहीं हैं।';

  switch (domain) {
    case 'INNER_WELLBEING':
      return [
        {
          id: 'UPAYA_WELLBEING_1',
          titleEn: 'Chandra-Budha Grounding: Evening Mindful Breathing',
          titleHi: 'चन्द्र-बुध शांति: सायंकालीन प्राणायाम',
          category: 'MINDFULNESS_GROUNDING',
          practiceEn: '10 minutes of slow diaphragmatic breathing (Anulom-Vilom or gentle breath focus) in quiet stillness before sleep; observe digital screen boundaries 1 hour before resting.',
          practiceHi: 'शयन से पूर्व १० मिनट शांत वातावरण में अनुलोम-विलोम अथवा सजग श्वास ध्यान; विश्राम से एक घंटा पूर्व डिजिटल स्क्रीन से दूरी बनाएं।',
          beneficialPeriodEn: 'Daily during evening Sandhya or pre-sleep transition',
          beneficialPeriodHi: 'प्रतिदिन सायंकाल अथवा शयन से पूर्व',
          nonGuaranteeNoticeEn: nonGuaranteeEn,
          nonGuaranteeNoticeHi: nonGuaranteeHi
        },
        {
          id: 'UPAYA_WELLBEING_2',
          titleEn: 'Jupiter Contemplative Reflection: Gratitude Journaling',
          titleHi: 'गुरु कृपा चिंतन: कृतज्ञता लेखन',
          category: 'CLASSICAL_DHYANA',
          practiceEn: 'Maintain a brief 3-line daily record of things in life you are grateful for, grounding cognitive attention in perspective rather than anxiety.',
          practiceHi: 'दैनिक जीवन में ३ सकारात्मक बातों का संक्षिप्त लेखन, जिससे मन व्यर्थ चिंता के स्थान पर कृतज्ञता पर केंद्रित हो सके।',
          beneficialPeriodEn: 'Morning quiet hours (Brahma / Surya Muhurta)',
          beneficialPeriodHi: 'प्रातःकाल शांत समय (ब्रह्म अथवा सूर्य मुहूर्त)',
          nonGuaranteeNoticeEn: nonGuaranteeEn,
          nonGuaranteeNoticeHi: nonGuaranteeHi
        }
      ];

    case 'CAREER':
      return [
        {
          id: 'UPAYA_CAREER_1',
          titleEn: 'Surya Morning Alignment: Conscious Work Intention',
          titleHi: 'सूर्य संरेखण: कार्य संकल्प एवं ऊर्जा संचय',
          category: 'LIFESTYLE_RHYTHM',
          practiceEn: 'Spend 5 minutes in natural morning sunlight setting 1-2 primary duty intentions for the day with disciplined focus.',
          practiceHi: 'प्रातः ५ मिनट सूर्य के प्रकाश में खड़े होकर दिन के १-२ प्रमुख कर्तव्यों का शांत मन से संकल्प लें।',
          beneficialPeriodEn: 'Morning within 1 hour of sunrise',
          beneficialPeriodHi: 'सूर्योदय के १ घंटे के भीतर',
          nonGuaranteeNoticeEn: nonGuaranteeEn,
          nonGuaranteeNoticeHi: nonGuaranteeHi
        },
        {
          id: 'UPAYA_CAREER_2',
          titleEn: 'Saturnian Perseverance: Mindful Service (Seva)',
          titleHi: 'शनि निष्काम कर्म: सहयोग एवं सेवा',
          category: 'CHARITABLE_DAAN',
          practiceEn: 'Offer supportive guidance or non-transactional practical assistance to juniors or support staff in your work environment.',
          practiceHi: 'कार्यस्थल अथवा समाज में सहयोगियों या सहायकों को निःस्वार्थ सहयोग व सम्मान प्रदान करें।',
          beneficialPeriodEn: 'Saturday mornings or mid-week workdays',
          beneficialPeriodHi: 'शनिवार प्रातः अथवा कार्य दिवस',
          nonGuaranteeNoticeEn: nonGuaranteeEn,
          nonGuaranteeNoticeHi: nonGuaranteeHi
        }
      ];

    case 'FINANCES':
      return [
        {
          id: 'UPAYA_FINANCE_1',
          titleEn: 'Brihaspati Discernment: Conscious Spending Review',
          titleHi: 'गुरु विवेक: सचेत व्यय समीक्षा',
          category: 'MINDFULNESS_GROUNDING',
          practiceEn: 'Practice a 48-hour cooling-off rule before non-essential purchases; review financial obligations with calm objectivity.',
          practiceHi: 'अनावश्यक खरीदारी से पूर्व ४८ घंटे का संयम रखें; अपने दायित्वों की शांत व तार्किक समीक्षा करें।',
          beneficialPeriodEn: 'Thursday mornings (Brihaspativara)',
          beneficialPeriodHi: 'गुरुवार प्रातः काल',
          nonGuaranteeNoticeEn: nonGuaranteeEn,
          nonGuaranteeNoticeHi: nonGuaranteeHi
        },
        {
          id: 'UPAYA_FINANCE_2',
          titleEn: 'Dharma-Aligned Daan: Educational or Food Support',
          titleHi: 'धर्मनिष्ठ दान: विद्या अथवा अन्न सेवा',
          category: 'CHARITABLE_DAAN',
          practiceEn: 'Contribute a modest, sustainable portion of earnings toward students needing books or community food relief.',
          practiceHi: 'अपनी क्षमता अनुसार किसी जरूरतमंद विद्यार्थी की पुस्तक सहायता अथवा अन्न दान में नियमित सहयोग करें।',
          beneficialPeriodEn: 'Regular monthly or weekly rhythm',
          beneficialPeriodHi: 'मासिक अथवा साप्ताहिक नियमित अंतराल',
          nonGuaranteeNoticeEn: nonGuaranteeEn,
          nonGuaranteeNoticeHi: nonGuaranteeHi
        }
      ];

    case 'HEALTH_VITALITY':
      return [
        {
          id: 'UPAYA_HEALTH_1',
          titleEn: 'Circadian Rhythm Realignment & Rest',
          titleHi: 'प्राकृतिक दिनचर्या एवं पर्याप्त विश्राम',
          category: 'LIFESTYLE_RHYTHM',
          practiceEn: 'Align meals and sleep times with natural solar rhythms; drink adequate water and avoid late heavy dining.',
          practiceHi: 'आहार एवं शयन के समय को सूर्योदय-सूर्यास्त के अनुसार व्यवस्थित करें; समय पर हल्का आहार लें।',
          beneficialPeriodEn: 'Daily consistency',
          beneficialPeriodHi: 'दैनिक नियमितता',
          nonGuaranteeNoticeEn: nonGuaranteeEn,
          nonGuaranteeNoticeHi: nonGuaranteeHi
        },
        {
          id: 'UPAYA_HEALTH_2',
          titleEn: 'Clinical Consultation Awareness',
          titleHi: 'सजग चिकित्सकीय परामर्श',
          category: 'MINDFULNESS_GROUNDING',
          practiceEn: 'For persistent physical symptoms, promptly consult qualified healthcare practitioners without relying on astrological remedies.',
          practiceHi: 'किसी भी निरंतर शारीरिक लक्षण हेतु अविलंब योग्य चिकित्सक से परामर्श लें; ज्योतिष को उपचार का विकल्प न समझें।',
          beneficialPeriodEn: 'Immediate whenever symptoms arise',
          beneficialPeriodHi: 'लक्षण प्रकट होने पर तत्काल',
          nonGuaranteeNoticeEn: nonGuaranteeEn,
          nonGuaranteeNoticeHi: nonGuaranteeHi
        }
      ];

    case 'RELATIONSHIPS':
      return [
        {
          id: 'UPAYA_RELATIONSHIPS_1',
          titleEn: 'Shukra Empathy: Mindful Active Listening',
          titleHi: 'शुक्र सौहार्द: सजग एवं धैर्यवान श्रवण',
          category: 'MINDFULNESS_GROUNDING',
          practiceEn: 'In interpersonal discussions, listen fully without interrupting or formulating an immediate defense; validate your partner’s emotions.',
          practiceHi: 'पारस्परिक संवाद में बीच में टोके बिना धैर्यपूर्वक सुनें और साथी के मनोभावों का सम्मान करें।',
          beneficialPeriodEn: 'Daily domestic interaction',
          beneficialPeriodHi: 'दैनिक पारिवारिक संवाद',
          nonGuaranteeNoticeEn: nonGuaranteeEn,
          nonGuaranteeNoticeHi: nonGuaranteeHi
        },
        {
          id: 'UPAYA_RELATIONSHIPS_2',
          titleEn: 'Harmony Restoration: Non-Reactive Pause',
          titleHi: 'सामंजस्य रक्षा: प्रतिक्रिया से पूर्व विराम',
          category: 'CLASSICAL_DHYANA',
          practiceEn: 'When emotional friction arises, take a conscious 5-breath pause before speaking to prevent regretful words.',
          practiceHi: 'तनाव की स्थिति में बोलने से पूर्व ५ गहरी श्वास लेकर मौन रहें ताकि कटु वचनों से बचा जा सके।',
          beneficialPeriodEn: 'Moments of conflict or emotional charge',
          beneficialPeriodHi: 'विवाद अथवा मतभेद के क्षणों में',
          nonGuaranteeNoticeEn: nonGuaranteeEn,
          nonGuaranteeNoticeHi: nonGuaranteeHi
        }
      ];

    case 'FAMILY_HOME':
      return [
        {
          id: 'UPAYA_HOME_1',
          titleEn: 'Griha Shanti: Space Cleansing & Calming Ritual',
          titleHi: 'गृह शांति: आवास स्वच्छता एवं दीप संध्या',
          category: 'LIFESTYLE_RHYTHM',
          practiceEn: 'Keep the living and sleeping areas free of clutter; light an evening lamp (Deepa) with a moment of silent shared harmony.',
          practiceHi: 'घर के शयन व बैठक कक्ष को स्वच्छ व व्यवस्थित रखें; सायंकाल शांत मन से दीप प्रज्वलित करें।',
          beneficialPeriodEn: 'Daily evening twilight (Sandhya)',
          beneficialPeriodHi: 'प्रतिदिन संध्या काल',
          nonGuaranteeNoticeEn: nonGuaranteeEn,
          nonGuaranteeNoticeHi: nonGuaranteeHi
        },
        {
          id: 'UPAYA_HOME_2',
          titleEn: 'Ancestral Respect & Elder Dialogue',
          titleHi: 'पितृ सम्मान एवं वृद्धजनों का आदर',
          category: 'CHARITABLE_DAAN',
          practiceEn: 'Speak respectfully with family elders, inquiring about their wellbeing and honoring ancestral roots.',
          practiceHi: 'परिवार के वरिष्ठ सदस्यों से आदरपूर्वक संवाद करें, उनका कुशलक्षेम पूछें व मार्गदर्शन लें।',
          beneficialPeriodEn: 'Weekly family gathering',
          beneficialPeriodHi: 'साप्ताहिक पारिवारिक समय',
          nonGuaranteeNoticeEn: nonGuaranteeEn,
          nonGuaranteeNoticeHi: nonGuaranteeHi
        }
      ];

    case 'SPIRITUAL_DIRECTION':
      return [
        {
          id: 'UPAYA_SPIRITUAL_1',
          titleEn: 'Dhyana & Japa: Daily Silent Contemplation',
          titleHi: 'ध्यान एवं जप: नित्य मौन साधना',
          category: 'CLASSICAL_DHYANA',
          practiceEn: 'Dedicate 15-20 minutes daily to silent meditation, mantra contemplation (e.g. Gayatri or Om), or reading philosophical Shastras.',
          practiceHi: 'प्रतिदिन १५-२० मिनट मौन ध्यान, मंत्र जप अथवा दार्शनिक ग्रंथों के स्वाध्याय में लगाएं।',
          beneficialPeriodEn: 'Early dawn (Brahma Muhurta, ~4:30 - 6:00 AM)',
          beneficialPeriodHi: 'ब्रह्म मुहूर्त (प्रातः ४:३० से ६:०० बजे)',
          nonGuaranteeNoticeEn: nonGuaranteeEn,
          nonGuaranteeNoticeHi: nonGuaranteeHi
        },
        {
          id: 'UPAYA_SPIRITUAL_2',
          titleEn: 'Nishkama Seva: Community Selfless Service',
          titleHi: 'निःस्वार्थ सेवा: लोक कल्याण कार्य',
          category: 'CHARITABLE_DAAN',
          practiceEn: 'Offer your time, skills, or presence without expecting reward or recognition to a spiritual or humanitarian cause.',
          practiceHi: 'बिना किसी मान-सम्मान की आकांक्षा के समाज अथवा आध्यात्मिक केंद्र में अपनी सेवा समर्पित करें।',
          beneficialPeriodEn: 'Weekend morning or monthly community day',
          beneficialPeriodHi: 'सप्ताहांत अथवा अवकाश का दिन',
          nonGuaranteeNoticeEn: nonGuaranteeEn,
          nonGuaranteeNoticeHi: nonGuaranteeHi
        }
      ];
  }
}
